import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Drawer,
  Box,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Divider,
  Chip,
  Rating,
  Button,
  Alert,
  TextField,
  Switch,
  FormControlLabel,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Popover,
  Snackbar,
} from '@mui/material';
import { alpha, darken } from '@mui/material/styles';
import type { IconContainerProps } from '@mui/material/Rating';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import AddIcon from '@mui/icons-material/Add';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import EventIcon from '@mui/icons-material/Event';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import SourceOutlinedIcon from '@mui/icons-material/SourceOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import SendPriceDialog from './whatsapp/SendPriceDialog';
import LeadBrochureMenu from './whatsapp/LeadBrochureMenu';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import { useAuth, usePermissions } from '../AuthContext';
import BrokerDrawer from './BrokerDrawer';
import { formatPersonName } from '../utils/format';
import {
  WebsiteRequest,
  websiteRequestConfig,
} from '../utils/websiteRequest';
import { functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import ChannelChat from './ChannelChat';
import LeadBriefCard from './LeadBriefCard';
import { normalizeContactId } from '../utils/messaging';
import { VISIT_QUICK_POINTS, dayKeyIST, minutesSince, receptionSummary, takeReceptionVisit } from '../utils/reception';
import { useMyPresenterDayAccess } from '../utils/presenterAccess';
import {
  collection,
  doc,
  getFirestore,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  limit,
  where,
  addDoc,
  updateDoc,
  increment,
  serverTimestamp,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';

type LeadStatus = string;
const EMPTY_ACTIVITY_NOTES: string[] = [];

function normalizeNameLower(value: string): string {
  return String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
}

function buildTrigrams(value: string): string[] {
  const s = normalizeNameLower(value);
  if (!s) return [];
  const out = new Set<string>();
  const padded = ` ${s} `;
  for (let i = 0; i + 3 <= padded.length; i += 1) {
    const gram = padded.slice(i, i + 3);
    if (!gram.trim()) continue;
    out.add(gram);
  }
  return Array.from(out);
}

function normalizeStatusForStage(value: string): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

function toProperCaseWords(value: string): string {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  return trimmed
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function firestoreServerTimestampToken() {
  return { __op: 'serverTimestamp' as const };
}

function firestoreIncrementToken(by: number) {
  return { __op: 'increment' as const, by };
}

function serializeCallableValue<T>(value: T): T {
  if (value === undefined) {
    return undefined as T;
  }
  if (value instanceof Timestamp) {
    return { __type: 'timestamp', millis: value.toMillis() } as T;
  }
  if (value instanceof Date) {
    return { __type: 'timestamp', millis: value.getTime() } as T;
  }
  if (Array.isArray(value)) {
    return value
      .map((item) => serializeCallableValue(item))
      .filter((item) => item !== undefined) as T;
  }
  if (
    value &&
    typeof value === 'object' &&
    Object.getPrototypeOf(value) === Object.prototype
  ) {
    const next: Record<string, unknown> = {};
    Object.entries(value as Record<string, unknown>).forEach(([key, entryValue]) => {
      const sanitized = serializeCallableValue(entryValue);
      if (sanitized !== undefined) {
        next[key] = sanitized;
      }
    });
    return next as T;
  }
  return value;
}

function findOptionIgnoreCase(options: string[], value: string): string {
  const target = String(value || '').trim().toLowerCase();
  if (!target) return '';
  return (
    options.find(
      (opt) => String(opt || '').trim().toLowerCase() === target
    ) || ''
  );
}

function timelinePromptLabel(promptId: string, promptLabel?: string | null): string {
  const byId: Record<string, string> = {
    legacy_budget: 'Budget Fit',
    legacy_floorChoice: 'Floor Choice',
    legacy_unitChoice: 'Unit Choice',
    legacy_currentResidence: 'Current Residence',
    legacy_occupation: 'Occupation',
  };
  const explicit = String(promptLabel || '').trim();
  if (explicit) return explicit;
  if (byId[promptId]) return byId[promptId];
  const spaced = String(promptId || '')
    .replace(/^legacy_/i, '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();
  if (!spaced) return promptId;
  return spaced
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

type BrokerOption = {
  id: string;
  brokerName: string;
  agencyName: string | null;
  phone?: string | null;
  label: string;
};

function normalizePhoneDigits(phone?: string | null): string {
  if (!phone) return '';
  const digits = String(phone).replace(/\D/g, '');
  if (digits.length === 10) return `91${digits}`; // default to India when only local 10-digit number is present
  return digits;
}

function formatPhoneDisplay(phone?: string | null): string {
  if (!phone) return '';
  const digits = String(phone).replace(/\D/g, '');
  if (!digits) return String(phone);
  if (digits.length === 10) {
    const n = digits;
    return `+91 ${n.slice(0, 5)} ${n.slice(5)}`;
  }
  if (digits.length > 10) {
    const cc = digits.slice(0, digits.length - 10);
    const n = digits.slice(-10);
    if (n.length === 10) return `+${cc} ${n.slice(0, 5)} ${n.slice(5)}`;
    return `+${cc} ${n}`;
  }
  return String(phone);
}

function phoneIdentity(phone?: string | null): string {
  const digits = String(phone || '').replace(/\D/g, '');
  return digits.length >= 10 ? digits.slice(-10) : digits;
}

function asStringArray(v: any): string[] {
  if (!v) return [];
  if (Array.isArray(v)) return v.filter((x) => typeof x === 'string' && x.trim()).map((x) => x.trim());
  if (typeof v === 'string') {
    const s = v.trim();
    return s ? [s] : [];
  }
  return [];
}

function localDateInputValue(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseLocalDateInputValue(v: string): Date | null {
  const s = String(v || '').trim();
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  const dt = new Date(y, mo, d, 0, 0, 0, 0);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function parseLocalTimeInputValue(v: string): { hours: number; minutes: number } | null {
  const s = String(v || '').trim();
  const m = /^(\d{2}):(\d{2})$/.exec(s);
  if (!m) return null;
  const hours = Number(m[1]);
  const minutes = Number(m[2]);
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return { hours, minutes };
}

function localTimeInputValue(d: Date): string {
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

function toDateMaybe(v: any): Date | null {
  if (!v) return null;
  if (typeof v?.toDate === 'function') {
    const d = v.toDate();
    return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
  }
  if (typeof v === 'string') {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function formatDateValue(v: any): string {
  const d = toDateMaybe(v) || (v instanceof Date ? v : null);
  if (!d || Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString();
}

function formatDateTimeValue(v: any): string {
  const d = toDateMaybe(v) || (v instanceof Date ? v : null);
  if (!d || Number.isNaN(d.getTime())) return '';
  return d.toLocaleString();
}

function appointmentDayLabel(date: Date, now = new Date()): string {
  const targetDay = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const dayDifference = Math.round((targetDay - today) / 86400000);
  if (dayDifference === 0) return 'Today';
  if (dayDifference === 1) return 'Tomorrow';
  return date.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

function relativeDateTimeLabel(date: Date, now = new Date()): string {
  const day = appointmentDayLabel(date, now);
  const time = date.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' });
  return `${day}, ${time}`;
}

type LeadDetailDrawerErrorBoundaryProps = {
  leadId?: string;
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
};

type LeadDetailDrawerErrorBoundaryState = {
  hasError: boolean;
};

class LeadDetailDrawerErrorBoundary extends React.Component<
  LeadDetailDrawerErrorBoundaryProps,
  LeadDetailDrawerErrorBoundaryState
> {
  state: LeadDetailDrawerErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): LeadDetailDrawerErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    console.error('Lead detail drawer render failed', error);
  }

  componentDidUpdate(prevProps: LeadDetailDrawerErrorBoundaryProps) {
    const leadChanged = prevProps.leadId !== this.props.leadId;
    const reopened = !prevProps.open && this.props.open;
    if (this.state.hasError && (leadChanged || reopened)) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <Box sx={{ p: 2 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={this.props.onClose}>
              Close
            </Button>
          }
          sx={{ borderRadius: 2 }}
        >
          Lead details failed to load for this record. Close the drawer and open it again.
        </Alert>
      </Box>
    );
  }
}

async function copyToClipboard(text: string) {
  const t = text.trim();
  if (!t) return;
  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(t);
    return;
  }
  // Fallback for older browsers
  const el = document.createElement('textarea');
  el.value = t;
  el.style.position = 'fixed';
  el.style.left = '-9999px';
  document.body.appendChild(el);
  el.select();
  document.execCommand('copy');
  document.body.removeChild(el);
}

type Lead = {
  id: string;
  name: string;
  phone?: string;
  alternatePhones?: string[];
  phone10s?: string[];
  email?: string;
  source?: string;
  referredBrokerId?: string | null;
  referredBrokerName?: string | null;
  referredAgencyName?: string | null;
  referredBrokerAt?: any;
  status: LeadStatus;
  createdAt?: any;
  importedAt?: any;
  projectId?: string | null;
  projectName?: string | null;
  unitType?: string | null;
  enquiryType?: string | null;
  websiteRequest?: WebsiteRequest | null;
  assignedTo?: string;
  assignedToName?: string | null;
  nextFollowUpDate?: any;
  nextFollowUpAt?: any;
  notes?: string | string[];
  closePrimaryReason?: string | null;
  closeAnalysis?: { primaryReason?: string | null; interestedLocation?: string | null } | null;
  rating?: number | null;
  ratingUpdatedAt?: any;
  ratingUpdatedBy?: string | null;
  appointment?: {
    type?: 'site-visit' | 'office-meeting';
    scheduledAt?: any;
    projectId?: string | null;
    projectName?: string | null;
    unitType?: string | null;
    visitWith?: 'alone' | 'family' | 'friend' | 'other' | null;
    visitWithOther?: string | null;
    meetingPoints?: string[] | null;
    status?: 'scheduled' | 'completed' | 'cancelled' | 'no-show';
    outcome?: string | null;
    completedAt?: any;
    createdAt?: any;
    createdBy?: string | null;
    updatedAt?: any;
    updatedBy?: string | null;
  } | null;
  receptionVisit?: { id?: string; name?: string } | null;
  promptAnswers?: Record<
    string,
    {
      value: any;
      type?: 'text' | 'date' | 'number' | 'select';
      label?: string;
      statusId?: string;
      answeredAt?: any;
      answeredBy?: string | null;
    }
  >;
};

type Activity = {
  id: string;
  type: 'call' | 'visit' | 'internal';
  event?: string;
  projectId?: string | null;
  projectName?: string | null;
  unitType?: string | null;
  projectFromId?: string | null;
  projectFromName?: string | null;
  projectToId?: string | null;
  projectToName?: string | null;
  unitTypeFrom?: string | null;
  unitTypeTo?: string | null;
  toPhone?: string | null;
  description?: string;
  notes?: string[];
  callOutcome?: string;
  visitWith?: 'alone' | 'family' | 'friend' | 'other';
  visitWithOther?: string;
  visitArrival?: 'walk-in' | 'broker' | 'c2c' | 'other' | 'unknown';
  visitArrivalBrokerId?: string | null;
  visitArrivalBrokerName?: string | null;
  visitArrivalBrokerAgencyName?: string | null;
  visitArrivalC2cName?: string | null;
  visitArrivalOtherNote?: string | null;
  visitSourceCorrection?: { from?: Record<string, any>; to?: Record<string, any>; correctedAt?: any; correctedBy?: string | null } | null;
  appointmentType?: 'site-visit' | 'office-meeting' | null;
  appointmentAt?: any;
  appointmentOutcome?: string | null;
  meetingPoints?: string[] | null;
  ratingFrom?: number | null;
  ratingTo?: number | null;
  requestType?: string | null;
  requestLabel?: string | null;
  statusFrom?: string;
  statusTo?: string;
  reopenReason?: string | null;
  reopenNote?: string | null;
  reopenWhatsappRequested?: boolean | null;
  assignedFrom?: string;
  assignedTo?: string;
  refByFromId?: string | null;
  refByFromName?: string | null;
  refByFromAgency?: string | null;
  refByToId?: string | null;
  refByToName?: string | null;
  refByToAgency?: string | null;
  followUpFrom?: any;
  followUpDate?: any;
  followUpAt?: any;
  promptAnswers?: Record<string, any> | null;
  closeAnalysis?: {
    primaryReason?: string | null;
    primaryReasonBase?: string | null;
    primaryOtherReason?: string | null;
    secondaryReasons?: string[] | null;
    decisionStage?: string | null;
    decisionStageAuto?: string | null;
    interestedLocation?: string | null;
    boughtAtLocation?: string | null;
    requiredType?: string | null;
    requiredTypeLocation?: string | null;
    budgetDetail?: string | null;
    distancePreferredArea?: string | null;
    competitorProject?: string | null;
    competitorPriceBand?: string | null;
    competitorAdvantage?: string | null;
  } | null;
  byEmail?: string;
  at?: any;
};

interface Props {
  lead: Lead | null;
  open: boolean;
  initialActivityNotes?: string[];
  initialActivityType?: 'call' | 'visit' | 'internal';
  /** Open with the Visit log (My Today ✓ Visited / Finish visit). */
  startVisitLog?: boolean;
  onClose: () => void;
  onExited?: () => void;
  onDeleted?: (leadId: string) => void;
  onUpdated?: (lead: Lead) => void;
  onActivityLogged?: (lead: Lead) => void;
  onWidthChange?: (width: number) => void;
  onResizingChange?: (resizing: boolean) => void;
  mobileFullScreen?: boolean;
  closeOnOutsideClick?: boolean;
}

// Lead detail/log UI styling is locked to "Style 2" (accent ribbon sections).

function LeadDetailDrawerInner({
  lead: leadProp,
  open,
  initialActivityNotes = EMPTY_ACTIVITY_NOTES,
  initialActivityType,
  startVisitLog = false,
  onClose,
  onExited,
  onDeleted,
  onUpdated,
  onActivityLogged,
  onWidthChange,
  onResizingChange,
  mobileFullScreen,
  closeOnOutsideClick = false,
}: Props) {
  // NOTE: `lead` prop can be stale on pages that use paginated/non-realtime queries.
  // We keep a live subscription to the selected lead doc so details update instantly.
  const { currentUser, roleId, projectIds, currentProjectId } = useAuth();
  const { canAction, canPage } = usePermissions();
  const db = useMemo(() => getFirestore(), []);
  const hasDayPresenterAccess = useMyPresenterDayAccess(db, currentUser?.uid, currentProjectId);

  const [statuses, setStatuses] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [brokers, setBrokers] = useState<BrokerOption[]>([]);

  const CLOSE_REASON_OPTIONS = useMemo(
    () => [
      'Completed',
      'Not Interested',
      'Interested in Another Location',
      'Purchased in Another Location',
      'Need Other Type',
      'Budget / Financial Issue',
      'Distance Issue',
      'Not Reachable (No Response)',
      'Wrong / Invalid Number',
      'Broker Inquiry Only',
      'Loss due to Competitor',
      'Other',
    ],
    []
  );
  const CLOSE_SECONDARY_OPTIONS = useMemo(
    () => [
      'Not Interested',
      'Interested in Another Location',
      'Budget / Financial Issue',
      'Distance Issue',
      'Not Reachable (No Response)',
      'Wrong / Invalid Number',
      'Broker Inquiry Only',
      'Loss due to Competitor',
      'Trust / Brand Concern',
      'Unit / Layout Mismatch',
      'Possession Timeline Mismatch',
      'Other',
    ],
    []
  );
  const CLOSE_STAGE_OPTIONS = useMemo(
    () => [
      'New Lead',
      'Connected Call',
      'Follow-up in Progress',
      'Site Visit Done',
      'Negotiation Stage',
      'Booking Stage',
    ],
    []
  );
  const CLOSE_BUDGET_DETAIL_OPTIONS = useMemo(
    () => [
      'Budget mismatch',
      'Loan not possible',
      'Other budget-related issue',
    ],
    []
  );
  const CLOSE_REQUIRED_TYPE_OPTIONS = useMemo(
    () => ['1 BHK', '2 BHK', '3 BHK', '4 BHK+', 'Row House', 'Open Plot', 'Shop'],
    []
  );
  const CLOSE_COMP_PRICE_BAND_OPTIONS = useMemo(
    () => [
      'Similar',
      'Up to 5L cheaper',
      '5L-10L cheaper',
      '10L+ cheaper',
    ],
    []
  );
  const CLOSE_COMP_ADVANTAGE_OPTIONS = useMemo(
    () => [
      'Lower price',
      'Better location',
      'Better layout',
      'Faster possession',
      'Better amenities',
      'Better payment plan',
      'Broker influence',
      'Other',
    ],
    []
  );
  const [liveLead, setLiveLead] = useState<Lead | null>(null);
  const [renderLead, setRenderLead] = useState<Lead | null>(leadProp);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [timelineError, setTimelineError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [onlyCustomerContact, setOnlyCustomerContact] = useState(true);

  // log activity form
  const [activityType, setActivityType] = useState<'' | 'call' | 'visit' | 'internal' | 'appointment'>('');
  const [ownerAskOpen, setOwnerAskOpen] = useState(false);
  const [takingVisit, setTakingVisit] = useState(false);

  // Visit log filled with what reception recorded at check-in today.
  const startVisitFromReception = (target: Lead | null) => {
    setActivityType('visit');
    setLogExpanded(true);
    const rv = (target as any)?.receptionVisit;
    if (!rv || rv.dayKey !== dayKeyIST() || rv.status === 'left') return;
    if (rv.visitWith) setVisitWith(rv.visitWith);
    if (rv.cameVia) setVisitArrival(rv.cameVia);
    if (rv.cameVia === 'broker' && rv.brokerId) {
      const brokerName = String(rv.brokerName || '');
      setVisitArrivalBroker({
        id: rv.brokerId,
        brokerName,
        agencyName: rv.agencyName || null,
        label: rv.agencyName ? `${brokerName} · ${rv.agencyName}` : brokerName,
      });
    }
    if (rv.cameVia === 'c2c' && rv.c2cName) setVisitArrivalC2cName(rv.c2cName);
    if (rv.projectId && !(target as any)?.projectId) setLogProjectIdDraft(rv.projectId);
  };
  const [noteInput, setNoteInput] = useState('');
  const [noteList, setNoteList] = useState<string[]>([]);
  const [newStatus, setNewStatus] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpTimeEnabled, setFollowUpTimeEnabled] = useState(false);
  const [followUpTime, setFollowUpTime] = useState('');
  const [callOutcome, setCallOutcome] = useState('');
  const [visitWith, setVisitWith] = useState<'' | 'alone' | 'family' | 'friend' | 'other'>('');
  const [visitWithOther, setVisitWithOther] = useState('');
  const [visitArrival, setVisitArrival] = useState<'' | 'walk-in' | 'broker' | 'c2c'>('');
  const [visitArrivalBroker, setVisitArrivalBroker] = useState<BrokerOption | null>(null);
  const [visitArrivalC2cName, setVisitArrivalC2cName] = useState('');
  const [visitReferredBroker, setVisitReferredBroker] = useState<BrokerOption | null>(null);
  const [visitFirstRefByMode, setVisitFirstRefByMode] = useState<
    '' | 'direct' | 'broker' | 'reference' | 'c2c'
  >('');
  const [visitFirstRefByName, setVisitFirstRefByName] = useState('');
  const [logProjectIdDraft, setLogProjectIdDraft] = useState<string>('');
  const [logUnitTypeDraft, setLogUnitTypeDraft] = useState<string>('');
  const [promptDraft, setPromptDraft] = useState<Record<string, string>>({});
  const [closePrimaryReason, setClosePrimaryReason] = useState('');
  const [closePrimaryOtherReason, setClosePrimaryOtherReason] = useState('');
  const [closeSecondaryReasons, setCloseSecondaryReasons] = useState<string[]>([]);
  const [closeDecisionStage, setCloseDecisionStage] = useState('');
  const [closeDecisionStageEditing, setCloseDecisionStageEditing] = useState(false);
  const [closeInterestedLocation, setCloseInterestedLocation] = useState('');
  const [closeInterestedLocationOptions, setCloseInterestedLocationOptions] = useState<string[]>([]);
  const [showAddInterestedLocation, setShowAddInterestedLocation] = useState(false);
  const [newInterestedLocationInput, setNewInterestedLocationInput] = useState('');
  const [closeBoughtAtLocation, setCloseBoughtAtLocation] = useState('');
  const [closeBoughtAtLocationOptions, setCloseBoughtAtLocationOptions] = useState<string[]>([]);
  const [showAddBoughtAtLocation, setShowAddBoughtAtLocation] = useState(false);
  const [newBoughtAtLocationInput, setNewBoughtAtLocationInput] = useState('');
  const [closeRequiredType, setCloseRequiredType] = useState('');
  const [closeRequiredTypeLocation, setCloseRequiredTypeLocation] = useState('');
  const [closeRequiredTypeLocationOptions, setCloseRequiredTypeLocationOptions] = useState<string[]>([]);
  const [showAddRequiredTypeLocation, setShowAddRequiredTypeLocation] = useState(false);
  const [newRequiredTypeLocationInput, setNewRequiredTypeLocationInput] = useState('');
  const [closeBudgetDetail, setCloseBudgetDetail] = useState('');
  const [closeDistancePreferredArea, setCloseDistancePreferredArea] = useState('');
  const [closeDistanceAreaOptions, setCloseDistanceAreaOptions] = useState<string[]>([]);
  const [showAddDistanceArea, setShowAddDistanceArea] = useState(false);
  const [newDistanceAreaInput, setNewDistanceAreaInput] = useState('');
  const [closeCompetitorProject, setCloseCompetitorProject] = useState('');
  const [closeCompetitorPriceBand, setCloseCompetitorPriceBand] = useState('');
  const [closeCompetitorAdvantage, setCloseCompetitorAdvantage] = useState('');
  const [closeOtherReasonOptions, setCloseOtherReasonOptions] = useState<string[]>([]);
  const [showAddOtherReason, setShowAddOtherReason] = useState(false);
  const [newOtherReasonInput, setNewOtherReasonInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [completingWebsiteRequest, setCompletingWebsiteRequest] = useState(false);
  const [logSubmitState, setLogSubmitState] = useState<'idle' | 'saving' | 'success'>('idle');
  const [logSubmitError, setLogSubmitError] = useState<string | null>(null);
  const [postSubmitToastOpen, setPostSubmitToastOpen] = useState(false);
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [reopenNote, setReopenNote] = useState('');
  const [brokerDrawerOpen, setBrokerDrawerOpen] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState(460);
  const [resizing, setResizing] = useState(false);
  // Lock Log Activity to the "Cards" UI.
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const canEditPromptAnswers = canAction('leads', 'editLead');
  const canEditLeadName = useMemo(() => {
    const r = String(roleId || '').trim().toLowerCase();
    return r !== '' && r !== 'trainee';
  }, [roleId]);
  const canDeleteLead = useMemo(() => (roleId || '').toString() === 'super-admin', [roleId]);
  const canMergeLead = useMemo(() => {
    const r = String(roleId || '').trim().toLowerCase();
    return r === 'super-admin' || r === 'admin' || r === 'sales-manager';
  }, [roleId]);
  const canSeeBrokerName = useMemo(() => {
    const r = (roleId || '').toString();
    return r === 'super-admin' || r === 'admin';
  }, [roleId]);
  const canEditRating = useMemo(() => {
    const r = (roleId || '').toString();
    return r === 'super-admin' || r === 'admin' || r === 'sales-manager';
  }, [roleId]);

  const canEditAssignment = useMemo(() => {
    const r = (roleId || '').toString();
    return (
      r === 'super-admin' ||
      r === 'admin' ||
      r === 'sales-manager' ||
      canAction('leads', 'changeAssign')
    );
  }, [canAction, roleId]);

  const canManageAppointments = useMemo(() => {
    // Use same access as updating follow-up for now.
    return canAction('leads', 'updateFollowUp') || canAction('leads', 'editLead');
  }, [canAction]);
  const canInlineAddBroker = useMemo(
    () =>
      canAction('brokers', 'addBroker') ||
      canAction('leads', 'addBroker') ||
      String(roleId || '').trim().toLowerCase() === 'sales-manager' ||
      String(roleId || '').trim().toLowerCase() === 'salesmanager',
    [canAction, roleId]
  );

  // Drawer is rendered in a portal and uses fixed positioning, so it won't naturally
  // "push down" below the static AppBar. When the AppBar is visible at the top,
  // offset the drawer by its height; once scrolled away, remove the offset.
  const [drawerTopOffset, setDrawerTopOffset] = useState(0);
  const drawerScrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;

    let raf = 0;
    const compute = () => {
      const el = document.querySelector<HTMLElement>('[data-role="app-header"]');
      if (!el) return 0;
      const rect = el.getBoundingClientRect();
      // Use the *visible* portion of the header (its bottom edge within viewport)
      // so the drawer smoothly snaps up as the non-sticky header scrolls away.
      // rect.bottom:
      // - header fully visible: ~= height
      // - partially scrolled away: between 0..height
      // - fully above viewport: <= 0
      const visible = Math.max(0, Math.min(rect.height, rect.bottom));
      return Math.round(visible);
    };

    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const next = compute();
        setDrawerTopOffset((prev) => (prev === next ? prev : next));
      });
    };

    schedule();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, [open]);

  // Avoid resubscribing the lead doc snapshot on every render due to a new callback identity.
  const onUpdatedRef = useRef<Props['onUpdated']>(onUpdated);
  useEffect(() => {
    onUpdatedRef.current = onUpdated;
  }, [onUpdated]);

  useEffect(() => {
    const next = liveLead ?? leadProp;
    if (next?.id) {
      setRenderLead(next);
    }
  }, [leadProp, liveLead]);

  const lead = liveLead ?? leadProp ?? renderLead;

  // Start Log Activity with no type selected when opening the drawer / switching leads.
  useEffect(() => {
    if (!open) return;
    setActivityType('');
    // Clear any previous type-specific selections so the UI truly starts blank.
    setError(null);
    setNoteInput('');
    setNoteList([]);
    setNewStatus('');
    setFollowUpDate('');
    setFollowUpTimeEnabled(false);
    setFollowUpTime('');
    setCallOutcome('');
    setVisitWith('');
    setVisitWithOther('');
    setVisitArrival('');
    setVisitArrivalBroker(null);
    setVisitArrivalC2cName('');
    setVisitReferredBroker(null);
    setVisitFirstRefByMode('');
    setVisitFirstRefByName('');
    setLogProjectIdDraft('');
    setLogUnitTypeDraft('');
    setPromptDraft({});
    setClosePrimaryReason('');
    setClosePrimaryOtherReason('');
    setCloseSecondaryReasons([]);
    setCloseDecisionStage('');
    setCloseDecisionStageEditing(false);
    setCloseInterestedLocation('');
    setCloseBoughtAtLocation('');
    setCloseRequiredType('');
    setCloseRequiredTypeLocation('');
    setCloseBudgetDetail('');
    setCloseDistancePreferredArea('');
    setCloseCompetitorProject('');
    setCloseCompetitorPriceBand('');
    setCloseCompetitorAdvantage('');
    setShowAddOtherReason(false);
    setShowAddInterestedLocation(false);
    setShowAddBoughtAtLocation(false);
    setShowAddRequiredTypeLocation(false);
    setShowAddDistanceArea(false);
    setNewOtherReasonInput('');
    setNewInterestedLocationInput('');
    setNewBoughtAtLocationInput('');
    setNewRequiredTypeLocationInput('');
    setNewDistanceAreaInput('');
  }, [open, leadProp?.id]);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      try {
        const leadsRef = collection(db, 'leads');
        const [primaryOtherSnap, secondaryOtherSnap, interestedLocationSnap, boughtLocationSnap, needOtherTypeSnap, distanceAreaSnap] = await Promise.all([
          getDocs(query(leadsRef, where('closePrimaryReason', '==', 'Other'), limit(300))),
          getDocs(query(leadsRef, where('closeSecondaryReasons', 'array-contains', 'Other'), limit(300))),
          getDocs(query(leadsRef, where('closePrimaryReason', '==', 'Interested in Another Location'), limit(300))),
          getDocs(query(leadsRef, where('closePrimaryReason', '==', 'Purchased in Another Location'), limit(300))),
          getDocs(query(leadsRef, where('closePrimaryReason', '==', 'Need Other Type'), limit(300))),
          getDocs(query(leadsRef, where('closePrimaryReason', '==', 'Distance Issue'), limit(300))),
        ]);
        const seen = new Map<string, string>();
        const seenLocations = new Map<string, string>();
        const seenBoughtLocations = new Map<string, string>();
        const seenRequiredTypeLocations = new Map<string, string>();
        const seenDistanceAreas = new Map<string, string>();
        const addUnique = (raw: unknown) => {
          const val = String(raw || '').trim();
          if (!val) return;
          const key = val.toLowerCase();
          if (!seen.has(key)) seen.set(key, val);
        };
        const addLocation = (raw: unknown) => {
          const val = String(raw || '').trim();
          if (!val) return;
          const key = val.toLowerCase();
          if (!seenLocations.has(key)) seenLocations.set(key, val);
        };
        const addDistanceArea = (raw: unknown) => {
          const val = String(raw || '').trim();
          if (!val) return;
          const key = val.toLowerCase();
          if (!seenDistanceAreas.has(key)) seenDistanceAreas.set(key, val);
        };
        const addRequiredTypeLocation = (raw: unknown) => {
          const val = String(raw || '').trim();
          if (!val) return;
          const key = val.toLowerCase();
          if (!seenRequiredTypeLocations.has(key)) seenRequiredTypeLocations.set(key, val);
        };
        const addBoughtLocation = (raw: unknown) => {
          const val = String(raw || '').trim();
          if (!val) return;
          const key = val.toLowerCase();
          if (!seenBoughtLocations.has(key)) seenBoughtLocations.set(key, val);
        };
        primaryOtherSnap.forEach((d) => {
          const data = d.data() as any;
          addUnique(data?.closePrimaryOtherReason);
          addUnique(data?.closedAnalysis?.primaryOtherReason);
        });
        secondaryOtherSnap.forEach((d) => {
          const data = d.data() as any;
          const arr = Array.isArray(data?.closeSecondaryOtherReasons)
            ? data.closeSecondaryOtherReasons
            : [];
          arr.forEach(addUnique);
        });
        interestedLocationSnap.forEach((d) => {
          const data = d.data() as any;
          addLocation(data?.closeInterestedLocation);
          addLocation(data?.closedAnalysis?.interestedLocation);
        });
        boughtLocationSnap.forEach((d) => {
          const data = d.data() as any;
          addBoughtLocation(data?.closeBoughtAtLocation);
          addBoughtLocation(data?.closedAnalysis?.boughtAtLocation);
        });
        needOtherTypeSnap.forEach((d) => {
          const data = d.data() as any;
          addRequiredTypeLocation(data?.closeRequiredTypeLocation);
          addRequiredTypeLocation(data?.closedAnalysis?.requiredTypeLocation);
        });
        distanceAreaSnap.forEach((d) => {
          const data = d.data() as any;
          addDistanceArea(data?.closeDistancePreferredArea);
          addDistanceArea(data?.closedAnalysis?.distancePreferredArea);
        });
        if (alive) {
          setCloseOtherReasonOptions(
            Array.from(seen.values()).sort((a, b) => a.localeCompare(b))
          );
          setCloseInterestedLocationOptions(
            Array.from(seenLocations.values()).sort((a, b) => a.localeCompare(b))
          );
          setCloseBoughtAtLocationOptions(
            Array.from(seenBoughtLocations.values()).sort((a, b) => a.localeCompare(b))
          );
          setCloseRequiredTypeLocationOptions(
            Array.from(seenRequiredTypeLocations.values()).sort((a, b) => a.localeCompare(b))
          );
          setCloseDistanceAreaOptions(
            Array.from(seenDistanceAreas.values()).sort((a, b) => a.localeCompare(b))
          );
        }
      } catch {
        if (alive) {
          setCloseOtherReasonOptions([]);
          setCloseInterestedLocationOptions([]);
          setCloseBoughtAtLocationOptions([]);
          setCloseRequiredTypeLocationOptions([]);
          setCloseDistanceAreaOptions([]);
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [db, open]);

  useEffect(() => {
    if (closePrimaryReason !== 'Interested in Another Location') {
      setCloseInterestedLocation('');
    }
    if (closePrimaryReason !== 'Purchased in Another Location') {
      setCloseBoughtAtLocation('');
      setShowAddBoughtAtLocation(false);
      setNewBoughtAtLocationInput('');
    }
    if (closePrimaryReason !== 'Need Other Type') {
      setCloseRequiredType('');
      setCloseRequiredTypeLocation('');
      setShowAddRequiredTypeLocation(false);
      setNewRequiredTypeLocationInput('');
    }
    if (closePrimaryReason !== 'Budget / Financial Issue') {
      setCloseBudgetDetail('');
    }
    if (closePrimaryReason !== 'Distance Issue') {
      setCloseDistancePreferredArea('');
    }
  }, [closePrimaryReason]);

  const isSocialAdLead = useMemo(() => {
    const s = String(lead?.source || '')
      .trim()
      .toLowerCase()
      .replace(/[-_]+/g, ' ')
      .replace(/\s+/g, ' ');
    return s === 'social ad' || s === 'social ads' || s === 'social lead' || s === 'meta lead';
  }, [lead?.source]);

  useEffect(() => {
    if (!open) {
      setLiveLead(null);
      return;
    }
    if (!leadProp?.id) {
      setLiveLead(null);
      return;
    }
    const ref = doc(db, 'leads', leadProp.id);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setLiveLead(null);
          return;
        }
        const next = { id: snap.id, ...(snap.data() as any) } as Lead;
        setLiveLead(next);
        // Keep parent list/detail in sync for pages that use paginated/non-realtime queries.
        onUpdatedRef.current?.(next);
      },
      () => {
        // Best-effort: keep showing prop data if subscription fails.
        setLiveLead(null);
      }
    );
    return () => unsub();
  }, [db, leadProp?.id, open]);

  const bumpLeadActivity = async (leadRef: any, n = 1) => {
    if (!leadRef) return;
    try {
      await updateDoc(leadRef, {
        activityCount: increment(n),
        lastActivityAt: serverTimestamp(),
      });
    } catch (err) {
      // Best-effort: don't block the UX for a secondary counter update.
      console.error('Failed to bump lead activity counters', err);
    }
  };

  const ratingLabels = useMemo(
    () => ({
      1: 'Very Cold',
      2: 'Cold',
      3: 'Warm',
      4: 'Hot',
      5: 'Almost Confirmed',
    }),
    []
  );

  // Use native `title` tooltips per-star (stable across desktop browsers).
  const RatingIconContainer = (props: IconContainerProps) => {
    const { value, ...other } = props;
    const title = ratingLabels[value as 1 | 2 | 3 | 4 | 5] || '';
    return <span {...other} title={title} style={{ display: 'inline-flex' }} />;
  };

  const [callBreakdownAnchorEl, setCallBreakdownAnchorEl] = useState<HTMLElement | null>(null);
  const [visitDatesAnchorEl, setVisitDatesAnchorEl] = useState<HTMLElement | null>(null);
  const [assignAnchorEl, setAssignAnchorEl] = useState<HTMLElement | null>(null);
  const [refByAnchorEl, setRefByAnchorEl] = useState<HTMLElement | null>(null);
  const [refByDraft, setRefByDraft] = useState<BrokerOption | null>(null);
  const [refByMode, setRefByMode] = useState<'none' | 'broker' | 'reference'>('none');
  const [refByNameDraft, setRefByNameDraft] = useState<string>('');
  const [savingRefBy, setSavingRefBy] = useState(false);
  const [projectAnchorEl, setProjectAnchorEl] = useState<HTMLElement | null>(null);
  const [savingProject, setSavingProject] = useState(false);
  const [moreAnchorEl, setMoreAnchorEl] = useState<HTMLElement | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingLead, setDeletingLead] = useState(false);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeSearchRaw, setMergeSearchRaw] = useState('');
  const [mergeResults, setMergeResults] = useState<Lead[]>([]);
  const [mergeSelectedLead, setMergeSelectedLead] = useState<Lead | null>(null);
  const [mergeSearching, setMergeSearching] = useState(false);
  const [mergeSaving, setMergeSaving] = useState(false);
  const [mergeError, setMergeError] = useState<string | null>(null);

  const [ratingValue, setRatingValue] = useState<number>(lead?.rating ? Number(lead.rating) : 0);
  const [priceDialogOpen, setPriceDialogOpen] = useState(false);
  const [brochureAnchorEl, setBrochureAnchorEl] = useState<HTMLElement | null>(null);
  const [brochureLastInboundAt, setBrochureLastInboundAt] = useState<Date | null>(null);

  const [logExpanded, setLogExpanded] = useState(true);

  const canEditLeadBasics = canAction('leads', 'editLead');
  const canEditLeadProject = canEditLeadBasics;
  const canEditRefBy = useMemo(() => canSeeBrokerName && canEditLeadBasics, [canEditLeadBasics, canSeeBrokerName]);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState<string>(lead?.name ? String(lead.name) : '');
  const [savingName, setSavingName] = useState(false);

  // Appointment UI state (one appointment per lead)
  const [apptFormOpen, setApptFormOpen] = useState(false);
  const [apptType, setApptType] = useState<'site-visit' | 'office-meeting'>('site-visit');
  const [apptScheduledAt, setApptScheduledAt] = useState<string>(''); // datetime-local
  const [apptProjectId, setApptProjectId] = useState<string>('');
  const [apptUnitType, setApptUnitType] = useState<string>('');
  const [apptVisitWith, setApptVisitWith] = useState<'' | 'alone' | 'family' | 'friend' | 'other'>('');
  const [apptVisitWithOther, setApptVisitWithOther] = useState('');
  const [apptMeetingPointInput, setApptMeetingPointInput] = useState('');
  const [apptMeetingPoints, setApptMeetingPoints] = useState<string[]>([]);

  const [apptCompleteOpen, setApptCompleteOpen] = useState(false);
  const [apptOutcome, setApptOutcome] = useState<'completed' | 'no-show' | 'cancelled' | 'rescheduled'>('completed');
  const [apptRescheduleAt, setApptRescheduleAt] = useState<string>(''); // datetime-local
  const [apptFollowUpDate, setApptFollowUpDate] = useState<string>(''); // date
  const [apptCompleteNoteInput, setApptCompleteNoteInput] = useState('');
  const [apptCompleteNotes, setApptCompleteNotes] = useState<string[]>([]);
  const [apptStatusChange, setApptStatusChange] = useState<string>(''); // '' => no change
  const [apptRating, setApptRating] = useState<number>(0);

  // Visit-related prompts (asked on every Visit; prefill from existing promptAnswers if present).
  const [visitBudgetFit, setVisitBudgetFit] = useState<string>('');
  const [visitFloorChoice, setVisitFloorChoice] = useState<string>('');
  const [visitResidenceInput, setVisitResidenceInput] = useState<string>('');
  const [visitOccupationInput, setVisitOccupationInput] = useState<string>('');

  const moreMenuOpen = Boolean(moreAnchorEl);

  useEffect(() => {
    // Reset destructive UI state when switching leads / closing.
    setMoreAnchorEl(null);
    setDeleteDialogOpen(false);
    setDeleteConfirmText('');
    setDeletingLead(false);
    setMergeDialogOpen(false);
    setMergeSearchRaw('');
    setMergeResults([]);
    setMergeSelectedLead(null);
    setMergeSearching(false);
    setMergeSaving(false);
    setMergeError(null);
    setRefByAnchorEl(null);
    setRefByDraft(null);
    setSavingRefBy(false);
  }, [lead?.id, open]);

  useEffect(() => {
    // Reset inline edits when switching leads.
    setEditingName(false);
    setSavingName(false);
    setNameDraft(lead?.name ? String(lead.name) : '');
  }, [lead?.id, open, lead?.name]);

  const handleSaveName = async () => {
    if (!lead) return;
    if (!canEditLeadName) return;
    const next = String(nameDraft || '').trim().replace(/\s+/g, ' ');
    if (!next) {
      setError('Name is required.');
      return;
    }
    try {
      setSavingName(true);
      const leadRef = doc(db, 'leads', lead.id);
      const batch = writeBatch(db);
      const receptionVisitId = String(lead.receptionVisit?.id || '').trim();
      const leadUpdates: Record<string, any> = {
        name: next,
        nameLower: normalizeNameLower(next),
        nameTrigrams: buildTrigrams(next),
        updatedAt: serverTimestamp(),
      };
      if (receptionVisitId) leadUpdates['receptionVisit.name'] = next;
      batch.update(leadRef, leadUpdates);
      if (receptionVisitId) {
        const receptionRef = doc(db, 'receptionVisits', receptionVisitId);
        const receptionSnap = await getDoc(receptionRef);
        if (receptionSnap.exists()) {
          batch.update(receptionRef, { name: next, updatedAt: serverTimestamp() });
        }
      }
      await batch.commit();
      setEditingName(false);
    } catch (err: any) {
      console.error('Save name failed', err);
      setError(err?.message || 'Failed to update name.');
    } finally {
      setSavingName(false);
    }
  };

  const refByOptions: BrokerOption[] = useMemo(() => {
    const base = brokers || [];
    const id = lead?.referredBrokerId ? String(lead.referredBrokerId) : '';
    if (!id) return base;
    if (base.some((b) => b.id === id)) return base;
    // If the broker record doesn't exist in the options list anymore, keep a fallback option
    // so the current value still renders in the autocomplete.
    const brokerName = lead?.referredBrokerName ? String(lead.referredBrokerName) : '';
    const agencyName = lead?.referredAgencyName ? String(lead.referredAgencyName) : '';
    const label = agencyName ? `${agencyName}${brokerName ? ` - ${brokerName}` : ''}` : (brokerName || id);
    return [
      { id, brokerName: brokerName || id, agencyName: agencyName || null, label },
      ...base,
    ];
  }, [brokers, lead?.referredAgencyName, lead?.referredBrokerId, lead?.referredBrokerName]);

  const openRefByEditor = (anchorEl: HTMLElement) => {
    if (!lead) return;
    const currentId = lead.referredBrokerId ? String(lead.referredBrokerId) : '';
    const current =
      currentId ? refByOptions.find((b) => b.id === currentId) || null : null;
    const currentRefName = String((lead as any)?.refByName || '').trim();
    if (currentId) {
      setRefByMode('broker');
      setRefByDraft(current);
      setRefByNameDraft('');
    } else if (currentRefName) {
      setRefByMode('reference');
      setRefByDraft(null);
      setRefByNameDraft(currentRefName);
    } else {
      setRefByMode('none');
      setRefByDraft(null);
      setRefByNameDraft('');
    }
    setRefByAnchorEl(anchorEl);
  };

  const handleSaveRefBy = async () => {
    if (!lead) return;
    if (!canEditRefBy) return;
    setSavingRefBy(true);
    setError(null);
    try {
      const leadRef = doc(db, 'leads', lead.id);
      const activitiesRef = collection(leadRef, 'activities');

      const fromId = lead.referredBrokerId || null;
      const fromName = lead.referredBrokerName || null;
      const fromAgency = lead.referredAgencyName || null;
      const fromRefName = String((lead as any)?.refByName || '').trim() || null;
      const fromType = fromId ? 'broker' : (fromRefName ? 'reference' : 'none');
      const fromDisplay = fromId
        ? (fromAgency ? `${fromAgency}${fromName ? ` - ${fromName}` : ''}` : (fromName || fromId))
        : (fromRefName || '—');

      const toId = refByMode === 'broker' ? (refByDraft?.id || null) : null;
      const toName = refByMode === 'broker' ? (refByDraft?.brokerName || null) : null;
      const toAgency = refByMode === 'broker' ? (refByDraft?.agencyName || null) : null;
      const toRefName = refByMode === 'reference' ? (refByNameDraft.trim() || null) : null;
      const toType = toId ? 'broker' : (toRefName ? 'reference' : 'none');
      const toDisplay = toId
        ? (toAgency ? `${toAgency}${toName ? ` - ${toName}` : ''}` : (toName || toId))
        : (toRefName || '—');

      if (refByMode === 'broker' && !toId) {
        setError('Select Ref By (Broker).');
        setSavingRefBy(false);
        return;
      }
      if (refByMode === 'reference' && !toRefName) {
        setError('Enter Ref By name.');
        setSavingRefBy(false);
        return;
      }

      await updateDoc(leadRef, {
        // Broker
        referredBrokerId: toId,
        referredBrokerName: toName,
        referredAgencyName: toAgency,
        referredBrokerAt: toId ? serverTimestamp() : null,
        // Reference (person)
        refByName: toRefName,
        refByType: toRefName ? 'reference' : null,
        refByAt: toRefName ? serverTimestamp() : null,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.email || null,
      });

      await addDoc(activitiesRef, {
        type: 'internal',
        event: 'ref-by',
        refByFromType: fromType,
        refByToType: toType,
        refByFromId: fromId,
        refByFromName: fromDisplay,
        refByFromAgency: fromAgency,
        refByToId: toId,
        refByToName: toDisplay,
        refByToAgency: toAgency,
        refByFromRefName: fromRefName,
        refByToRefName: toRefName,
        byEmail: currentUser?.email || null,
        at: serverTimestamp(),
      });
      await bumpLeadActivity(leadRef, 1);

      setSavingRefBy(false);
      setRefByAnchorEl(null);
    } catch (err: any) {
      console.error('ref-by update failed', err);
      setError(err?.message || 'Failed to update Ref By');
      setSavingRefBy(false);
    }
  };

  const handleDeleteLead = async () => {
    if (!lead) return;
    if (!canDeleteLead) return;
    if (deleteConfirmText.trim() !== 'DELETE') return;

    try {
      setDeletingLead(true);
      const fn = httpsCallable(functions, 'deleteLead');
      await fn({ leadId: lead.id });
      setDeleteDialogOpen(false);
      setDeleteConfirmText('');
      onDeleted?.(lead.id);
      onClose();
    } catch (err: any) {
      console.error('Delete lead failed', err);
      setError(err?.message || 'Failed to delete lead.');
    } finally {
      setDeletingLead(false);
    }
  };

  const searchMergeLeads = async () => {
    if (!lead) return;
    const qRaw = mergeSearchRaw.trim();
    if (qRaw.length < 3) {
      setMergeError('Enter at least 3 characters or digits.');
      return;
    }
    setMergeSearching(true);
    setMergeError(null);
    try {
      const leadsRef = collection(db, 'leads');
      const digits = qRaw.replace(/\D/g, '');
      let snap;
      if (digits.length >= 10) {
        const phone10 = digits.slice(-10);
        const [primarySnap, mergedPhoneSnap] = await Promise.all([
          getDocs(query(leadsRef, where('phone10', '==', phone10), limit(25))),
          getDocs(query(leadsRef, where('phoneSearch', 'array-contains', phone10), limit(25))),
        ]);
        const docsById = new Map<string, any>();
        primarySnap.docs.forEach((d) => docsById.set(d.id, d));
        mergedPhoneSnap.docs.forEach((d) => docsById.set(d.id, d));
        snap = { docs: Array.from(docsById.values()) };
      } else if (digits.length >= 3) {
        snap = await getDocs(query(leadsRef, where('phoneSearch', 'array-contains', digits), limit(25)));
      } else {
        const qLower = qRaw.toLowerCase();
        snap = await getDocs(
          query(
            leadsRef,
            where('nameLower', '>=', qLower),
            where('nameLower', '<=', `${qLower}\uf8ff`),
            limit(25)
          )
        );
      }
      const rows = snap.docs
        .map((d) => ({ id: d.id, ...(d.data() as any) } as Lead))
        .filter((row: any) => row.id !== lead.id && !row.isMerged && !row.archived);
      setMergeResults(rows);
      if (!rows.length) setMergeError('No matching duplicate lead found.');
    } catch (err: any) {
      console.error('merge lead search failed', err);
      setMergeError(err?.message || 'Failed to search leads.');
    } finally {
      setMergeSearching(false);
    }
  };

  const handleMergeLead = async () => {
    if (!lead || !mergeSelectedLead || !canMergeLead) return;
    setMergeSaving(true);
    setMergeError(null);
    try {
      const fn = httpsCallable(functions, 'mergeLeads');
      await fn({
        primaryLeadId: lead.id,
        duplicateLeadId: mergeSelectedLead.id,
      });
      setMergeDialogOpen(false);
      setMergeSearchRaw('');
      setMergeResults([]);
      setMergeSelectedLead(null);
      setPostSubmitToastOpen(true);
      onUpdated?.({
        ...lead,
        mergedLeadIds: [
          ...(((lead as any).mergedLeadIds || []) as string[]),
          mergeSelectedLead.id,
        ],
      } as Lead);
    } catch (err: any) {
      console.error('merge lead failed', err);
      setMergeError(err?.message || 'Failed to merge lead.');
    } finally {
      setMergeSaving(false);
    }
  };

  const handleProjectChange = async (nextProjectId: string) => {
    if (!lead || !canEditLeadProject || savingProject) return;
    const fromProjectId = String((lead as any)?.projectId || '').trim();
    const toProjectId = String(nextProjectId || '').trim();
    if (!toProjectId || toProjectId === fromProjectId) {
      setProjectAnchorEl(null);
      return;
    }

    const nextProject = projectForId.get(toProjectId);
    if (!nextProject) {
      setError('Selected project is not available.');
      setProjectAnchorEl(null);
      return;
    }

    const fromProjectName = String(
      (fromProjectId ? projectForId.get(fromProjectId)?.name : '') ||
      (lead as any)?.projectName ||
      fromProjectId ||
      ''
    ).trim();
    const toProjectName = String(nextProject?.name || toProjectId).trim();
    const currentUnitType = String((lead as any)?.unitType || '').trim();
    const nextUnitTypes = Array.isArray(nextProject?.unitTypes)
      ? nextProject.unitTypes
        .map((x: any) => String(x || '').trim())
        .filter(Boolean)
      : [];
    const keepUnitType =
      !currentUnitType ||
      nextUnitTypes.length === 0 ||
      nextUnitTypes.some((x: string) => x.toLowerCase() === currentUnitType.toLowerCase());
    const nextUnitType = keepUnitType ? currentUnitType : null;

    try {
      setSavingProject(true);
      setError(null);
      const leadRef = doc(db, 'leads', lead.id);
      const activitiesRef = collection(leadRef, 'activities');
      await updateDoc(leadRef, {
        projectId: toProjectId,
        projectName: toProjectName,
        unitType: nextUnitType,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.email || null,
      });
      await addDoc(activitiesRef, {
        type: 'internal',
        event: 'project-change',
        projectFromId: fromProjectId || null,
        projectFromName: fromProjectName || null,
        projectToId: toProjectId,
        projectToName: toProjectName,
        unitTypeFrom: currentUnitType || null,
        unitTypeTo: nextUnitType || null,
        byEmail: currentUser?.email || null,
        at: serverTimestamp(),
      });
      await bumpLeadActivity(leadRef, 1);
      setProjectAnchorEl(null);
    } catch (err: any) {
      console.error('project update failed', err);
      setError(err?.message || 'Failed to update project');
    } finally {
      setSavingProject(false);
    }
  };

  const [visitUnitChoices, setVisitUnitChoices] = useState<string[]>([]);
  const [visitUnitChoiceInput, setVisitUnitChoiceInput] = useState<string>('');

  const [editingPromptId, setEditingPromptId] = useState<string | null>(null);
  const [editingPromptValue, setEditingPromptValue] = useState<string>('');
  const [editingPromptType, setEditingPromptType] = useState<'text' | 'date' | 'number' | 'select'>('text');
  const [editingPromptOptions, setEditingPromptOptions] = useState<string[]>([]);

  // Projects for appointment defaults/options
  const [projects, setProjects] = useState<any[]>([]);
  const [unitChoiceOptions, setUnitChoiceOptions] = useState<string[]>([]);
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'projects'), orderBy('createdAt', 'desc')),
      (snap) => setProjects(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }))),
      () => setProjects([])
    );
    return () => unsub();
  }, [db]);

  const visibleProjects = useMemo(() => {
    const r = (roleId || '').toString();
    const isAdmin = r === 'super-admin' || r === 'admin';
    if (isAdmin && (!projectIds || projectIds.length === 0)) return projects;
    const allowed = new Set((projectIds || []).filter(Boolean));
    return projects.filter((p) => allowed.has(p.id));
  }, [projectIds, projects, roleId]);

  const projectForId = useMemo(() => {
    const m = new Map<string, any>();
    visibleProjects.forEach((p) => m.set(p.id, p));
    return m;
  }, [visibleProjects]);

  const logUnitTypeOptions = useMemo(() => {
    const pid = logProjectIdDraft || (lead?.projectId ? String(lead.projectId) : '') || (currentProjectId || '');
    const p = pid ? projectForId.get(pid) : null;
    const raw = p?.unitTypes;
    return Array.isArray(raw) ? raw.filter((x: any) => typeof x === 'string' && x.trim()) : [];
  }, [currentProjectId, lead?.projectId, logProjectIdDraft, projectForId]);

  useEffect(() => {
    if (!open) return;
    if (!lead) return;
    // Prefill drafts from lead (or fallback project) so missing values can be captured during logging.
    const leadPid = lead?.projectId ? String(lead.projectId) : '';
    const leadUnit = lead?.unitType ? String(lead.unitType) : '';
    setLogProjectIdDraft((prev) => {
      if (prev) return prev;
      if (leadPid) return leadPid;
      const fallback = currentProjectId && projectForId.has(currentProjectId) ? currentProjectId : '';
      return fallback;
    });
    setLogUnitTypeDraft((prev) => (prev ? prev : (leadUnit || '')));
  }, [currentProjectId, lead, open, projectForId]);

  const apptUnitOptions = useMemo(() => {
    const p = apptProjectId ? projectForId.get(apptProjectId) : null;
    const raw = p?.unitTypes;
    return Array.isArray(raw) ? raw.filter((x: any) => typeof x === 'string' && x.trim()) : [];
  }, [apptProjectId, projectForId]);

  const visitProjectId = useMemo(() => {
    return String(logProjectIdDraft || (lead?.projectId as any) || currentProjectId || '').trim();
  }, [currentProjectId, lead?.projectId, logProjectIdDraft]);
  useEffect(() => {
    if (!open) return undefined;
    // Unit Choice is a unit number (A-101, G-4, etc). Load from the project's uploaded units.
    if (!visitProjectId) {
      setUnitChoiceOptions([]);
      return undefined;
    }
    const ref = collection(db, 'projects', visitProjectId, 'units');
    const q = query(ref, orderBy('unitNumberLower', 'asc'), limit(1000));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const vals = snap.docs
          .map((d) => (d.data() as any)?.unitNumber)
          .filter((v) => typeof v === 'string' && v.trim())
          .map((v) => String(v).trim());
        setUnitChoiceOptions(Array.from(new Set(vals)));
      },
      () => setUnitChoiceOptions([])
    );
    return () => unsub();
  }, [db, open, visitProjectId]);

  const resolveStatusId = (nameOrId: string): string | null => {
    const raw = String(nameOrId || '').trim();
    if (!raw) return null;
    const norm = raw.toLowerCase();
    const byId = statuses.find((s: any) => String(s?.id || '').trim().toLowerCase() === norm);
    if (byId?.id) return byId.id;
    const byLabel = statuses.find((s: any) => String(s?.label || '').trim().toLowerCase() === norm);
    return byLabel?.id || null;
  };

  const callOutcomeLabel = useMemo(() => {
    const m: Record<string, string> = {
      connected: 'Connected',
      // Legacy key (older activities). We now use `cnr` instead.
      'no-answer': 'CNR',
      busy: 'Busy',
      'call-back': 'Call Back Later',
      'wrong-number': 'Wrong Number',
      cnr: 'CNR',
    };
    return m;
  }, []);

  const visitCount = useMemo(
    () => activities.filter((a) => a.type === 'visit').length,
    [activities]
  );
  const hasExistingRefBy = useMemo(
    () =>
      Boolean(
        lead?.referredBrokerId ||
          lead?.referredBrokerName ||
          lead?.referredAgencyName ||
          (lead as any)?.brokerName ||
          String((lead as any)?.refByName || '').trim()
      ),
    [lead]
  );
  const requireFirstVisitRefByChoice =
    activityType === 'visit' &&
    visitCount === 0 &&
    !hasExistingRefBy;

  const callOutcomeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const a of activities) {
      if (a.type !== 'call') continue;
      const rawKey = (a.callOutcome || '').toString();
      const key = rawKey === 'no-answer' ? 'cnr' : rawKey; // normalize legacy key
      if (!key) continue;
      counts[key] = (counts[key] || 0) + 1;
    }
    return counts;
  }, [activities]);

  const connectedCallCount = useMemo(
    () => callOutcomeCounts.connected || 0,
    [callOutcomeCounts]
  );

  const callBreakdownRows = useMemo(() => {
    return Object.entries(callOutcomeCounts)
      .filter(([k, v]) => k !== 'connected' && v > 0)
      .map(([k, v]) => ({ key: k, label: callOutcomeLabel[k] || k, count: v }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [callOutcomeCounts, callOutcomeLabel]);

  const callBreakdownOpen = Boolean(callBreakdownAnchorEl);
  const visitDatesOpen = Boolean(visitDatesAnchorEl);

  const visitWithLabelFor = (a: Activity) => {
    if (a.visitWith === 'alone') return 'Alone';
    if (a.visitWith === 'family') return 'With Family';
    if (a.visitWith === 'friend') return 'With Friend/Relative';
    if (a.visitWith === 'other') return a.visitWithOther ? `Other: ${a.visitWithOther}` : 'Other';
    return null;
  };

  const visitArrivalLabelFor = (a: Activity) => {
    if (a.visitArrival === 'walk-in') return 'Walk-in';
    if (a.visitArrival === 'c2c') return a.visitArrivalC2cName ? `C2C: ${a.visitArrivalC2cName}` : 'C2C';
    if (a.visitArrival === 'other') return a.visitArrivalOtherNote ? `Other: ${a.visitArrivalOtherNote}` : 'Other';
    if (a.visitArrival === 'unknown') return 'Unknown source';
    if (a.visitArrival === 'broker') {
      const broker = [a.visitArrivalBrokerAgencyName, a.visitArrivalBrokerName].filter(Boolean).join(' - ');
      return broker ? `Broker: ${broker}` : 'Broker';
    }
    return null;
  };

  const visitItems = useMemo(() => {
    return activities
      .filter((a) => a.type === 'visit')
      .map((a) => {
        const d: Date | null = a.at?.toDate ? a.at.toDate() : null;
        const withLabel = visitWithLabelFor(a);
        return { at: d, withLabel };
      })
      .filter((x) => !!x.at) as { at: Date; withLabel: string | null }[];
  }, [activities]);

  useEffect(() => {
    if (!lead) return;
    const v = lead.rating;
    setRatingValue(typeof v === 'number' && Number.isFinite(v) ? v : 0);
  }, [lead]);

  useEffect(() => {
    if (!lead) return;
    // Prefill appointment UI from existing appointment (if any)
    const appt = lead.appointment || null;
    setApptFormOpen(false);
    setApptCompleteOpen(false);
    setApptOutcome('completed');
    setApptStatusChange('');
    setApptMeetingPointInput('');
    setApptMeetingPoints([]);
    setApptCompleteNoteInput('');
    setApptCompleteNotes([]);
    setApptVisitWith('');
    setApptVisitWithOther('');
    setApptMeetingPointInput('');
    setApptMeetingPoints([]);
    setApptRescheduleAt('');
    setApptFollowUpDate('');

    if (appt?.type) setApptType(appt.type);
    const scheduledAt = appt?.scheduledAt?.toDate ? appt.scheduledAt.toDate() : null;
    setApptScheduledAt(scheduledAt ? new Date(scheduledAt.getTime() - scheduledAt.getTimezoneOffset() * 60000).toISOString().slice(0, 16) : '');
    setApptProjectId((appt?.projectId as string) || (lead.projectId as string) || (currentProjectId || '') || (visibleProjects[0]?.id || ''));
    setApptUnitType((appt?.unitType as string) || (lead.unitType as string) || '');
    setApptVisitWith((appt?.visitWith as any) || '');
    setApptVisitWithOther((appt?.visitWithOther as string) || '');
    const mp =
      Array.isArray((appt as any)?.meetingPoints)
        ? ((appt as any).meetingPoints as any[]).filter((x) => typeof x === 'string' && x.trim())
        : typeof (appt as any)?.meetingPoint === 'string' && (appt as any).meetingPoint.trim()
          ? [(appt as any).meetingPoint.trim()]
          : [];
    setApptMeetingPoints(mp);
    setApptRating(typeof lead.rating === 'number' && Number.isFinite(lead.rating) ? lead.rating : 0);
  }, [currentProjectId, lead, visibleProjects]);

  useEffect(() => {
    // Keep visit-only fields tidy when switching types.
    if (activityType !== 'visit') {
      setVisitWith('');
      setVisitWithOther('');
    }
    // Don't keep a previous call outcome selected when switching away from Call.
    if (activityType !== 'call') {
      setCallOutcome('');
    }
  }, [activityType]);

  // fetch statuses
  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, 'leadStatuses'), orderBy('order', 'asc')), (snap) => {
      setStatuses(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [db]);

  // fetch users
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'users'), (snap) => {
      setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [db]);

  useEffect(() => {
    const brokersRef = collection(db, 'brokers');
    const q = query(brokersRef, orderBy('displayNameLower', 'asc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const data: BrokerOption[] = snap.docs.map((d) => {
          const v = d.data() as any;
          const brokerName = typeof v.brokerName === 'string' ? v.brokerName : '';
          const agencyName = typeof v.agencyName === 'string' ? v.agencyName : null;
          const phone = typeof v.phone === 'string' ? v.phone : null;
          const label = `${agencyName ? `${agencyName} - ` : ''}${brokerName}`.trim();
          return { id: d.id, brokerName, agencyName, phone, label };
        });
        setBrokers(data.filter((b) => b.label.length > 0));
      },
      () => setBrokers([])
    );
    return () => unsub();
  }, [db]);

  // Prefill visit ref-by (used for Social Ad leads) from the lead itself.
  useEffect(() => {
    if (!open) return;
    if (!lead) return;
    if (!isSocialAdLead) return;
    if (activityType !== 'visit') return;
    const currentId = lead?.referredBrokerId ? String(lead.referredBrokerId) : '';
    if (!currentId) return;

    setVisitReferredBroker((prev) => {
      if (prev?.id) return prev; // don't override user's selection for this visit

      const inOptions = brokers.find((b) => b.id === currentId);
      if (inOptions) return inOptions;

      const brokerName = lead?.referredBrokerName ? String(lead.referredBrokerName) : '';
      const agencyName = lead?.referredAgencyName ? String(lead.referredAgencyName) : '';
      const label =
        agencyName && brokerName ? `${agencyName} - ${brokerName}` : brokerName || agencyName || currentId;
      return {
        id: currentId,
        brokerName: brokerName || label,
        agencyName: agencyName || null,
        label,
      };
    });
  }, [
    activityType,
    brokers,
    isSocialAdLead,
    lead,
    lead?.referredAgencyName,
    lead?.referredBrokerId,
    lead?.referredBrokerName,
    open,
  ]);

  // fetch activities for this lead
  useEffect(() => {
    if (!open || !lead?.id) return;
    setLoadingActivities(true);
    setTimelineError(null);
    let active = true;
    const activitiesCol = collection(db, 'leads', lead.id, 'activities');
    const applyActivities = (docs: Activity[]) => {
      if (!active) return;
      setActivities(
        docs.sort((a, b) => {
          const bMs = toDateMaybe(b.at)?.getTime() || 0;
          const aMs = toDateMaybe(a.at)?.getTime() || 0;
          return bMs - aMs;
        })
      );
      setLoadingActivities(false);
      setTimelineError(null);
    };
    const timelineErrorMessage = (err: any) => {
      const detail = String(err?.message || err?.code || '').trim();
      return detail ? `Failed to load timeline: ${detail}` : 'Failed to load timeline';
    };
    let unsub = () => {};
    let didFallback = false;
    const loadingTimer = window.setTimeout(() => {
      if (!active) return;
      setTimelineError('Timeline is taking longer than expected. Close and reopen this lead if it does not appear.');
      setLoadingActivities(false);
    }, 6000);
    unsub = onSnapshot(
      query(activitiesCol, orderBy('at', 'desc')),
      (snap) => {
        window.clearTimeout(loadingTimer);
        applyActivities(snap.docs.map((d) => ({ id: d.id, ...d.data() } as Activity)));
      },
      (err) => {
        window.clearTimeout(loadingTimer);
        console.error('activities fetch error', err);
        if (didFallback) {
          if (!active) return;
          setTimelineError(timelineErrorMessage(err));
          setLoadingActivities(false);
          return;
        }
        didFallback = true;
        try {
          unsub();
        } catch (_) {
          // ignore cleanup failure while switching listener strategy
        }
        unsub = onSnapshot(
          activitiesCol,
          (fallbackSnap) => {
            window.clearTimeout(loadingTimer);
            applyActivities(fallbackSnap.docs.map((d) => ({ id: d.id, ...d.data() } as Activity)));
          },
          (fallbackErr) => {
            window.clearTimeout(loadingTimer);
            if (!active) return;
            console.error('activities fallback fetch error', fallbackErr);
            setTimelineError(timelineErrorMessage(fallbackErr));
            setLoadingActivities(false);
          }
        );
      }
    );
    return () => {
      active = false;
      window.clearTimeout(loadingTimer);
      unsub();
    };
  }, [db, lead?.id, open]);

  useEffect(() => {
    if (!lead) return;
    setNewStatus('');
    const nextFollowUpDateValue = toDateMaybe(lead.nextFollowUpDate);
    setFollowUpDate(nextFollowUpDateValue ? nextFollowUpDateValue.toISOString().slice(0, 10) : '');
    const followUpAtDate = toDateMaybe((lead as any)?.nextFollowUpAt);
    setFollowUpTimeEnabled(!!followUpAtDate);
    setFollowUpTime(followUpAtDate ? localTimeInputValue(followUpAtDate) : '');
    setNoteInput('');
    setNoteList(initialActivityNotes.filter((note) => typeof note === 'string' && note.trim()));
    // Meta submissions can prefill an internal activity; normal lead opens start blank.
    setActivityType(initialActivityNotes.length && initialActivityType ? initialActivityType : '');
    setCallOutcome('');
    setVisitWith('');
    setVisitWithOther('');
    setPromptDraft({});
    setEditingPromptId(null);
    setEditingPromptValue('');
    setError(null);
    setOnlyCustomerContact(true);
    setLogExpanded(true);
    // My Today ✓ Visited / Finish visit: open the Visit log, filled with what
    // reception recorded at check-in today.
    if (startVisitLog) startVisitFromReception(lead);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialActivityNotes, initialActivityType, lead, startVisitLog]);

  useLayoutEffect(() => {
    if (!open || !lead?.id) return;

    const scrollTop = () => {
      const el = drawerScrollRef.current;
      if (!el) return;
      if (el.contains(document.activeElement)) {
        (document.activeElement as HTMLElement | null)?.blur?.();
      }
      el.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    };

    scrollTop();
    const raf1 = requestAnimationFrame(scrollTop);
    const raf2 = requestAnimationFrame(() => requestAnimationFrame(scrollTop));
    const timers = [
      window.setTimeout(scrollTop, 60),
      window.setTimeout(scrollTop, 180),
      window.setTimeout(scrollTop, 360),
    ];

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [open, lead?.id]);

  // handle drag resize
  const startResize = (e: React.MouseEvent) => {
    setResizing(true);
    onResizingChange?.(true);
    e.stopPropagation();
    e.preventDefault();
  };

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!resizing) return;
      const newWidth = Math.min(700, Math.max(360, window.innerWidth - e.clientX));
      setDrawerWidth(newWidth);
      onWidthChange?.(newWidth);
    };
    const onUp = () => {
      if (resizing) {
        setResizing(false);
        onResizingChange?.(false);
      }
    };
    if (resizing) {
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    }
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [resizing, onResizingChange, onWidthChange]);

  useEffect(() => {
    if (open) {
      onWidthChange?.(drawerWidth);
    } else {
      onWidthChange?.(0);
    }
  }, [open, drawerWidth, onWidthChange]);

  const isClosedStatusPreview = (statusIdOrLabel?: string | null) => {
    if (!statusIdOrLabel) return false;
    const norm = String(statusIdOrLabel).trim().toLowerCase();
    if (norm === 'closed' || norm === 'completed') return true;
    const byId = statuses.find((s: any) => s?.id === statusIdOrLabel);
    const byLabel = statuses.find((s: any) => typeof s?.label === 'string' && String(s.label).trim().toLowerCase() === norm);
    const byStartsWithNew =
      norm === 'new'
        ? statuses.find((s: any) => typeof s?.label === 'string' && String(s.label).trim().toLowerCase().startsWith('new'))
        : null;
    const doc = byId || byLabel || byStartsWithNew;
    const labelNorm = typeof doc?.label === 'string' ? String(doc.label).trim().toLowerCase() : '';
    const idNorm = typeof doc?.id === 'string' ? String(doc.id).trim().toLowerCase() : '';
    return labelNorm === 'closed' || idNorm === 'closed' || labelNorm === 'completed' || idNorm === 'completed';
  };

  const statusWillBeClosedPreview = isClosedStatusPreview(newStatus || lead?.status || null);
  const isClosingNowPreviewPreLead =
    Boolean(newStatus) &&
    !isClosedStatusPreview(lead?.status || null) &&
    isClosedStatusPreview(newStatus);
  useEffect(() => {
    if (statusWillBeClosedPreview && followUpDate) setFollowUpDate('');
    if (statusWillBeClosedPreview && followUpTimeEnabled) {
      setFollowUpTimeEnabled(false);
      setFollowUpTime('');
    }
  }, [statusWillBeClosedPreview, followUpDate, followUpTimeEnabled]);

  const closeDecisionStageAuto = useMemo(() => {
    const idToLabel = new Map<string, string>();
    statuses.forEach((s: any) => {
      const id = normalizeStatusForStage(String(s?.id || ''));
      const label = normalizeStatusForStage(String(s?.label || ''));
      if (id && label) idToLabel.set(id, label);
    });

    const resolveStatus = (raw: unknown): string => {
      const token = normalizeStatusForStage(String(raw || ''));
      if (!token) return '';
      return idToLabel.get(token) || token;
    };

    const seen = new Set<string>();
    const add = (raw: unknown) => {
      const resolved = resolveStatus(raw);
      if (resolved) seen.add(resolved);
    };

    add(lead?.status);
    activities.forEach((a) => {
      add(a.statusFrom);
      add(a.statusTo);
    });

    if (
      Array.from(seen).some((s) => s.includes('book') || s.includes('booking'))
    ) {
      return 'Booking Stage';
    }
    if (seen.has('negotiation')) return 'Negotiation Stage';
    if (seen.has('site visited')) return 'Site Visit Done';
    if (
      seen.has('qualify') ||
      seen.has('appointment') ||
      seen.has('prospect') ||
      seen.has('cnr') ||
      seen.has('reopen')
    ) {
      return 'Follow-up in Progress';
    }
    return 'New Lead';
  }, [activities, lead?.status, statuses]);

  useEffect(() => {
    if (!isClosingNowPreviewPreLead) return;
    if (closePrimaryReason === 'Completed') return;
    if (closeDecisionStage) return;
    setCloseDecisionStage(closeDecisionStageAuto);
  }, [
    closeDecisionStage,
    closeDecisionStageAuto,
    closePrimaryReason,
    isClosingNowPreviewPreLead,
  ]);

  // Re-ask visit-related "prompts" on every Visit; prefill from saved answers.
  // Must be above the early `if (!lead) return null;` to satisfy hooks ordering.
  useEffect(() => {
    if (!open || !lead) return;
    if (activityType !== 'visit') return;
    const toStr = (ans: any) => {
      if (!ans) return '';
      const v = ans.value;
      if (v?.toDate) return v.toDate().toLocaleDateString();
      if (typeof v === 'number') return String(v);
      if (typeof v === 'string') return v;
      return '';
    };
    setVisitBudgetFit(toStr(lead.promptAnswers?.legacy_budget) || '');
    setVisitFloorChoice(toStr(lead.promptAnswers?.legacy_floorChoice) || '');
    setVisitUnitChoices(asStringArray((lead.promptAnswers?.legacy_unitChoice as any)?.value));
    setVisitUnitChoiceInput('');
    setVisitResidenceInput('');
    setVisitOccupationInput('');
  }, [open, lead, activityType]);

  const [editPA, setEditPA] = useState<null | { id: string; label: string; value: string }>(null);
  const [editPAInput, setEditPAInput] = useState('');
  const [editPASaving, setEditPASaving] = useState(false);

  const saveEditPA = async () => {
    if (!lead || !editPA) return;
    const next = editPAInput.trim();
    setEditPASaving(true);
    try {
      const leadRef = doc(db, 'leads', lead.id);
      // Update the existing prompt answer in-place; this is not a new "ask" during log activity.
      await updateDoc(leadRef, {
        [`promptAnswers.${editPA.id}.value`]: next,
        [`promptAnswers.${editPA.id}.editedAt`]: serverTimestamp(),
        [`promptAnswers.${editPA.id}.editedBy`]: currentUser?.email || null,
      });
      setEditPA(null);
      setEditPAInput('');
      onUpdatedRef.current?.({
        promptAnswers: {
          ...(lead.promptAnswers || {}),
          [editPA.id]: {
            ...(lead.promptAnswers?.[editPA.id] as any),
            value: next,
          },
        },
      } as any);
    } finally {
      setEditPASaving(false);
    }
  };

  if (!lead) return null;

  // Keep accent generation consistent with the compact card view.
  const ACCENT_PALETTE = ['#1e88e5', '#00897b', '#43a047', '#fb8c00', '#8e24aa', '#e53935'];
  const hashString = (input: string) => {
    let h = 0;
    for (let i = 0; i < input.length; i += 1) h = (h * 31 + input.charCodeAt(i)) | 0;
    return Math.abs(h);
  };
  const accentKey = String(lead.status || lead.source || lead.id);
  const accentFromStatus = (() => {
    const norm = String(lead.status || '').trim().toLowerCase();
    const byId = statuses.find((s: any) => s?.id === lead.status);
    const byLabel = statuses.find((s: any) => typeof s?.label === 'string' && String(s.label).trim().toLowerCase() === norm);
    const byStartsWithNew =
      norm === 'new'
        ? statuses.find((s: any) => typeof s?.label === 'string' && String(s.label).trim().toLowerCase().startsWith('new'))
        : null;
    const doc = byId || byLabel || byStartsWithNew;
    const c = typeof (doc as any)?.color === 'string' ? String((doc as any).color).trim() : '';
    return /^#([0-9a-fA-F]{6})$/.test(c) ? c : null;
  })();
  const accent = accentFromStatus || ACCENT_PALETTE[hashString(accentKey) % ACCENT_PALETTE.length];
  // Match the compact-card status chip style (soft tint, not solid fill).
  const accentBg = alpha(accent, 0.18);
  const accentBgHover = alpha(accent, 0.24);
  const accentBorder = alpha(accent, 0.35);
  const accentText = alpha('#000', 0.78);

  const accentFilledChipSx = {
    backgroundColor: accentBg,
    color: accentText,
    border: `1px solid ${accentBorder}`,
    '&:hover': { backgroundColor: accentBgHover },
    '& .MuiChip-label': { fontWeight: 700 },
  } as const;
  const accentOutlinedChipSx = {
    // Unselected chips use a neutral border (not status-colored) for better visual hierarchy.
    borderColor: 'divider',
    '&:hover': { borderColor: 'text.secondary', backgroundColor: 'action.hover' },
  } as const;
  const accentContainedBtnSx = {
    backgroundColor: accentBg,
    color: accentText,
    boxShadow: 'none',
    '&:hover': { backgroundColor: accentBgHover, boxShadow: 'none' },
  } as const;
  const accentOutlinedBtnSx = {
    color: accentText,
    borderColor: accentBorder,
    '&:hover': { borderColor: alpha(accent, 0.55), backgroundColor: alpha(accent, 0.06) },
  } as const;
  // Primary submit CTA should use the main status color (solid), not the soft accent tint.
  const submitBtnSx = {
    backgroundColor: accent,
    color: '#fff',
    fontWeight: 700,
    boxShadow: 'none',
    '&:hover': { backgroundColor: darken(accent, 0.12), boxShadow: 'none' },
    '& .MuiButton-startIcon': { color: '#fff' },
  } as const;

  // "Cards" log activity UI tokens (lighter version of the screenshot style).
  const cardsUi = {
    cardSx: {
      p: 1.75,
      borderRadius: 3,
      bgcolor: 'rgba(15, 23, 42, 0.04)', // light slate wash
      border: '1px solid',
      borderColor: 'rgba(15, 23, 42, 0.08)',
    },
    pillUnselectedSx: {
      borderRadius: 999,
      px: 1.25,
      py: 2.25,
      fontWeight: 700,
      borderWidth: 1,
      borderStyle: 'solid',
      bgcolor: 'rgba(255, 255, 255, 0.9)',
      borderColor: 'rgba(15, 23, 42, 0.14)',
      color: 'text.primary',
      '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' },
    },
    pillSelectedSx: {
      borderRadius: 999,
      px: 1.25,
      py: 2.25,
      fontWeight: 700,
      borderWidth: 1,
      borderStyle: 'solid',
      ...accentFilledChipSx,
      borderColor: 'transparent',
    },
  } as const;

  const findStatusDoc = (val?: string | null) => {
    if (!val) return null;
    const byId = statuses.find((s) => s.id === val);
    if (byId) return byId;
    const norm = val.trim().toLowerCase();
    const byLabel = statuses.find(
      (s) => typeof s.label === 'string' && s.label.trim().toLowerCase() === norm
    );
    if (byLabel) return byLabel;
    // Legacy: existing leads may store `status: "new"` while the configured status id differs.
    if (norm === 'new') {
      const byStartsWith = statuses.find(
        (s) => typeof s.label === 'string' && s.label.trim().toLowerCase().startsWith('new')
      );
      if (byStartsWith) return byStartsWith;
    }
    return null;
  };

  const isClosedStatus = (statusIdOrLabel?: string | null) => {
    if (!statusIdOrLabel) return false;
    const norm = String(statusIdOrLabel).trim().toLowerCase();
    if (norm === 'closed' || norm === 'completed') return true;
    const doc = findStatusDoc(statusIdOrLabel);
    const labelNorm = typeof doc?.label === 'string' ? doc.label.trim().toLowerCase() : '';
    const idNorm = typeof doc?.id === 'string' ? doc.id.trim().toLowerCase() : '';
    return labelNorm === 'closed' || idNorm === 'closed' || labelNorm === 'completed' || idNorm === 'completed';
  };

  const currentStatusDocResolved = findStatusDoc(lead.status);
  const statusDoc = currentStatusDocResolved;
  const allowedStatus = statusDoc
    ? Array.from(new Set([statusDoc.id, ...(statusDoc.allowedNext || [])]))
    : statuses.map((s) => s.id);

  const selectedStatusId = newStatus || lead.status;
  const selectedStatusDoc = findStatusDoc(selectedStatusId);
  const selectedPrompts = (
    Array.isArray(selectedStatusDoc?.prompts) ? selectedStatusDoc?.prompts : []
  )
    .filter((p: any) => String(p?.label || '').trim().toLowerCase() !== 'close reason') as Array<{
      id: string;
      label: string;
      type: 'text' | 'date' | 'number' | 'select';
      options?: string[];
    }>;

  const hasPromptValue = (promptId: string) => {
    const raw = (lead.promptAnswers as any)?.[promptId];
    const value = raw && typeof raw === 'object' && 'value' in raw ? raw.value : raw;
    if (value === null || value === undefined) return false;
    if (Array.isArray(value)) {
      return value.some((v) => String(v || '').trim().length > 0);
    }
    return String(value).trim().length > 0;
  };
  const hasLegacyBudget = hasPromptValue('legacy_budget');
  const hasLegacyFloorChoice = hasPromptValue('legacy_floorChoice');
  const hasLegacyUnitChoice = hasPromptValue('legacy_unitChoice');
  const hasLegacyResidence = hasPromptValue('legacy_currentResidence');
  const hasLegacyOccupation = hasPromptValue('legacy_occupation');

  const userLabel = (val?: string | null) => {
    if (!val) return '—';
    const u = users.find((usr) => usr.email === val);
    if (u?.name) return u.name;
    const local = val.includes('@') ? val.split('@')[0] : val;
    return local || val;
  };

  const currentStatusLabel =
    currentStatusDocResolved?.label || lead.status || 'Current';
  const currentAssigneeLabel = lead.assignedTo
    ? userLabel(lead.assignedTo)
    : (lead.assignedToName ? String(lead.assignedToName) : 'Unassigned');
  const websiteRequestValue =
    lead.websiteRequest?.type ||
    lead.websiteRequest?.label ||
    lead.enquiryType;
  const websiteRequest = websiteRequestValue
    ? websiteRequestConfig(websiteRequestValue)
    : null;
  const websiteRequestCompleted =
    String(lead.websiteRequest?.status || 'pending').toLowerCase() === 'completed';
  const websiteRequestReceivedAt =
    toDateMaybe(lead.websiteRequest?.receivedAt) ||
    toDateMaybe(lead.createdAt);
  const phoneDigits = normalizePhoneDigits(lead.phone);
  const telHref = phoneDigits ? `tel:+${phoneDigits}` : '';
  const primaryPhoneKey = phoneIdentity(lead.phone);
  const alternatePhoneRows = Array.from(
    new Map(
      [
        ...((lead.alternatePhones || []) as string[]),
        ...((lead.phone10s || []) as string[]),
      ]
        .map((phone) => String(phone || '').trim())
        .filter(Boolean)
        .filter((phone) => phoneIdentity(phone) !== primaryPhoneKey)
        .map((phone) => [phoneIdentity(phone), phone] as [string, string])
    ).values()
  );
  const refBrokerId = String(lead.referredBrokerId || '').trim();
  const refByBroker = refBrokerId
    ? brokers.find((b) => b.id === refBrokerId) || null
    : null;
  const refByPhoneRaw = String(
    (lead as any).referredBrokerPhone || refByBroker?.phone || ''
  ).trim();
  const refByPhoneDigits = normalizePhoneDigits(refByPhoneRaw);
  const refByTelHref = refByPhoneDigits ? `tel:+${refByPhoneDigits}` : '';
  const refByPhoneDisplay = refByPhoneDigits
    ? formatPhoneDisplay(refByPhoneDigits)
    : '';
  const createdAtDate: Date | null = (() => {
    const ts = (lead as any)?.createdAt;
    if (ts?.toDate) return ts.toDate();
    if (typeof ts === 'string') {
      const d = new Date(ts);
      if (!Number.isNaN(d.getTime())) return d;
    }
    const imp = (lead as any)?.importedAt;
    if (imp?.toDate) return imp.toDate();
    if (typeof imp === 'string') {
      const d = new Date(imp);
      if (!Number.isNaN(d.getTime())) return d;
    }
    return null;
  })();
  const statusWillBeClosed = isClosedStatus(newStatus || lead.status);
  const isClosingNowPreview =
    Boolean(newStatus) && !isClosedStatus(lead.status) && isClosedStatus(newStatus);
  const followUpRequired =
    !statusWillBeClosed && activityType === 'call' && (callOutcome === 'cnr' || callOutcome === 'call-back');

  const todayLocalDateInput = localDateInputValue(new Date());

  const displayAnswer = (ans: any) => {
    if (!ans) return '';
    const v = ans.value;
    if (v?.toDate) return v.toDate().toLocaleDateString();
    if (typeof v === 'number') return String(v);
    if (Array.isArray(v)) return v.filter((x) => typeof x === 'string' && x.trim()).join(', ');
    if (typeof v === 'string') return v;
    return JSON.stringify(v);
  };

  const findPromptDef = (promptId: string) => {
    for (const s of statuses) {
      const prompts = Array.isArray(s.prompts) ? s.prompts : [];
      const found = prompts.find((p: any) => p?.id === promptId);
      if (found) return found as any;
    }
    return null;
  };

  const openEditPrompt = (promptId: string) => {
    if (!lead?.promptAnswers?.[promptId]) return;
    const existing = lead.promptAnswers[promptId];
    const def = findPromptDef(promptId);
    const type = (def?.type || existing?.type || 'text') as any;
    const options = (def?.options || []) as string[];
    setEditingPromptId(promptId);
    setEditingPromptType(type);
    setEditingPromptOptions(Array.isArray(options) ? options : []);
    if (type === 'date') {
      const d = existing.value?.toDate ? existing.value.toDate() : null;
      setEditingPromptValue(d ? d.toISOString().slice(0, 10) : '');
      return;
    }
    setEditingPromptValue(displayAnswer(existing));
  };

  const handleSavePromptEdit = async () => {
    if (!lead || !editingPromptId) return;
    if (!canEditPromptAnswers) return;
    setSaving(true);
    setError(null);
    try {
      const promptId = editingPromptId;
      const existing = lead.promptAnswers?.[promptId];
      if (!existing) {
        setEditingPromptId(null);
        setSaving(false);
        return;
      }
      const def = findPromptDef(promptId);
      const label = (def?.label || existing?.label || promptId) as string;
      const type = (def?.type || existing?.type || 'text') as any;
      const raw = editingPromptValue.trim();

      let value: any = raw;
      if (type === 'number') {
        if (raw && Number.isNaN(Number(raw))) {
          setError(`Invalid number for: ${label}`);
          setSaving(false);
          return;
        }
        value = raw ? Number(raw) : null;
      } else if (type === 'date') {
        if (raw && Number.isNaN(new Date(raw).getTime())) {
          setError(`Invalid date for: ${label}`);
          setSaving(false);
          return;
        }
        value = raw ? Timestamp.fromDate(new Date(raw)) : null;
      } else {
        value = raw || null;
      }

      const leadRef = doc(db, 'leads', lead.id);
      await updateDoc(leadRef, {
        [`promptAnswers.${promptId}`]: {
          ...existing,
          value,
          editedAt: serverTimestamp(),
          editedBy: currentUser?.email || null,
        },
      });

      setEditingPromptId(null);
      setEditingPromptValue('');
      setSaving(false);
    } catch (err: any) {
      console.error('prompt edit failed', err);
      setError(err?.message || 'Failed to edit prompt answer');
      setSaving(false);
    }
  };

  // Presenter of today's reception visit saving the Visit log on someone
  // else's lead: ask whether to take the lead over.
  const receptionToday = (() => {
    const rv = (lead as any)?.receptionVisit;
    return rv && rv.dayKey === dayKeyIST() ? rv : null;
  })();
  const isReceptionPresenter =
    !!receptionToday && receptionToday.status === 'with' &&
    String(receptionToday.presenterEmail || '').toLowerCase() === String(currentUser?.email || '').toLowerCase();
  const requestLog = () => {
    const owner = String((lead as any)?.assignedTo || '').trim().toLowerCase();
    const me = String(currentUser?.email || '').toLowerCase();
    if (activityType === 'visit' && isReceptionPresenter && owner && owner !== me) {
      setOwnerAskOpen(true);
      return;
    }
    void handleLog();
  };

  const handleLog = async (
    reopenOptions?: { reason: string; note: string; sendWhatsapp: boolean },
    receptionAssignToMe = false
  ) => {
    setOwnerAskOpen(false);
    setError(null);
    setLogSubmitError(null);
    setSaving(true);
    setLogSubmitState('saving');
    try {
      if (!activityType) {
        const msg = 'Select Type.';
        setError(msg);
        setLogSubmitError(msg);
        setSaving(false);
        setLogSubmitState('idle');
        return;
      }

      // optional updates
      const updates: Record<string, any> = {};
      let statusFrom: string | undefined;
      let statusTo: string | undefined;
      let followAt: Timestamp | null | undefined;
      let followAtExact: Timestamp | null | undefined;
      const followFrom = lead.nextFollowUpDate || null;
      const followFromAt = (lead as any).nextFollowUpAt || null;
      let refByFromId: string | null | undefined;
      let refByToId: string | null | undefined;
      let refByToName: string | null | undefined;
      let refByToAgency: string | null | undefined;
      let refByFromName: string | null | undefined;
      let refByFromAgency: string | null | undefined;
      const activityPromptValues: Record<string, any> = {};
      const willBeClosed = isClosedStatus(newStatus || lead.status);
      const wasClosed = isClosedStatus(lead.status);
      const isClosingNow = Boolean(newStatus) && !wasClosed && willBeClosed;
      const isReopeningNow = Boolean(newStatus) && wasClosed && !willBeClosed;
      if (isReopeningNow && !reopenOptions) {
        setSaving(false);
        setLogSubmitState('idle');
        setReopenDialogOpen(true);
        return;
      }
      const normalizedReopenReason = String(reopenOptions?.reason || '').trim();
      const normalizedReopenNote = String(reopenOptions?.note || '').trim();
      if (isReopeningNow && !normalizedReopenReason) {
        const msg = 'Select reopen reason.';
        setError(msg);
        setLogSubmitError(msg);
        setSaving(false);
        setLogSubmitState('idle');
        setReopenDialogOpen(true);
        return;
      }
      const isCloseCompetitor =
        closePrimaryReason === 'Loss due to Competitor' ||
        closeSecondaryReasons.includes('Loss due to Competitor');
      const isCompletedClose = closePrimaryReason === 'Completed';
      const isAnotherLocationClose = closePrimaryReason === 'Interested in Another Location';
      const isBoughtAnotherLocationClose =
        closePrimaryReason === 'Purchased in Another Location';
      const isNeedOtherTypeClose = closePrimaryReason === 'Need Other Type';
      const isBudgetFinancialClose = closePrimaryReason === 'Budget / Financial Issue';
      const isDistanceIssueClose = closePrimaryReason === 'Distance Issue';
      const resolvedCloseDecisionStage =
        closeDecisionStage || closeDecisionStageAuto || 'New Lead';
      const normalizedPrimaryOtherInput = toProperCaseWords(closePrimaryOtherReason);
      const normalizedInterestedLocation = toProperCaseWords(closeInterestedLocation);
      const normalizedBoughtAtLocation = toProperCaseWords(closeBoughtAtLocation);
      const normalizedRequiredTypeLocation = toProperCaseWords(closeRequiredTypeLocation);
      const normalizedDistancePreferredArea = toProperCaseWords(closeDistancePreferredArea);
      const normalizedCompetitorProject = toProperCaseWords(closeCompetitorProject);
      const matchedPrimaryOther = findOptionIgnoreCase(
        closeOtherReasonOptions,
        normalizedPrimaryOtherInput
      );
      const matchedInterestedLocation = findOptionIgnoreCase(
        closeInterestedLocationOptions,
        normalizedInterestedLocation
      );
      const matchedBoughtAtLocation = findOptionIgnoreCase(
        closeBoughtAtLocationOptions,
        normalizedBoughtAtLocation
      );
      const matchedRequiredTypeLocation = findOptionIgnoreCase(
        closeRequiredTypeLocationOptions,
        normalizedRequiredTypeLocation
      );
      const matchedDistanceArea = findOptionIgnoreCase(
        closeDistanceAreaOptions,
        normalizedDistancePreferredArea
      );
      const normalizedPrimaryOtherReason = matchedPrimaryOther || normalizedPrimaryOtherInput;
      const resolvedInterestedLocation = matchedInterestedLocation || normalizedInterestedLocation;
      const resolvedBoughtAtLocation = matchedBoughtAtLocation || normalizedBoughtAtLocation;
      const resolvedRequiredTypeLocation =
        matchedRequiredTypeLocation || normalizedRequiredTypeLocation;
      const resolvedDistancePreferredArea = matchedDistanceArea || normalizedDistancePreferredArea;
      const resolvedClosePrimaryReason =
        closePrimaryReason === 'Other' ? normalizedPrimaryOtherReason : closePrimaryReason;
	      // Some leads (e.g., Social Ad) may have blank project/unit type on creation; capture them during a Visit log.
	      const needProject = !String((lead as any)?.projectId || '').trim();
	      const needUnitType = !String((lead as any)?.unitType || '').trim();
	      if (activityType === 'visit' && (needProject || needUnitType)) {
	        const pid = String(logProjectIdDraft || (lead as any)?.projectId || '').trim();
	        const ut = String(logUnitTypeDraft || (lead as any)?.unitType || '').trim();
        if (needProject && !pid) {
          const msg = 'Select Project.';
	          setError(msg);
          setLogSubmitError(msg);
          setSaving(false);
          setLogSubmitState('idle');
          return;
        }
        if (needUnitType && !ut) {
          const msg = 'Enter Unit Type.';
          setError(msg);
          setLogSubmitError(msg);
          setSaving(false);
          setLogSubmitState('idle');
          return;
        }
        if (needProject && pid) {
          updates.projectId = pid;
          updates.projectName = projectForId.get(pid)?.name || null;
        }
        if (needUnitType && ut) {
          updates.unitType = ut;
        }
      }

      if (activityType === 'call' && !callOutcome) {
        const msg = 'Select Call Outcome.';
        setError(msg);
        setLogSubmitError(msg);
        setSaving(false);
        setLogSubmitState('idle');
        return;
      }

      if (activityType === 'visit' && !visitWith) {
        const msg = 'Select Visit With.';
        setError(msg);
        setLogSubmitError(msg);
        setSaving(false);
        setLogSubmitState('idle');
        return;
      }

      if (activityType === 'visit' && visitArrival === 'broker' && !visitArrivalBroker?.id) {
        const msg = 'Select the broker for this visit.';
        setError(msg);
        setLogSubmitError(msg);
        setSaving(false);
        setLogSubmitState('idle');
        return;
      }

      if (activityType === 'visit' && visitArrival === 'c2c' && !visitArrivalC2cName.trim()) {
        const msg = 'Enter the C2C reference name.';
        setError(msg);
        setLogSubmitError(msg);
        setSaving(false);
        setLogSubmitState('idle');
        return;
      }

      if (requireFirstVisitRefByChoice) {
        if (!visitFirstRefByMode) {
          const msg = 'Select Direct, Broker, Reference, or C2C.';
          setError(msg);
          setLogSubmitError(msg);
          setSaving(false);
          setLogSubmitState('idle');
          return;
        }
        if (visitFirstRefByMode === 'broker' && !visitReferredBroker?.id) {
          const msg = 'Select Ref By.';
          setError(msg);
          setLogSubmitError(msg);
          setSaving(false);
          setLogSubmitState('idle');
          return;
        }
        if (
          (visitFirstRefByMode === 'reference' ||
            visitFirstRefByMode === 'c2c') &&
          !visitFirstRefByName.trim()
        ) {
          const msg = 'Enter Ref By name.';
          setError(msg);
          setLogSubmitError(msg);
          setSaving(false);
          setLogSubmitState('idle');
          return;
        }
      }


      if (newStatus) {
        if (!allowedStatus.includes(newStatus)) {
          const msg = 'Status change not allowed.';
          setError(msg);
          setLogSubmitError(msg);
          setSaving(false);
          setLogSubmitState('idle');
          return;
        }
        statusFrom = lead.status;
        statusTo = newStatus;
        updates.status = newStatus;
      }

      if (isClosingNow) {
        if (!closePrimaryReason) {
          const msg = 'Select Primary reason.';
          setError(msg);
          setLogSubmitError(msg);
          setSaving(false);
          setLogSubmitState('idle');
          return;
        }
        if (closePrimaryReason === 'Other' && !normalizedPrimaryOtherReason) {
          const msg = 'Enter Other primary reason.';
          setError(msg);
          setLogSubmitError(msg);
          setSaving(false);
          setLogSubmitState('idle');
          return;
        }
        if (closePrimaryReason === 'Other' && !matchedPrimaryOther) {
          const msg = 'Select existing Other reason or tap + to add.';
          setError(msg);
          setLogSubmitError(msg);
          setSaving(false);
          setLogSubmitState('idle');
          return;
        }
        if (!isCompletedClose && !resolvedCloseDecisionStage) {
          const msg = 'Decision stage is missing.';
          setError(msg);
          setLogSubmitError(msg);
          setSaving(false);
          setLogSubmitState('idle');
          return;
        }
        if (!isCompletedClose && isAnotherLocationClose && !resolvedInterestedLocation) {
          const msg = 'Enter interested location.';
          setError(msg);
          setLogSubmitError(msg);
          setSaving(false);
          setLogSubmitState('idle');
          return;
        }
        if (!isCompletedClose && isAnotherLocationClose && !matchedInterestedLocation) {
          const msg = 'Select existing location or tap + to add.';
          setError(msg);
          setLogSubmitError(msg);
          setSaving(false);
          setLogSubmitState('idle');
          return;
        }
        if (!isCompletedClose && isBoughtAnotherLocationClose && !resolvedBoughtAtLocation) {
          const msg = 'Enter purchased location.';
          setError(msg);
          setLogSubmitError(msg);
          setSaving(false);
          setLogSubmitState('idle');
          return;
        }
        if (!isCompletedClose && isBoughtAnotherLocationClose && !matchedBoughtAtLocation) {
          const msg = 'Select existing location or tap + to add.';
          setError(msg);
          setLogSubmitError(msg);
          setSaving(false);
          setLogSubmitState('idle');
          return;
        }
        if (!isCompletedClose && isNeedOtherTypeClose && !closeRequiredType) {
          const msg = 'Select required type.';
          setError(msg);
          setLogSubmitError(msg);
          setSaving(false);
          setLogSubmitState('idle');
          return;
        }
        if (!isCompletedClose && isNeedOtherTypeClose && !resolvedRequiredTypeLocation) {
          const msg = 'Enter required location.';
          setError(msg);
          setLogSubmitError(msg);
          setSaving(false);
          setLogSubmitState('idle');
          return;
        }
        if (!isCompletedClose && isNeedOtherTypeClose && !matchedRequiredTypeLocation) {
          const msg = 'Select existing location or tap + to add.';
          setError(msg);
          setLogSubmitError(msg);
          setSaving(false);
          setLogSubmitState('idle');
          return;
        }
        if (!isCompletedClose && isBudgetFinancialClose && !closeBudgetDetail) {
          const msg = 'Select budget issue detail.';
          setError(msg);
          setLogSubmitError(msg);
          setSaving(false);
          setLogSubmitState('idle');
          return;
        }
        if (!isCompletedClose && isDistanceIssueClose && !resolvedDistancePreferredArea) {
          const msg = 'Enter preferred area.';
          setError(msg);
          setLogSubmitError(msg);
          setSaving(false);
          setLogSubmitState('idle');
          return;
        }
        if (!isCompletedClose && isDistanceIssueClose && !matchedDistanceArea) {
          const msg = 'Select existing area or tap + to add.';
          setError(msg);
          setLogSubmitError(msg);
          setSaving(false);
          setLogSubmitState('idle');
          return;
        }
        if (!isCompletedClose && isCloseCompetitor) {
          if (!normalizedCompetitorProject) {
            const msg = 'Enter competitor project name.';
            setError(msg);
            setLogSubmitError(msg);
            setSaving(false);
            setLogSubmitState('idle');
            return;
          }
          if (!closeCompetitorPriceBand) {
            const msg = 'Select competitor price band.';
            setError(msg);
            setLogSubmitError(msg);
            setSaving(false);
            setLogSubmitState('idle');
            return;
          }
          if (!closeCompetitorAdvantage) {
            const msg = 'Select main competitor advantage.';
            setError(msg);
            setLogSubmitError(msg);
            setSaving(false);
            setLogSubmitState('idle');
            return;
          }
        }
      }

      // Maintain explicit close fields for dashboard reporting.
      if (isClosingNow) {
        if (!(lead as any)?.closedAt) updates.closedAt = firestoreServerTimestampToken();
        updates.closeReason = resolvedClosePrimaryReason || null;
        updates.closePrimaryReason = closePrimaryReason || null;
        updates.closePrimaryOtherReason =
          closePrimaryReason === 'Other' ? (normalizedPrimaryOtherReason || null) : null;
        updates.closeSecondaryReasons = isCompletedClose
          ? []
          : (closeSecondaryReasons.length ? closeSecondaryReasons : []);
        updates.closeDecisionStageAuto = closeDecisionStageAuto || null;
        updates.closeDecisionStage = isCompletedClose
          ? null
          : (resolvedCloseDecisionStage || null);
        updates.closeInterestedLocation = !isCompletedClose && isAnotherLocationClose
          ? (resolvedInterestedLocation || null)
          : null;
        updates.closeBoughtAtLocation = !isCompletedClose && isBoughtAnotherLocationClose
          ? (resolvedBoughtAtLocation || null)
          : null;
        updates.closeRequiredType = !isCompletedClose && isNeedOtherTypeClose
          ? (closeRequiredType || null)
          : null;
        updates.closeRequiredTypeLocation = !isCompletedClose && isNeedOtherTypeClose
          ? (resolvedRequiredTypeLocation || null)
          : null;
        updates.closeBudgetDetail = !isCompletedClose && isBudgetFinancialClose
          ? (closeBudgetDetail || null)
          : null;
        updates.closeDistancePreferredArea = !isCompletedClose && isDistanceIssueClose
          ? (resolvedDistancePreferredArea || null)
          : null;
        updates.closeCompetitorProject = !isCompletedClose && isCloseCompetitor
          ? (normalizedCompetitorProject || null)
          : null;
        updates.closeCompetitorBuilder = null;
        updates.closeCompetitorPriceBand = !isCompletedClose && isCloseCompetitor
          ? (closeCompetitorPriceBand || null)
          : null;
        updates.closeCompetitorAdvantage = !isCompletedClose && isCloseCompetitor
          ? (closeCompetitorAdvantage || null)
          : null;
        updates.closedAnalysis = {
          primaryReason: resolvedClosePrimaryReason || null,
          primaryReasonBase: closePrimaryReason || null,
          primaryOtherReason:
            closePrimaryReason === 'Other' ? (normalizedPrimaryOtherReason || null) : null,
          secondaryReasons: isCompletedClose
            ? []
            : (closeSecondaryReasons.length ? closeSecondaryReasons : []),
          decisionStageAuto: closeDecisionStageAuto || null,
          decisionStage: isCompletedClose ? null : (resolvedCloseDecisionStage || null),
          interestedLocation: !isCompletedClose && isAnotherLocationClose
            ? (resolvedInterestedLocation || null)
            : null,
          boughtAtLocation: !isCompletedClose && isBoughtAnotherLocationClose
            ? (resolvedBoughtAtLocation || null)
            : null,
          requiredType: !isCompletedClose && isNeedOtherTypeClose
            ? (closeRequiredType || null)
            : null,
          requiredTypeLocation: !isCompletedClose && isNeedOtherTypeClose
            ? (resolvedRequiredTypeLocation || null)
            : null,
          budgetDetail: !isCompletedClose && isBudgetFinancialClose
            ? (closeBudgetDetail || null)
            : null,
          distancePreferredArea: !isCompletedClose && isDistanceIssueClose
            ? (resolvedDistancePreferredArea || null)
            : null,
          competitorProject: !isCompletedClose && isCloseCompetitor
            ? (normalizedCompetitorProject || null)
            : null,
          competitorBuilder: null,
          competitorPriceBand: !isCompletedClose && isCloseCompetitor
            ? (closeCompetitorPriceBand || null)
            : null,
          competitorAdvantage: !isCompletedClose && isCloseCompetitor
            ? (closeCompetitorAdvantage || null)
            : null,
          capturedBy: currentUser?.email || null,
          capturedAt: firestoreServerTimestampToken(),
        };
        updates.reopenWhatsappRequested = false;
      } else if (isReopeningNow) {
        updates.closedAt = null;
        updates.closeReason = null;
        updates.closePrimaryReason = null;
        updates.closePrimaryOtherReason = null;
        updates.closeSecondaryReasons = [];
        updates.closeDecisionStageAuto = null;
        updates.closeDecisionStage = null;
        updates.closeInterestedLocation = null;
        updates.closeBudgetDetail = null;
        updates.closeBoughtAtLocation = null;
        updates.closeRequiredType = null;
        updates.closeRequiredTypeLocation = null;
        updates.closeDistancePreferredArea = null;
        updates.closeCompetitorProject = null;
        updates.closeCompetitorBuilder = null;
        updates.closeCompetitorPriceBand = null;
        updates.closeCompetitorAdvantage = null;
        updates.closedAnalysis = null;
        updates.reopenedAt = firestoreServerTimestampToken();
        updates.reopenedBy = currentUser?.email || null;
        updates.reopenReason = normalizedReopenReason || null;
        updates.reopenNote = normalizedReopenNote || null;
        updates.reopenWhatsappRequested = reopenOptions?.sendWhatsapp === true;
      }

      if (followUpDate && !willBeClosed) {
        const picked = parseLocalDateInputValue(followUpDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (!picked || Number.isNaN(picked.getTime())) {
          const msg = 'Invalid follow-up date.';
          setError(msg);
          setLogSubmitError(msg);
          setSaving(false);
          setLogSubmitState('idle');
          return;
        }
        if (picked.getTime() < today.getTime()) {
          const msg = 'Next follow-up date must be today or a future date.';
          setError(msg);
          setLogSubmitError(msg);
          setSaving(false);
          setLogSubmitState('idle');
          return;
        }
        followAt = Timestamp.fromDate(picked);
        if (followUpTimeEnabled) {
          const parsedTime = parseLocalTimeInputValue(followUpTime);
          if (!parsedTime) {
            const msg = 'Invalid reminder time.';
            setError(msg);
            setLogSubmitError(msg);
            setSaving(false);
            setLogSubmitState('idle');
            return;
          }
          const dt = new Date(picked);
          dt.setHours(parsedTime.hours, parsedTime.minutes, 0, 0);
          followAtExact = Timestamp.fromDate(dt);
        } else {
          followAtExact = null;
        }
        updates.nextFollowUpDate = followAt;
        updates.nextFollowUpAt = followAtExact;
      }

      if (
        !willBeClosed &&
        activityType === 'call' &&
        (callOutcome === 'cnr' || callOutcome === 'call-back') &&
        !followUpDate
      ) {
        const msg = 'Set a follow-up date for this call outcome.';
        setError(msg);
        setLogSubmitError(msg);
        setSaving(false);
        setLogSubmitState('idle');
        return;
      }

      // Closed leads should not carry a follow-up date.
      if (willBeClosed) {
        followAt = null;
        followAtExact = null;
        updates.nextFollowUpDate = null;
        updates.nextFollowUpAt = null;
      }

      // Prompts: answer once per prompt id across lead lifetime.
      if (selectedPrompts.length > 0) {
        for (const p of selectedPrompts) {
          const existing = lead.promptAnswers?.[p.id];
          if (existing && existing.value !== undefined && existing.value !== null && displayAnswer(existing)) {
            continue; // already answered
          }
          const raw = (promptDraft[p.id] || '').trim();
          if (!raw) continue;
          if (p.type === 'number' && Number.isNaN(Number(raw))) {
            const msg = `Invalid number for: ${p.label}`;
            setError(msg);
            setLogSubmitError(msg);
            setSaving(false);
            setLogSubmitState('idle');
            return;
          }
          if (p.type === 'date' && Number.isNaN(new Date(raw).getTime())) {
            const msg = `Invalid date for: ${p.label}`;
            setError(msg);
            setLogSubmitError(msg);
            setSaving(false);
            setLogSubmitState('idle');
            return;
          }

          const value =
            p.type === 'number' ? Number(raw) :
            p.type === 'date' ? Timestamp.fromDate(new Date(raw)) :
            raw;
          updates[`promptAnswers.${p.id}`] = {
            value,
            type: p.type,
            label: p.label,
            statusId: selectedStatusId,
            answeredAt: firestoreServerTimestampToken(),
            answeredBy: currentUser?.email || null,
          };
          activityPromptValues[p.id] = value;

        }
      }

      // Visit prompts: asked every Visit; prefill from existing value; allow updating.
      if (activityType === 'visit') {
        const visitDefs: Record<
          string,
          { id: string; label: string; type: 'text' | 'select'; options?: string[] }
        > = {
          legacy_budget: { id: 'legacy_budget', label: 'Budget Fit', type: 'select', options: ['In Budget', 'Under Budget'] },
          legacy_floorChoice: { id: 'legacy_floorChoice', label: 'Floor Choice', type: 'select', options: ['Lower', 'Middle', 'Higher', 'Any'] },
          legacy_unitChoice: { id: 'legacy_unitChoice', label: 'Unit Choice', type: 'text' },
          legacy_currentResidence: { id: 'legacy_currentResidence', label: 'Current Residence', type: 'text' },
          legacy_occupation: { id: 'legacy_occupation', label: 'Occupation', type: 'text' },
        };

        const maybeWriteVisitPrompt = (promptId: string, rawValue: any) => {
          const v = rawValue;
          if (v === null || v === undefined) return;
          if (typeof v === 'string' && !v.trim()) return;
          if (Array.isArray(v) && v.length === 0) return;
          const existing = lead.promptAnswers?.[promptId];
          const existingStr = existing ? displayAnswer(existing) : '';
          const nextStr =
            Array.isArray(v) ? v.filter((x) => typeof x === 'string' && x.trim()).join(', ') : String(v).trim();
          if (existingStr === nextStr) return;

          const def = (findPromptDef(promptId) as any) || visitDefs[promptId] || { id: promptId, label: promptId, type: 'text' };
          const base: any = {
            ...(existing || {}),
            value: v,
            type: existing?.type || def.type || 'text',
            label: existing?.label || def.label || promptId,
            statusId: selectedStatusId,
            answeredAt: existing?.answeredAt || firestoreServerTimestampToken(),
            answeredBy: existing?.answeredBy || (currentUser?.email || null),
            editedAt: existing ? firestoreServerTimestampToken() : undefined,
            editedBy: existing ? (currentUser?.email || null) : undefined,
          };
          // Firestore updateDoc rejects undefined nested fields.
          if (base.editedAt === undefined) delete base.editedAt;
          if (base.editedBy === undefined) delete base.editedBy;

          updates[`promptAnswers.${promptId}`] = base;
          activityPromptValues[promptId] = v;
        };

        maybeWriteVisitPrompt('legacy_budget', visitBudgetFit);
        maybeWriteVisitPrompt('legacy_floorChoice', visitFloorChoice);
        maybeWriteVisitPrompt('legacy_unitChoice', visitUnitChoices);

        // Visit-only fields: capture once; later shown in lead details section.
        if (!hasLegacyResidence) {
          maybeWriteVisitPrompt('legacy_currentResidence', (visitResidenceInput || '').trim());
        }
        if (!hasLegacyOccupation) {
          maybeWriteVisitPrompt('legacy_occupation', (visitOccupationInput || '').trim());
        }

        if (requireFirstVisitRefByChoice) {
          const currentId = lead.referredBrokerId || null;
          const currentName = lead.referredBrokerName || null;
          const currentAgency = lead.referredAgencyName || null;
          const currentRefName = String((lead as any).refByName || '').trim() || null;

          if (visitFirstRefByMode === 'broker' && visitReferredBroker?.id) {
            refByFromId = currentId;
            refByFromName = currentName;
            refByFromAgency = currentAgency;
            refByToId = visitReferredBroker.id;
            refByToName = visitReferredBroker.brokerName || null;
            refByToAgency = visitReferredBroker.agencyName || null;
            updates.referredBrokerId = refByToId;
            updates.referredBrokerName = refByToName;
            updates.referredAgencyName = refByToAgency;
            updates.referredBrokerAt = firestoreServerTimestampToken();
          } else if (visitFirstRefByMode === 'reference') {
            const nextRefName = visitFirstRefByName.trim();
            refByFromId = currentId;
            refByFromName = currentName || currentRefName;
            refByFromAgency = currentAgency;
            refByToName = nextRefName;
            updates.refByName = nextRefName;
            updates.refByType = 'reference';
            updates.refByAt = firestoreServerTimestampToken();
          } else if (visitFirstRefByMode === 'c2c') {
            const nextRefName = visitFirstRefByName.trim();
            refByFromId = currentId;
            refByFromName = currentName || currentRefName;
            refByFromAgency = currentAgency;
            refByToName = nextRefName;
            updates.refByName = nextRefName;
            updates.refByType = 'c2c';
            updates.refByAt = firestoreServerTimestampToken();
          }
        }

        // Social Ad leads: allow setting Ref By (broker) during a Visit.
        if (isSocialAdLead && visitReferredBroker?.id) {
          const currentId = lead.referredBrokerId || null;
          if (currentId !== visitReferredBroker.id) {
            refByFromId = currentId;
            refByFromName = lead.referredBrokerName || null;
            refByFromAgency = lead.referredAgencyName || null;
            refByToId = visitReferredBroker.id;
            refByToName = visitReferredBroker.brokerName || null;
            refByToAgency = visitReferredBroker.agencyName || null;
            updates.referredBrokerId = refByToId;
            updates.referredBrokerName = refByToName;
            updates.referredAgencyName = refByToAgency;
            updates.referredBrokerAt = firestoreServerTimestampToken();
          }
        }
      }

	      const activityProjectId =
	        activityType === 'call' || activityType === 'visit'
	          ? String((updates.projectId ?? (lead as any)?.projectId ?? logProjectIdDraft ?? currentProjectId ?? '') || '')
	              .trim()
	          : '';
	      const activityUnitType =
	        activityType === 'call' || activityType === 'visit'
	          ? String((updates.unitType ?? (lead as any)?.unitType ?? logUnitTypeDraft ?? '') || '').trim()
	          : '';

      const activityNotes =
        isReopeningNow && normalizedReopenNote
          ? [...noteList, normalizedReopenNote]
          : noteList;

      const activityPayload = {
	        type: activityType,
	        projectId: activityProjectId || null,
	        projectName: activityProjectId ? (projectForId.get(activityProjectId)?.name || null) : null,
	        unitType: activityUnitType || null,
	        callOutcome: activityType === 'call' ? callOutcome : null,
	        visitWith: activityType === 'visit' ? (visitWith || null) : null,
	        visitWithOther:
	          activityType === 'visit' && visitWith === 'other' ? (visitWithOther.trim() || null) : null,
        // This describes this visit only and never changes the lead's original source or Ref By.
        visitArrival: activityType === 'visit' ? (visitArrival || null) : null,
        visitArrivalBrokerId:
          activityType === 'visit' && visitArrival === 'broker' ? (visitArrivalBroker?.id || null) : null,
        visitArrivalBrokerName:
          activityType === 'visit' && visitArrival === 'broker'
            ? (visitArrivalBroker?.brokerName || null)
            : null,
        visitArrivalBrokerAgencyName:
          activityType === 'visit' && visitArrival === 'broker'
            ? (visitArrivalBroker?.agencyName || null)
            : null,
        visitArrivalC2cName:
          activityType === 'visit' && visitArrival === 'c2c' ? (visitArrivalC2cName.trim() || null) : null,
        refByFromId: refByFromId || null,
        refByFromName: refByFromName || null,
        refByFromAgency: refByFromAgency || null,
        refByToId: refByToId || null,
        refByToName: refByToName || null,
        refByToAgency: refByToAgency || null,
        notes: activityNotes.length ? activityNotes : null,
        promptAnswers: Object.keys(activityPromptValues).length ? activityPromptValues : null,
        statusFrom: statusFrom || null,
        statusTo: statusTo || null,
        reopenReason: isReopeningNow ? (normalizedReopenReason || null) : null,
        reopenNote: isReopeningNow ? (normalizedReopenNote || null) : null,
        reopenWhatsappRequested: isReopeningNow ? reopenOptions?.sendWhatsapp === true : null,
        followUpFrom: followFrom,
        followUpFromAt: followFromAt,
	        followUpDate: followAt || null,
        followUpAt: followAtExact || null,
        closeAnalysis:
          isClosingNow
            ? {
                primaryReason: resolvedClosePrimaryReason || null,
                primaryReasonBase: closePrimaryReason || null,
                primaryOtherReason:
                  closePrimaryReason === 'Other' ? (normalizedPrimaryOtherReason || null) : null,
                secondaryReasons: isCompletedClose
                  ? []
                  : (closeSecondaryReasons.length ? closeSecondaryReasons : []),
                decisionStageAuto: closeDecisionStageAuto || null,
                decisionStage: isCompletedClose
                  ? null
                  : (resolvedCloseDecisionStage || null),
                interestedLocation: !isCompletedClose && isAnotherLocationClose
                  ? (resolvedInterestedLocation || null)
                  : null,
                boughtAtLocation: !isCompletedClose && isBoughtAnotherLocationClose
                  ? (resolvedBoughtAtLocation || null)
                  : null,
                requiredType: !isCompletedClose && isNeedOtherTypeClose
                  ? (closeRequiredType || null)
                  : null,
                requiredTypeLocation: !isCompletedClose && isNeedOtherTypeClose
                  ? (resolvedRequiredTypeLocation || null)
                  : null,
                budgetDetail: !isCompletedClose && isBudgetFinancialClose
                  ? (closeBudgetDetail || null)
                  : null,
                distancePreferredArea: !isCompletedClose && isDistanceIssueClose
                  ? (resolvedDistancePreferredArea || null)
                  : null,
                competitorProject: !isCompletedClose && isCloseCompetitor
                  ? (normalizedCompetitorProject || null)
                  : null,
                competitorBuilder: null,
                competitorPriceBand: !isCompletedClose && isCloseCompetitor
                  ? (closeCompetitorPriceBand || null)
                  : null,
                competitorAdvantage: !isCompletedClose && isCloseCompetitor
                  ? (closeCompetitorAdvantage || null)
                  : null,
              }
            : null,
	        byEmail: currentUser?.email || null,
	        at: firestoreServerTimestampToken(),
	      };

      const leadPayload: Record<string, any> = {
        activityCount: firestoreIncrementToken(1),
        lastActivityAt: firestoreServerTimestampToken(),
      };
      if (activityType === 'visit') {
        leadPayload.visitCount = firestoreIncrementToken(1);
      }
      if (Object.keys(updates).length > 0) {
        leadPayload.updatedAt = firestoreServerTimestampToken();
        leadPayload.updatedBy = currentUser?.email || null;
        Object.assign(leadPayload, updates);
      }

      const safeLeadPayload = serializeCallableValue(leadPayload);
      const safeActivityPayload = serializeCallableValue(activityPayload);
      const logLeadActivity = httpsCallable(functions, 'logLeadActivity');
      await logLeadActivity({
        leadId: String(lead.id || '').trim(),
        leadPayload: safeLeadPayload,
        activityPayload: safeActivityPayload,
        receptionAssignToMe,
      });

      const localLeadPatch = Object.fromEntries(
        Object.entries(updates).filter(([key, value]) => {
          if (key.includes('.')) return false;
          if (value && typeof value === 'object' && (value as any).__op) return false;
          return true;
        })
      );
      const nextLead = {
        ...lead,
        ...localLeadPatch,
        activityCount: Number((lead as any).activityCount || 0) + 1,
        visitCount:
          activityType === 'visit'
            ? Number((lead as any).visitCount || 0) + 1
            : (lead as any).visitCount,
      } as Lead;
      onActivityLogged?.(nextLead);
      onUpdated?.(nextLead);

      setNoteInput('');
      setNoteList([]);
      setPromptDraft({});
      setClosePrimaryReason('');
      setClosePrimaryOtherReason('');
      setCloseSecondaryReasons([]);
      setCloseDecisionStage('');
      setCloseDecisionStageEditing(false);
      setCloseInterestedLocation('');
      setCloseBudgetDetail('');
      setCloseDistancePreferredArea('');
      setCloseCompetitorProject('');
      setCloseCompetitorPriceBand('');
      setCloseCompetitorAdvantage('');
      setReopenDialogOpen(false);
      setReopenReason('');
      setReopenNote('');
      // Ask "Visit With" fresh each time.
      setVisitWith('');
      setVisitWithOther('');
      setSaving(false);
      setLogSubmitError(null);
      setLogSubmitState('success');
      setPostSubmitToastOpen(true);
      onClose();
      window.setTimeout(() => {
        setLogSubmitState('idle');
      }, 1200);
    } catch (err: any) {
      console.error('log activity failed', err);
      const msg = err?.message || 'Failed to log activity';
      setError(msg);
      setLogSubmitError(msg);
      setSaving(false);
      setLogSubmitState('idle');
      setLogExpanded(true);
    }
  };

  const handleRatingSave = async (next: number) => {
    if (!lead) return;
    if (!canEditRating) return;
    try {
      setError(null);
      setSaving(true);
      const from = typeof lead.rating === 'number' ? lead.rating : null;
      const to = next === 0 ? null : next;

      const leadRef = doc(db, 'leads', lead.id);
      const activitiesRef = collection(leadRef, 'activities');

      await updateDoc(leadRef, {
        rating: to,
        ratingUpdatedAt: serverTimestamp(),
        ratingUpdatedBy: currentUser?.email || null,
      });

      await addDoc(activitiesRef, {
        type: 'internal',
        event: 'rating',
        ratingFrom: from,
        ratingTo: to,
        byEmail: currentUser?.email || null,
        at: serverTimestamp(),
      });
      await bumpLeadActivity(leadRef, 1);

      setSaving(false);
    } catch (err: any) {
      console.error('rating update failed', err);
      setError(err?.message || 'Failed to update rating');
      setSaving(false);
    }
  };

  const toTimestampFromLocalDatetime = (val: string) => {
    // val: "YYYY-MM-DDTHH:mm" local time
    if (!val) return null;
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return null;
    return Timestamp.fromDate(d);
  };

  const handleScheduleAppointment = async () => {
    if (!lead) return;
    if (!canManageAppointments) return;
    setError(null);
    try {
      const ts = toTimestampFromLocalDatetime(apptScheduledAt);
      if (!ts) {
        setError('Appointment date & time is required.');
        return;
      }
      if (!apptProjectId) {
        setError('Project is required.');
        return;
      }
      const leadRef = doc(db, 'leads', lead.id);
      const activitiesRef = collection(leadRef, 'activities');
      const proj = projectForId.get(apptProjectId);

      const pendingPoint = apptMeetingPointInput.trim();
      const pointsToSave = pendingPoint && !apptMeetingPoints.includes(pendingPoint)
        ? [...apptMeetingPoints, pendingPoint]
        : apptMeetingPoints;

      const payload = {
        type: apptType,
        scheduledAt: ts,
        projectId: apptProjectId,
        projectName: (proj?.name as string) || null,
        unitType: apptUnitType ? apptUnitType : null,
        visitWith: apptVisitWith ? apptVisitWith : null,
        visitWithOther: apptVisitWith === 'other' ? (apptVisitWithOther.trim() || null) : null,
        meetingPoints: pointsToSave.length ? pointsToSave : null,
        status: 'scheduled',
        createdAt: serverTimestamp(),
        createdBy: currentUser?.email || null,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.email || null,
      };

      const appointmentStatusId = resolveStatusId('appointment');
      const statusFrom = lead.status || null;
      const statusTo = appointmentStatusId && appointmentStatusId !== lead.status ? appointmentStatusId : null;
      const requestValue =
        lead.websiteRequest?.type ||
        lead.websiteRequest?.label ||
        lead.enquiryType;
      const completesVisitRequest =
        Boolean(requestValue) &&
        websiteRequestConfig(requestValue).kind === 'schedule-site-visit' &&
        String(lead.websiteRequest?.status || 'pending').toLowerCase() !== 'completed';

      await updateDoc(leadRef, {
        appointment: payload,
        // Follow-up becomes due on the appointment schedule by default.
        nextFollowUpDate: ts,
        nextFollowUpAt: ts,
        ...(statusTo ? { status: statusTo } : {}),
        ...(completesVisitRequest
          ? {
              websiteRequest: {
                ...(lead.websiteRequest || {}),
                type: 'schedule-site-visit',
                label: lead.websiteRequest?.label || lead.enquiryType || 'Schedule a Site Visit',
                status: 'completed',
                receivedAt: lead.websiteRequest?.receivedAt || lead.createdAt || null,
                completedAt: serverTimestamp(),
                completedBy: currentUser?.email || null,
              },
            }
          : {}),
      });

      await addDoc(activitiesRef, {
        type: 'internal',
        event: 'appointment-scheduled',
        appointmentType: apptType,
        appointmentAt: ts,
        followUpDate: ts,
        projectId: apptProjectId,
        projectName: (proj?.name as string) || null,
        unitType: apptUnitType ? apptUnitType : null,
        meetingPoints: pointsToSave.length ? pointsToSave : null,
        statusFrom,
        statusTo,
        byEmail: currentUser?.email || null,
        at: serverTimestamp(),
      });
      if (completesVisitRequest) {
        await addDoc(activitiesRef, {
          type: 'internal',
          event: 'website-request-completed',
          requestType: 'schedule-site-visit',
          requestLabel: lead.websiteRequest?.label || lead.enquiryType || 'Schedule a Site Visit',
          byEmail: currentUser?.email || null,
          at: serverTimestamp(),
        });
      }
      await bumpLeadActivity(leadRef, completesVisitRequest ? 2 : 1);

      setApptMeetingPointInput('');
      setApptMeetingPoints([]);
      setApptFormOpen(false);
      setActivityType('');
    } catch (err: any) {
      console.error('schedule appointment failed', err);
      setError(err?.message || 'Failed to schedule appointment');
    }
  };

  const handleCompleteAppointment = async () => {
    if (!lead) return;
    if (!canManageAppointments) return;
    setError(null);
    try {
      const appt = lead.appointment || null;
      if (!appt || appt.status !== 'scheduled') {
        setError('No scheduled appointment to complete.');
        return;
      }
      const leadRef = doc(db, 'leads', lead.id);
      const activitiesRef = collection(leadRef, 'activities');

      if (apptOutcome === 'rescheduled') {
        const ts = toTimestampFromLocalDatetime(apptRescheduleAt);
        if (!ts) {
          setError('New appointment date & time is required.');
          return;
        }
        // follow-up is optional when rescheduling, but allow setting it
        const followTs = apptFollowUpDate ? Timestamp.fromDate(new Date(apptFollowUpDate)) : null;
        if (apptFollowUpDate && Number.isNaN(new Date(apptFollowUpDate).getTime())) {
          setError('Invalid follow-up date.');
          return;
        }
        if (apptFollowUpDate) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const picked = new Date(apptFollowUpDate);
          picked.setHours(0, 0, 0, 0);
          if (picked.getTime() < today.getTime()) {
            setError('Next follow-up date must be today or a future date.');
            return;
          }
        }

        await updateDoc(leadRef, {
          appointment: {
            ...(appt as any),
            scheduledAt: ts,
            status: 'scheduled',
            updatedAt: serverTimestamp(),
            updatedBy: currentUser?.email || null,
            // keep meeting points as-is; completion notes go to timeline only
          },
          ...(followTs ? { nextFollowUpDate: followTs, nextFollowUpAt: null } : {}),
        });

        const apptMeetingPointsList = Array.isArray((appt as any).meetingPoints)
          ? ((appt as any).meetingPoints as any[])
          : typeof (appt as any)?.meetingPoint === 'string' && (appt as any).meetingPoint.trim()
            ? [(appt as any).meetingPoint.trim()]
            : null;

        await addDoc(activitiesRef, {
          type: 'internal',
          event: 'appointment-rescheduled',
          appointmentType: appt.type || null,
          appointmentAt: ts,
          appointmentOutcome: 'rescheduled',
          meetingPoints: apptMeetingPointsList,
          notes: apptCompleteNotes.length ? apptCompleteNotes : null,
          byEmail: currentUser?.email || null,
          at: serverTimestamp(),
        });
        await bumpLeadActivity(leadRef, 1);

        setApptCompleteOpen(false);
        setActivityType('');
        return;
      }

      // Completed / No-show / Cancelled
      const willBeClosed = isClosedStatus(apptStatusChange || lead.status);
      const nextFollowTs =
        apptFollowUpDate && apptOutcome !== 'cancelled' && !willBeClosed
          ? Timestamp.fromDate(new Date(apptFollowUpDate))
          : null;
      if (!willBeClosed && apptOutcome !== 'cancelled' && !apptFollowUpDate) {
        setError('Set a next follow-up date.');
        return;
      }
      if (apptFollowUpDate && !willBeClosed && apptOutcome !== 'cancelled') {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const picked = new Date(apptFollowUpDate);
        picked.setHours(0, 0, 0, 0);
        if (Number.isNaN(picked.getTime())) {
          setError('Invalid follow-up date.');
          return;
        }
        if (picked.getTime() < today.getTime()) {
          setError('Next follow-up date must be today or a future date.');
          return;
        }
      }

      const updates: Record<string, any> = {
        appointment: {
          ...(appt as any),
          status:
            apptOutcome === 'completed'
              ? 'completed'
              : apptOutcome === 'no-show'
                ? 'no-show'
                : 'cancelled',
          outcome: apptOutcome,
          completedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          updatedBy: currentUser?.email || null,
          // keep meeting points as-is; completion notes go to timeline only
        },
      };
      if (willBeClosed) updates.nextFollowUpDate = null;
      if (willBeClosed) updates.nextFollowUpAt = null;
      else if (nextFollowTs) {
        updates.nextFollowUpDate = nextFollowTs;
        updates.nextFollowUpAt = null;
      }
      if ((appt.type || apptType) === 'site-visit' && apptOutcome === 'completed') {
        updates.visitCount = increment(1);
      }

      // Optional status change (must be allowed)
      if (apptStatusChange) {
        if (!allowedStatus.includes(apptStatusChange)) {
          setError('Status change not allowed.');
          return;
        }
        updates.status = apptStatusChange;
      }

      // Optional rating update (admin/manager only)
      const ratingFrom = typeof lead.rating === 'number' && Number.isFinite(lead.rating) ? lead.rating : null;
      const ratingTo = apptRating > 0 ? apptRating : null;
      const shouldUpdateRating = canEditRating && ratingTo !== ratingFrom;
      if (shouldUpdateRating) {
        updates.rating = ratingTo;
        updates.ratingUpdatedAt = serverTimestamp();
        updates.ratingUpdatedBy = currentUser?.email || null;
      }

      await updateDoc(leadRef, updates);

      // Timeline entry: count as a visit only for site visits on completion.
      if ((appt.type || apptType) === 'site-visit' && apptOutcome === 'completed') {
        await addDoc(activitiesRef, {
          type: 'visit',
          visitWith: (appt.visitWith as any) || null,
          visitWithOther: (appt.visitWith as any) === 'other' ? (appt.visitWithOther || null) : null,
          notes: apptCompleteNotes.length ? apptCompleteNotes : null,
          meetingPoints: Array.isArray((appt as any).meetingPoints) ? ((appt as any).meetingPoints as any[]) : null,
          appointmentType: 'site-visit',
          appointmentAt: appt.scheduledAt || null,
          appointmentOutcome: 'completed',
          statusFrom: apptStatusChange ? lead.status : null,
          statusTo: apptStatusChange ? apptStatusChange : null,
          followUpDate: nextFollowTs || null,
          byEmail: currentUser?.email || null,
          at: serverTimestamp(),
        });
      } else {
        await addDoc(activitiesRef, {
          type: 'internal',
          event: 'appointment-completed',
          appointmentType: (appt.type as any) || null,
          appointmentAt: appt.scheduledAt || null,
          appointmentOutcome: apptOutcome,
          meetingPoints: Array.isArray((appt as any).meetingPoints) ? ((appt as any).meetingPoints as any[]) : null,
          notes: apptCompleteNotes.length ? apptCompleteNotes : null,
          statusFrom: apptStatusChange ? lead.status : null,
          statusTo: apptStatusChange ? apptStatusChange : null,
          followUpDate: nextFollowTs || null,
          byEmail: currentUser?.email || null,
          at: serverTimestamp(),
        });
      }

      if (shouldUpdateRating) {
        await addDoc(activitiesRef, {
          type: 'internal',
          event: 'rating',
          ratingFrom,
          ratingTo,
          byEmail: currentUser?.email || null,
          at: serverTimestamp(),
        });
      }

      await bumpLeadActivity(leadRef, shouldUpdateRating ? 2 : 1);
      setApptCompleteOpen(false);
      setActivityType('');
    } catch (err: any) {
      console.error('complete appointment failed', err);
      setError(err?.message || 'Failed to complete appointment');
    }
  };

  const handleAssign = async (toEmail: string) => {
    if (!lead) return;
    if (!canEditAssignment) return;
    try {
      setError(null);
      setSaving(true);
      const from = lead.assignedTo ?? null;
      const to = toEmail;
      if (from === to) {
        setSaving(false);
        setAssignAnchorEl(null);
        return;
      }

      const leadRef = doc(db, 'leads', lead.id);
      const activitiesRef = collection(leadRef, 'activities');

      await updateDoc(leadRef, {
        assignedTo: to,
        assignedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.email || null,
      });

      await addDoc(activitiesRef, {
        type: 'internal',
        event: 'assign',
        assignedFrom: from,
        assignedTo: to,
        byEmail: currentUser?.email || null,
        at: serverTimestamp(),
      });
      await bumpLeadActivity(leadRef, 1);

      setSaving(false);
      setAssignAnchorEl(null);
    } catch (err: any) {
      console.error('assign update failed', err);
      setError(err?.message || 'Failed to update assignment');
      setSaving(false);
    }
  };

  const handleWhatsApp = async () => {
    if (!lead?.phone) return;
    const digits = normalizePhoneDigits(lead.phone);
    if (!digits) return;

    const url = `https://wa.me/${digits}`;
    window.open(url, '_blank', 'noopener,noreferrer');

    // Log "WhatsApp sent" as an internal activity (best-effort).
    try {
      const leadRef = doc(db, 'leads', lead.id);
      const activitiesRef = collection(leadRef, 'activities');
      await addDoc(activitiesRef, {
        type: 'internal',
        event: 'whatsapp-sent',
        toPhone: lead.phone || null,
        byEmail: currentUser?.email || null,
        at: serverTimestamp(),
      });
      await bumpLeadActivity(leadRef, 1);
    } catch (err) {
      // Don't block the UX for logging failures.
      console.error('whatsapp log failed', err);
    }
  };

  const handleAlternateWhatsApp = async (phone: string) => {
    if (!lead) return;
    const digits = normalizePhoneDigits(phone);
    if (!digits) return;

    window.open(`https://wa.me/${digits}`, '_blank', 'noopener,noreferrer');

    try {
      const leadRef = doc(db, 'leads', lead.id);
      const activitiesRef = collection(leadRef, 'activities');
      await addDoc(activitiesRef, {
        type: 'internal',
        event: 'whatsapp-sent',
        toPhone: phone || null,
        byEmail: currentUser?.email || null,
        at: serverTimestamp(),
      });
      await bumpLeadActivity(leadRef, 1);
    } catch (err) {
      console.error('alternate whatsapp log failed', err);
    }
  };

  const handleCompleteWebsiteRequest = async () => {
    if (!lead) return;
    const requestValue =
      lead.websiteRequest?.type ||
      lead.websiteRequest?.label ||
      lead.enquiryType;
    if (!requestValue) return;

    const config = websiteRequestConfig(requestValue);
    setCompletingWebsiteRequest(true);
    setError(null);
    try {
      const leadRef = doc(db, 'leads', lead.id);
      const activitiesRef = collection(leadRef, 'activities');
      await updateDoc(leadRef, {
        websiteRequest: {
          ...(lead.websiteRequest || {}),
          type: config.kind,
          label: lead.websiteRequest?.label || lead.enquiryType || config.label,
          status: 'completed',
          receivedAt: lead.websiteRequest?.receivedAt || lead.createdAt || null,
          completedAt: serverTimestamp(),
          completedBy: currentUser?.email || null,
        },
        updatedAt: serverTimestamp(),
      });
      await addDoc(activitiesRef, {
        type: 'internal',
        event: 'website-request-completed',
        requestType: config.kind,
        requestLabel: lead.websiteRequest?.label || lead.enquiryType || config.label,
        byEmail: currentUser?.email || null,
        at: serverTimestamp(),
      });
      await bumpLeadActivity(leadRef, 1);
    } catch (err: any) {
      console.error('website request completion failed', err);
      setError(err?.message || 'Failed to complete website request');
    } finally {
      setCompletingWebsiteRequest(false);
    }
  };

  const renderActivity = (a: Activity, isLast: boolean, idx: number) => {
    try {
      const ts = formatDateTimeValue(a.at);
      const prevActivity = visibleActivities[idx + 1] || null;
      const normText = (v: any) =>
        String(v || '')
          .trim()
          .toLowerCase()
          .replace(/\s+/g, ' ');
      const visitWithLabel =
        a.visitWith === 'alone'
          ? 'Alone'
          : a.visitWith === 'family'
            ? 'With Family'
            : a.visitWith === 'friend'
              ? 'With Friend/Relative'
              : a.visitWith === 'other'
                ? (a.visitWithOther ? `Other: ${a.visitWithOther}` : 'Other')
                : null;
      const visitArrivalLabel = visitArrivalLabelFor(a);
      const ratingTitle =
        a.type === 'internal' && a.event === 'rating'
          ? `Rating: ${(a.ratingFrom ?? 0) || 0}★ → ${(a.ratingTo ?? 0) || 0}★`
          : null;

    const assignTitle =
      a.type === 'internal' && a.event === 'assign'
        ? `Assigned: ${userLabel(a.assignedFrom)} → ${userLabel(a.assignedTo)}`
        : null;

    const whatsappTitle =
      a.type === 'internal' && a.event === 'whatsapp-sent'
        ? `WhatsApp sent${a.toPhone ? ` to ${a.toPhone}` : ''}`
        : null;

    const websiteRequestTitle =
      a.type === 'internal' && a.event === 'website-request-received'
        ? `Website request received: ${a.requestLabel || 'General Enquiry'}`
        : a.type === 'internal' && a.event === 'website-request-completed'
          ? `Website request completed: ${a.requestLabel || 'General Enquiry'}`
          : null;
    const reopenTitle =
      a.reopenReason
        ? `Lead reopened: ${a.reopenReason}`
        : null;

    const refByTitle =
      a.type === 'internal' && a.event === 'ref-by'
        ? 'Ref By updated'
        : null;

      const apptAtStr = formatDateTimeValue(a.appointmentAt);
    const apptTypeLabel =
      a.appointmentType === 'office-meeting'
        ? 'Office Meeting'
        : a.appointmentType === 'site-visit'
          ? 'Site Visit'
          : '';
    const apptTitle =
      a.type === 'internal' && typeof a.event === 'string' && a.event.startsWith('appointment-')
        ? `${a.event === 'appointment-scheduled'
            ? 'Appointment Scheduled'
            : a.event === 'appointment-rescheduled'
              ? 'Appointment Rescheduled'
              : 'Appointment Completed'}${apptTypeLabel ? ` (${apptTypeLabel})` : ''}${apptAtStr ? ` • ${apptAtStr}` : ''}`
        : null;

    const titleMap: Record<string, string> = {
      status: `Status: ${a.statusFrom || ''} → ${a.statusTo || ''}`,
      assign: `Assigned: ${userLabel(a.assignedFrom)} → ${userLabel(a.assignedTo)}`,
      followup: `Follow-up set: ${formatDateValue(a.followUpDate)}`,
      call: a.callOutcome ? `Call (${a.callOutcome})` : 'Call',
      visit: [visitWithLabel, visitArrivalLabel].filter(Boolean).length
        ? `Visit (${[visitWithLabel, visitArrivalLabel].filter(Boolean).join(' • ')})`
        : 'Visit',
      internal: 'Internal Update',
    };

		    const title =
          ratingTitle ||
          assignTitle ||
          whatsappTitle ||
          websiteRequestTitle ||
          reopenTitle ||
          refByTitle ||
          apptTitle ||
          titleMap[a.type] ||
          a.type;

	    const statusLabelFor = (idOrLabel?: string | null) => {
	      const raw = String(idOrLabel || '').trim();
	      if (!raw) return '';
	      const byId = statuses.find((s: any) => String(s?.id || '') === raw);
      if (byId?.label) return String(byId.label);
      const norm = raw.toLowerCase();
      const byLabel = statuses.find(
        (s: any) => String(s?.label || '').trim().toLowerCase() === norm
      );
      if (byLabel?.label) return String(byLabel.label);
      return raw;
    };

      const fmtDate = (v: any) => {
        const out = formatDateValue(v);
        return out || '—';
      };

    const changeRows: Array<{ label: string; from: string; to: string }> = [];
    if (a.statusFrom || a.statusTo) {
      const fromLabel = statusLabelFor(a.statusFrom) || '—';
      const toLabel = statusLabelFor(a.statusTo) || '—';
      if (normText(fromLabel) !== normText(toLabel)) {
        changeRows.push({
          label: 'Status',
          from: fromLabel,
          to: toLabel,
        });
      }
    }
    if (a.followUpFrom || a.followUpDate) {
      const fromDate = fmtDate(a.followUpFrom);
      const toDate = fmtDate(a.followUpDate);
      if (normText(fromDate) !== normText(toDate)) {
        changeRows.push({
          label: 'Next',
          from: fromDate,
          to: toDate,
        });
      }
    }
    if (a.reopenReason) {
      changeRows.push({
        label: 'Reason',
        from: '—',
        to: String(a.reopenReason),
      });
    }
    if (a.reopenWhatsappRequested === true) {
      changeRows.push({
        label: 'WhatsApp',
        from: '—',
        to: 'Requested',
      });
    }
    if (a.refByFromId || a.refByToId || a.refByFromName || a.refByToName || (a as any).refByFromRefName || (a as any).refByToRefName) {
      const from =
        a.refByFromName ||
        a.refByFromId ||
        (a as any).refByFromRefName ||
        '—';
      const to =
        a.refByToName ||
        a.refByToId ||
        (a as any).refByToRefName ||
        '—';
      if (normText(from) !== normText(to)) {
        changeRows.push({
          label: 'Ref By',
          from,
          to,
        });
      }
    }
    if (a.event === 'project-change') {
      const fromProject = a.projectFromName || a.projectFromId || '—';
      const toProject = a.projectToName || a.projectToId || '—';
      if (normText(fromProject) !== normText(toProject)) {
        changeRows.push({
          label: 'Project',
          from: fromProject,
          to: toProject,
        });
      }
      const fromUnit = a.unitTypeFrom || '—';
      const toUnit = a.unitTypeTo || '—';
      if (normText(fromUnit) !== normText(toUnit)) {
        changeRows.push({
          label: 'Unit Type',
          from: fromUnit,
          to: toUnit,
        });
      }
    }
    if (a.projectName || a.projectId) {
      const currentProject = String(a.projectName || a.projectId || '').trim();
      const previousProject = String(
        prevActivity?.projectName || prevActivity?.projectId || ''
      ).trim();
      if (!previousProject || normText(currentProject) !== normText(previousProject)) {
        changeRows.push({
          label: 'Project',
          from: previousProject || '—',
          to: currentProject || '—',
        });
      }
    }
    if (a.unitType) {
      const currentUnit = String(a.unitType || '').trim();
      const previousUnit = String(prevActivity?.unitType || '').trim();
      if (!previousUnit || normText(currentUnit) !== normText(previousUnit)) {
        changeRows.push({
          label: 'Unit Type',
          from: previousUnit || '—',
          to: currentUnit || '—',
        });
      }
    }
    if (a.type === 'call' && a.callOutcome) {
      const currentOutcome = String(a.callOutcome || '').trim();
      const previousOutcome = String(prevActivity?.callOutcome || '').trim();
      if (!previousOutcome || normText(currentOutcome) !== normText(previousOutcome)) {
        changeRows.push({
          label: 'Call Outcome',
          from: previousOutcome || '—',
          to: currentOutcome || '—',
        });
      }
    }
    if (a.type === 'visit' && (a.visitWith || a.visitWithOther)) {
      const toVisit =
        a.visitWith === 'other'
          ? (a.visitWithOther ? `Other: ${a.visitWithOther}` : 'Other')
          : (visitWithLabel || String(a.visitWith || ''));
      const previousVisitLabel =
        prevActivity?.visitWith === 'other'
          ? (prevActivity?.visitWithOther
            ? `Other: ${String(prevActivity.visitWithOther)}`
            : 'Other')
          : (prevActivity?.visitWith === 'alone'
            ? 'Alone'
            : prevActivity?.visitWith === 'family'
              ? 'With Family'
              : prevActivity?.visitWith === 'friend'
                ? 'With Friend/Relative'
                : String(prevActivity?.visitWith || ''));
      if (!previousVisitLabel || normText(toVisit || '—') !== normText(previousVisitLabel || '—')) {
        changeRows.push({
          label: 'Visit With',
          from: previousVisitLabel || '—',
          to: toVisit || '—',
        });
      }
    }
    if (a.type === 'visit' && a.visitArrival) {
      const currentArrival = visitArrivalLabelFor(a);
      const previousArrival = prevActivity ? visitArrivalLabelFor(prevActivity) : null;
      if (!previousArrival || normText(currentArrival || '—') !== normText(previousArrival || '—')) {
        changeRows.push({
          label: 'Visit Came Via',
          from: previousArrival || '—',
          to: currentArrival || '—',
        });
      }
    }
    if (a.promptAnswers && typeof a.promptAnswers === 'object') {
      const fmtPromptValue = (value: any): string => {
        if (value === null || value === undefined) return '—';
        if (Array.isArray(value)) {
          const out = value
            .map((v) => String(v || '').trim())
            .filter(Boolean)
            .join(', ');
          return out || '—';
        }
        if (value?.toDate) {
          const d = value.toDate();
          return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString() : '—';
        }
        if (value instanceof Date) {
          return !Number.isNaN(value.getTime()) ? value.toLocaleDateString() : '—';
        }
        const s = String(value).trim();
        return s || '—';
      };
      Object.entries(a.promptAnswers).forEach(([promptId, rawVal]) => {
        const def = findPromptDef(promptId);
        const lbl = timelinePromptLabel(promptId, String(def?.label || ''));
        const val =
          rawVal && typeof rawVal === 'object' && 'value' in (rawVal as any)
            ? (rawVal as any).value
            : rawVal;
        const currentVal = fmtPromptValue(val);
        const prevRawVal = prevActivity?.promptAnswers?.[promptId];
        const prevVal =
          prevRawVal && typeof prevRawVal === 'object' && 'value' in (prevRawVal as any)
            ? (prevRawVal as any).value
            : prevRawVal;
        const previousVal = fmtPromptValue(prevVal);
        if (normText(currentVal) !== normText(previousVal || '—')) {
          changeRows.push({
            label: lbl,
            from: previousVal || '—',
            to: currentVal,
          });
        }
      });
    }

    const iconFor = () => {
      if (a.type === 'call') return <CallOutlinedIcon sx={{ fontSize: 16 }} />;
      if (a.type === 'visit') return <PlaceOutlinedIcon sx={{ fontSize: 16 }} />;
      if (a.type === 'internal' && a.event === 'rating') return <StarOutlineIcon sx={{ fontSize: 16 }} />;
      if (a.type === 'internal' && a.event === 'assign') return <AssignmentIndIcon sx={{ fontSize: 16 }} />;
      if (a.type === 'internal' && a.event === 'whatsapp-sent') return <WhatsAppIcon sx={{ fontSize: 16 }} />;
      if (a.type === 'internal' && typeof a.event === 'string' && a.event.startsWith('appointment-')) {
        return <EventAvailableOutlinedIcon sx={{ fontSize: 16 }} />;
      }
      if (a.type === 'internal' && a.followUpDate) return <EventIcon sx={{ fontSize: 16 }} />;
      return <InfoOutlinedIcon sx={{ fontSize: 16 }} />;
    };

    const iconTone = () => {
      if (a.type === 'call') return { bg: 'primary.lighter', fg: 'primary.main' };
      if (a.type === 'visit') return { bg: 'success.lighter', fg: 'success.main' };
      if (a.type === 'internal' && a.event === 'rating') return { bg: 'warning.lighter', fg: 'warning.main' };
      if (a.type === 'internal' && a.event === 'assign') return { bg: 'info.lighter', fg: 'info.main' };
      if (a.type === 'internal' && a.event === 'whatsapp-sent') return { bg: 'success.lighter', fg: 'success.main' };
      if (a.type === 'internal' && typeof a.event === 'string' && a.event.startsWith('appointment-')) {
        return { bg: 'secondary.lighter', fg: 'secondary.main' };
      }
      return { bg: 'grey.200', fg: 'text.secondary' };
    };
    const tone = iconTone();

      return (
        <Box
          key={a.id}
          sx={{
            display: 'flex',
            gap: 1.5,
            alignItems: 'flex-start',
            position: 'relative',
          }}
        >
        <Box sx={{ width: 28, display: 'flex', justifyContent: 'center', position: 'relative', flexShrink: 0 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: tone.bg,
              color: tone.fg,
              border: '1px solid',
              borderColor: 'divider',
              zIndex: 1,
            }}
          >
            {iconFor()}
          </Box>
          {!isLast && (
            <Box
              sx={{
                position: 'absolute',
                top: 28,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 2,
                bottom: -8,
                bgcolor: 'divider',
              }}
            />
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 1.25,
              bgcolor: 'background.paper',
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.2 }} noWrap>
                {title}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                {ts}
              </Typography>
            </Stack>

	            {a.byEmail && (
	              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
	                {(() => {
	                  const email = String(a.byEmail || '');
	                  const u = users.find((usr: any) => usr?.email === email);
	                  if (u?.name) return u.name;
	                  // Fallback: show local-part if we don't have a user record.
	                  return email.includes('@') ? email.split('@')[0] : email;
	                })()}
	              </Typography>
	            )}

	            {changeRows.length > 0 && (
	              <Stack spacing={0.5} sx={{ mt: 0.75 }}>
	                {changeRows.map((c, idx) => (
	                  <Box
                    key={idx}
                    sx={{
                      display: 'flex',
                      gap: 0.75,
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      bgcolor: 'rgba(15, 23, 42, 0.035)',
                      border: '1px solid rgba(15, 23, 42, 0.08)',
                      borderRadius: 2,
                      px: 1,
                      py: 0.5,
                    }}
                  >
	                    <Chip
	                      size="small"
	                      label={c.label}
	                      sx={{
	                        height: 22,
	                        fontSize: 12,
	                        fontWeight: 800,
	                        bgcolor: 'rgba(15, 23, 42, 0.08)',
	                        '& .MuiChip-label': { px: 0.75 },
	                      }}
	                    />
	                    <Typography
	                      variant="body2"
	                      color="text.secondary"
	                      sx={{ fontWeight: 700 }}
	                    >
                      {c.from}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mx: 0.25 }}>
                      →
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 800, color: 'text.primary' }}
                    >
                      {c.to}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}

            {(() => {
              const rawPoints = (a as any).meetingPoints ?? (a as any).meetingPoint;
              let points: string[] = Array.isArray(rawPoints)
                ? rawPoints.filter((x: any) => typeof x === 'string' && x.trim())
                : typeof rawPoints === 'string' && rawPoints.trim()
                  ? [rawPoints.trim()]
                  : [];
              if (
                points.length === 0 &&
                a.type === 'internal' &&
                typeof a.event === 'string' &&
                a.event.startsWith('appointment-')
              ) {
                const apptPoints =
                  (lead?.appointment as any)?.meetingPoints ?? (lead?.appointment as any)?.meetingPoint;
                if (Array.isArray(apptPoints)) {
                  points = apptPoints.filter((x: any) => typeof x === 'string' && x.trim());
                } else if (typeof apptPoints === 'string' && apptPoints.trim()) {
                  points = [apptPoints.trim()];
                }
              }
              if (points.length === 0) return null;
              return (
                <Box sx={{ mt: 0.75 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 800,
                      color: '#9a3412',
                      display: 'block',
                      mb: 0.35,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      fontSize: 10.5,
                    }}
                  >
                    Meeting Points
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {points.map((mp, idx) => (
                      <Chip
                        key={idx}
                        size="small"
                        label={mp}
                        sx={{
                          borderRadius: 999,
                          fontWeight: 700,
                          bgcolor: '#fff7ed',
                          color: '#9a3412',
                          border: '1px solid #fed7aa',
                          maxWidth: '100%',
                          height: 'auto',
                          alignItems: 'flex-start',
                          '& .MuiChip-label': {
                            px: 0.9,
                            py: 0.4,
                            whiteSpace: 'normal',
                            overflow: 'visible',
                            textOverflow: 'clip',
                            display: 'block',
                            lineHeight: 1.25,
                            wordBreak: 'break-word',
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              );
            })()}

            {(() => {
              const notesRaw: any = (a as any).notes;
              const notesArr: string[] = Array.isArray(notesRaw)
                ? notesRaw
                : typeof notesRaw === 'string' && notesRaw.trim()
                  ? [notesRaw.trim()]
                  : [];
              if (notesArr.length === 0) return null;
              return (
                <Box sx={{ mt: 0.75, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {notesArr
                    .map((n) => String(n || '').trim())
                    .filter(Boolean)
                    .map((n, idx) => (
                      <Chip
                        key={idx}
                        size="small"
                        label={n}
                        sx={{
                          borderRadius: 999,
                          fontWeight: 700,
                          bgcolor: 'rgba(15, 23, 42, 0.06)',
                          border: '1px solid rgba(15, 23, 42, 0.10)',
                          // Allow long notes to wrap instead of ellipsis.
                          maxWidth: '100%',
                          height: 'auto',
                          alignItems: 'flex-start',
                          '& .MuiChip-label': {
                            px: 0.9,
                            py: 0.5,
                            whiteSpace: 'normal',
                            overflow: 'visible',
                            textOverflow: 'clip',
                            display: 'block',
                            lineHeight: 1.25,
                            wordBreak: 'break-word',
                          },
                        }}
                      />
                    ))}
                </Box>
              );
            })()}

            {(() => {
              const ca: any = (a as any)?.closeAnalysis || null;
              if (!ca) return null;
              const secondary = Array.isArray(ca.secondaryReasons)
                ? ca.secondaryReasons.filter((x: any) => typeof x === 'string' && x.trim())
                : [];
              const parts: string[] = [];
              if (ca.primaryReason) parts.push(`Primary: ${ca.primaryReason}`);
              if (secondary.length) parts.push(`Secondary: ${secondary.join(', ')}`);
              if (ca.decisionStage) parts.push(`Stage: ${ca.decisionStage}`);
              else if (ca.decisionStageAuto) parts.push(`Stage: ${ca.decisionStageAuto}`);
              if (ca.interestedLocation) parts.push(`Location: ${ca.interestedLocation}`);
              if (ca.boughtAtLocation) parts.push(`Purchased At: ${ca.boughtAtLocation}`);
              if (ca.requiredType) parts.push(`Required Type: ${ca.requiredType}`);
              if (ca.requiredTypeLocation) parts.push(`Required Location: ${ca.requiredTypeLocation}`);
              if (ca.budgetDetail) parts.push(`Budget: ${ca.budgetDetail}`);
              if (ca.distancePreferredArea) parts.push(`Area: ${ca.distancePreferredArea}`);
              if (ca.competitorProject) parts.push(`Competitor Project: ${ca.competitorProject}`);
              if (ca.competitorPriceBand) parts.push(`Price Band: ${ca.competitorPriceBand}`);
              if (ca.competitorAdvantage) parts.push(`Advantage: ${ca.competitorAdvantage}`);
              if (parts.length === 0) return null;
              return (
                <Box sx={{ mt: 0.75, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {parts.map((txt, idx) => (
                    <Chip
                      key={`close-analysis-${a.id}-${idx}`}
                      size="small"
                      label={txt}
                      sx={{
                        borderRadius: 999,
                        fontWeight: 700,
                        bgcolor: 'rgba(2, 132, 199, 0.08)',
                        border: '1px solid rgba(2, 132, 199, 0.22)',
                        maxWidth: '100%',
                        height: 'auto',
                        alignItems: 'flex-start',
                        '& .MuiChip-label': {
                          px: 0.9,
                          py: 0.5,
                          whiteSpace: 'normal',
                          overflow: 'visible',
                          textOverflow: 'clip',
                          display: 'block',
                          lineHeight: 1.25,
                          wordBreak: 'break-word',
                        },
                      }}
                    />
                  ))}
                </Box>
              );
            })()}
          </Box>
        </Box>
        </Box>
      );
    } catch (err) {
      console.error('Failed to render activity row', a?.id, err);
      return (
        <Alert key={a.id} severity="warning" sx={{ borderRadius: 2 }}>
          One activity entry could not be shown.
        </Alert>
      );
    }
  };

  const isCustomerContactActivity = (a: Activity) => {
    if (a.type === 'call') return true;
    if (a.type === 'visit') return true;
    if (a.type === 'internal' && a.event === 'fb-lead') return true;
    if (a.type === 'internal' && a.event === 'whatsapp-sent') return true;
    if (
      a.type === 'internal' &&
      (a.event === 'website-request-received' ||
        a.event === 'website-request-completed')
    ) {
      return true;
    }
    if (a.type === 'internal' && typeof a.event === 'string' && a.event.startsWith('appointment-')) return true;
    return false;
  };

  const visibleActivities = onlyCustomerContact ? activities.filter(isCustomerContactActivity) : activities;

  const showQuick = true;
  const showLog = true;
  const showTimeline = true;

  const sectionSx = () => {
    // "Ribbon" sections: solid card with an accent top bar (Style 2).
    return {
      p: 1.5,
      borderRadius: 2.5,
      border: '1px solid',
      borderColor: alpha(accent, 0.18),
      backgroundColor: 'background.paper',
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        right: 0,
        height: 4,
        background: `linear-gradient(90deg, ${alpha(accent, 0.95)} 0%, ${alpha(accent, 0.15)} 70%, ${alpha(accent, 0)} 100%)`,
      },
    } as const;
  };

  return (
    <>
    <Drawer
      anchor="right"
      open={open}
      variant={isMobile && mobileFullScreen ? 'temporary' : (closeOnOutsideClick ? 'temporary' : 'persistent')}
      hideBackdrop={!(isMobile && mobileFullScreen) && !closeOnOutsideClick}
      ModalProps={{
        keepMounted: true,
        disableEscapeKeyDown: !(isMobile && mobileFullScreen) && !closeOnOutsideClick,
        BackdropProps: closeOnOutsideClick && !(isMobile && mobileFullScreen)
          ? { invisible: true }
          : undefined,
      }}
      SlideProps={{
        onExited,
      }}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isMobile && mobileFullScreen ? '100%' : drawerWidth,
          maxWidth: '100%',
          top: `${drawerTopOffset}px`,
          height: `calc(100vh - ${drawerTopOffset}px)`,
        },
      }}
    >
      <Box
        ref={drawerScrollRef}
        sx={{
          width: isMobile && mobileFullScreen ? '100%' : drawerWidth,
          maxWidth: '100%',
          p: { xs: 1.25, sm: 2 },
          position: 'relative',
          height: '100%',
          overflowY: 'auto',
        }}
        data-role="lead-detail-drawer"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <Box
          onMouseDown={startResize}
          sx={{ position: 'absolute', left: -6, top: 0, bottom: 0, width: 8, cursor: 'ew-resize', zIndex: 2 }}
        />
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <HistoryIcon />
            <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 700 }}>
              Lead Details
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {(canMergeLead || canDeleteLead) && (
              <IconButton
                aria-label="More actions"
                onClick={(e) => setMoreAnchorEl(e.currentTarget)}
              >
                <MoreVertIcon />
              </IconButton>
            )}
            <IconButton onClick={onClose} aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>

        {(canMergeLead || canDeleteLead) && (
          <>
            <Menu
              anchorEl={moreAnchorEl}
              open={moreMenuOpen}
              onClose={() => setMoreAnchorEl(null)}
              sx={{ zIndex: (t) => t.zIndex.modal + 2 }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {canMergeLead && (
                <MenuItem
                  onClick={() => {
                    setMoreAnchorEl(null);
                    setMergeDialogOpen(true);
                  }}
                >
                  Merge Lead
                </MenuItem>
              )}
              {canDeleteLead && (
                <MenuItem
                  onClick={() => {
                    setMoreAnchorEl(null);
                    setDeleteDialogOpen(true);
                  }}
                  sx={{ color: 'error.main' }}
                >
                  Delete Lead
                </MenuItem>
              )}
            </Menu>

            <Dialog
              open={mergeDialogOpen}
              onClose={() => {
                if (!mergeSaving) setMergeDialogOpen(false);
              }}
              fullWidth
              maxWidth="sm"
            >
              <DialogTitle>Merge duplicate lead</DialogTitle>
              <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Current lead stays primary. Search old/new duplicate by phone or name.
                  Matching phone search also checks alternate merged phone numbers.
                </Typography>
                {mergeError && <Alert severity="error" sx={{ mb: 1 }}>{mergeError}</Alert>}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Search duplicate lead"
                    value={mergeSearchRaw}
                    onChange={(e) => setMergeSearchRaw(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        searchMergeLeads();
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={searchMergeLeads}
                    disabled={mergeSearching || mergeSaving}
                  >
                    Search
                  </Button>
                </Stack>
                <Stack spacing={1}>
                  {mergeResults.map((row: any) => {
                    const selected = mergeSelectedLead?.id === row.id;
                    return (
                      <Box
                        key={row.id}
                        onClick={() => setMergeSelectedLead(row)}
                        sx={{
                          border: `1px solid ${
                            selected ?
                              theme.palette.primary.main :
                              theme.palette.divider
                          }`,
                          borderRadius: 2,
                          p: 1.25,
                          cursor: 'pointer',
                          bgcolor: selected ?
                            alpha(theme.palette.primary.main, 0.08) :
                            '#fff',
                        }}
                      >
                        <Typography sx={{ fontWeight: 800 }}>
                          {row.name || '--'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {[
                            row.phone || row.phone10,
                            row.status,
                            row.source,
                          ].filter(Boolean).join(' · ')}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setMergeDialogOpen(false)} disabled={mergeSaving}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleMergeLead}
                  disabled={!mergeSelectedLead || mergeSaving}
                >
                  Confirm Merge
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog
              open={deleteDialogOpen}
              onClose={() => {
                if (!deletingLead) setDeleteDialogOpen(false);
              }}
            >
              <DialogTitle>Delete this lead?</DialogTitle>
              <DialogContent>
                <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
                  This will permanently delete the lead and its timeline/activities. This cannot be undone.
                </Typography>
                <TextField
                  fullWidth
                  label="Type DELETE to confirm"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  autoComplete="off"
                />
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => setDeleteDialogOpen(false)}
                  disabled={deletingLead}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDeleteLead}
                  disabled={deletingLead || deleteConfirmText.trim() !== 'DELETE'}
                  sx={{ fontWeight: 700 }}
                >
                  Delete
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}

        <Box
          sx={{
            mb: 2,
            display: showQuick ? 'block' : 'none',
            ...sectionSx(),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                {editingName ? (
                  <>
                    <TextField
                      value={nameDraft}
                      onChange={(e) => setNameDraft(e.target.value)}
                      size="small"
                      autoFocus
                      placeholder="Lead name"
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        '& .MuiOutlinedInput-root': {
                          height: 34,
                          borderRadius: 2,
                          bgcolor: 'rgba(255, 255, 255, 0.92)',
                        },
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleSaveName();
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          setEditingName(false);
                          setNameDraft(lead?.name ? String(lead.name) : '');
                        }
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={handleSaveName}
                      disabled={savingName}
                      aria-label="Save name"
                      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
                    >
                      <CheckOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditingName(false);
                        setNameDraft(lead?.name ? String(lead.name) : '');
                      }}
                      disabled={savingName}
                      aria-label="Cancel name edit"
                      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 600 }} noWrap>
                      {formatPersonName(lead.name)}
                    </Typography>
                    {canEditLeadName && (
                      <IconButton
                        size="small"
                        onClick={() => setEditingName(true)}
                        aria-label="Edit name"
                        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    )}
                  </>
                )}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, pt: 0.25 }}>
              <Rating
                value={ratingValue}
                max={5}
                size="small"
                readOnly={!canEditRating}
                IconContainerComponent={RatingIconContainer}
                onChange={(_, v) => {
                  const next = typeof v === 'number' ? v : 0;
                  setRatingValue(next);
                  // Save immediately to keep it lightweight (no extra "Save" button).
                  handleRatingSave(next);
                }}
              />
            </Box>
          </Box>
          <Box
            sx={{
              mt: 0.75,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              flexWrap: 'nowrap',
              overflowX: 'auto',
              pb: 0.25,
            }}
          >
            <Chip
              label={`Status: ${statuses.find((s) => s.id === lead.status)?.label || lead.status}`}
              size="small"
              sx={{ px: 0.3, height: 20, ...accentFilledChipSx }}
            />
            {(() => {
              const pid = String((lead as any)?.projectId || '').trim();
              const pLabel = pid
                ? String(projectForId.get(pid)?.name || (lead as any)?.projectName || pid).trim()
                : '';
              const rawUnitType = String((lead as any)?.unitType || '').trim();
              const enquiryLabel = String(lead.enquiryType || '').trim();
              const ut =
                websiteRequest &&
                enquiryLabel &&
                rawUnitType.toLowerCase() === enquiryLabel.toLowerCase()
                  ? ''
                  : rawUnitType;
              if (!pLabel && !ut) return null;
              return (
                <>
                  {pLabel ? (
                    <Chip
                      label={`${pLabel}`}
                      size="small"
                      clickable={canEditLeadProject && visibleProjects.length > 0}
                      onClick={(e) => {
                        if (!canEditLeadProject || visibleProjects.length === 0) return;
                        setProjectAnchorEl(e.currentTarget as HTMLElement);
                      }}
                      sx={{
                        px: 0.3,
                        height: 20,
                        bgcolor: 'rgba(25, 118, 210, 0.10)',
                        fontWeight: 800,
                        cursor: canEditLeadProject && visibleProjects.length > 0 ? 'pointer' : 'default',
                        '&:hover': canEditLeadProject && visibleProjects.length > 0
                          ? { bgcolor: 'rgba(25, 118, 210, 0.16)' }
                          : undefined,
                      }}
                    />
                  ) : null}
                  {ut ? (
                    <Chip
                      label={`${ut}`}
                      size="small"
                      sx={{ px: 0.3, height: 20, bgcolor: 'rgba(46, 125, 50, 0.10)', fontWeight: 800 }}
                    />
                  ) : null}
                </>
              );
            })()}
          </Box>
          <LeadBriefCard
            lead={lead}
            activities={activities}
            statusLabel={statuses.find((status) => status.id === lead.status)?.label || String(lead.status || '')}
            loading={loadingActivities}
            historyError={!!timelineError}
          />
          {/* Put the next commitment immediately after the brief so it cannot be missed. */}
          {lead.appointment?.status === 'scheduled' && lead.appointment?.scheduledAt?.toDate && (() => {
            const appointmentAt = toDateMaybe(lead.appointment?.scheduledAt);
            if (!appointmentAt) return null;
            const appointmentType = lead.appointment?.type === 'office-meeting' ? 'Office meeting' : 'Site visit';
            const apptPointsRaw = (lead.appointment as any)?.meetingPoints ?? (lead.appointment as any)?.meetingPoint;
            const apptPoints: string[] = Array.isArray(apptPointsRaw)
              ? apptPointsRaw.filter((x: any) => typeof x === 'string' && x.trim())
              : typeof apptPointsRaw === 'string' && apptPointsRaw.trim()
                ? [apptPointsRaw.trim()]
                : [];
            return (
              <Box
                component="section"
                aria-label="Upcoming appointment"
                sx={{
                  mt: 1.25,
                  p: 1.5,
                  borderRadius: 2.5,
                  border: '1px solid #eadcc8',
                  borderLeft: '4px solid #9d6f32',
                  bgcolor: '#fffaf3',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                  <Box sx={{ width: 42, height: 42, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: '#f2e5d2', color: '#8a5a20', flexShrink: 0 }}>
                    <EventAvailableOutlinedIcon sx={{ fontSize: 23 }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} sx={{ mb: 0.45 }}>
                      <Typography variant="caption" sx={{ fontWeight: 900, letterSpacing: 0.55, color: '#7b4f15' }}>
                        UPCOMING APPOINTMENT
                      </Typography>
                      <Chip
                        label="Scheduled"
                        size="small"
                        sx={{ height: 20, bgcolor: '#efe2cf', color: '#7b4f15', fontWeight: 800, flexShrink: 0, '& .MuiChip-label': { px: 0.85, fontSize: 10 } }}
                      />
                    </Stack>
                    <Typography sx={{ fontSize: 16, lineHeight: 1.3, fontWeight: 800, color: 'text.primary' }}>
                      {appointmentDayLabel(appointmentAt)}
                    </Typography>
                    <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap" sx={{ mt: 0.5 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#7b4f15' }}>
                        {appointmentAt.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' })}
                      </Typography>
                      <Box component="span" sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: '#b8aa96' }} />
                      <Typography variant="body2" color="text.secondary">{appointmentType}</Typography>
                    </Stack>
                    {apptPoints.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 800,
                            color: '#7b4f15',
                            display: 'block',
                            mb: 0.35,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            fontSize: 10.5,
                          }}
                        >
                          Meeting Points
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                          {apptPoints.map((mp, idx) => (
                            <Chip
                              key={idx}
                              size="small"
                              label={mp}
                              sx={{
                                borderRadius: 999,
                                fontWeight: 700,
                                bgcolor: '#efe2cf',
                                color: '#7b4f15',
                                border: '1px solid #dfcfb9',
                                maxWidth: '100%',
                                height: 'auto',
                                alignItems: 'flex-start',
                                '& .MuiChip-label': {
                                  px: 0.9,
                                  py: 0.4,
                                  whiteSpace: 'normal',
                                  overflow: 'visible',
                                  textOverflow: 'clip',
                                  display: 'block',
                                  lineHeight: 1.25,
                                  wordBreak: 'break-word',
                                },
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
                {canManageAppointments && (
                  <Stack direction="row" spacing={1} sx={{ mt: 1.35, pt: 1.2, borderTop: '1px solid #eee2d2' }}>
                    <Button fullWidth size="small" variant="outlined" startIcon={<EditOutlinedIcon />} onClick={() => {
                      setActivityType('appointment');
                      setApptCompleteOpen(false);
                      setApptFormOpen(true);
                      setLogExpanded(true);
                    }} sx={{ borderColor: '#cba979', color: '#7b4f15', bgcolor: '#fff', '&:hover': { borderColor: '#9d6f32', bgcolor: '#fff8ed' } }}>
                      Edit
                    </Button>
                    <Button fullWidth size="small" variant="contained" startIcon={<CheckOutlinedIcon />} onClick={() => {
                      setActivityType('appointment');
                      setApptFormOpen(false);
                      setApptCompleteOpen(true);
                      setLogExpanded(true);
                    }} sx={{ bgcolor: '#9d6f32', '&:hover': { bgcolor: '#805622' } }}>
                      Complete
                    </Button>
                  </Stack>
                )}
              </Box>
            );
          })()}
          <Popover
            open={Boolean(projectAnchorEl)}
            anchorEl={projectAnchorEl}
            onClose={() => {
              if (!savingProject) setProjectAnchorEl(null);
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            <Box sx={{ minWidth: 260, maxWidth: 340, p: 1 }}>
              <Typography variant="subtitle2" sx={{ px: 1, py: 0.75, fontWeight: 800 }}>
                Change Project
              </Typography>
              {visibleProjects.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 1 }}>
                  No projects available.
                </Typography>
              ) : (
                visibleProjects.map((project) => {
                  const pid = String(project.id || '');
                  const selected = pid === String((lead as any)?.projectId || '');
                  return (
                    <MenuItem
                      key={pid}
                      selected={selected}
                      disabled={savingProject || selected}
                      onClick={() => handleProjectChange(pid)}
                      sx={{ borderRadius: 1 }}
                    >
                      <Stack sx={{ minWidth: 0 }}>
                        <Typography noWrap sx={{ fontWeight: selected ? 800 : 600 }}>
                          {project.name || pid}
                        </Typography>
                        {Array.isArray(project.unitTypes) && project.unitTypes.length > 0 && (
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {project.unitTypes.join(', ')}
                          </Typography>
                        )}
                      </Stack>
                    </MenuItem>
                  );
                })
              )}
            </Box>
          </Popover>
          {receptionToday && (receptionToday.status === 'waiting' || receptionToday.status === 'with') && (
            <Box
              sx={{
                mt: 1.25, p: 1.25, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
                border: '1px solid', borderColor: receptionToday.status === 'waiting' ? '#f0c98f' : '#c4d8ea',
                bgcolor: receptionToday.status === 'waiting' ? '#fff8ec' : '#eef4fa',
              }}
            >
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: receptionToday.status === 'waiting' ? '#c77700' : '#2f6b9a', flexShrink: 0 }} />
              <Typography variant="body2" sx={{ fontWeight: 700, flex: 1, minWidth: 200 }}>
                {receptionToday.status === 'waiting'
                  ? `At reception now · waiting ${minutesSince(receptionToday.checkedInAt)} min`
                  : `With ${isReceptionPresenter ? 'you' : receptionToday.presenterName || receptionToday.presenterEmail} · ${minutesSince(receptionToday.takenAt)} min`}
                <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                  {` · ${receptionSummary(receptionToday)}`}
                </Typography>
              </Typography>
              {receptionToday.status === 'waiting' && (canMergeLead || (hasDayPresenterAccess && receptionToday.projectId === currentProjectId)) && (
                <Button size="small" variant="contained" disabled={takingVisit}
                  onClick={async () => {
                    setTakingVisit(true);
                    try {
                      const email = String(currentUser?.email || '');
                      await takeReceptionVisit(db, { id: receptionToday.id, leadId: lead.id, name: lead.name || 'Visitor' },
                        { email, name: currentUser?.displayName || email });
                    } catch (err: any) {
                      setError(err?.message || String(err));
                    } finally {
                      setTakingVisit(false);
                    }
                  }}>
                  I'll take it
                </Button>
              )}
              {isReceptionPresenter && (
                <Button size="small" variant="contained" color="inherit" onClick={() => startVisitFromReception(lead)}
                  sx={{ bgcolor: '#1c1b19', color: '#fff', '&:hover': { bgcolor: '#33312d' } }}>
                  Finish visit
                </Button>
              )}
            </Box>
          )}
          {websiteRequest && (() => {
            const viaWhatsapp = /^whatsapp/i.test(String((lead.websiteRequest as any)?.source || ''));
            const sourceLabel = viaWhatsapp ? 'WHATSAPP REQUEST' : 'WEBSITE REQUEST';
            const rawDue = (lead.websiteRequest as any)?.dueAt || (websiteRequest.kind === 'callback-request' ? (lead as any).nextFollowUpAt : null);
            const dueAt = toDateMaybe(rawDue);
            const requestTitle = websiteRequest.kind === 'callback-request'
              ? 'Call back'
              : websiteRequest.kind === 'schedule-site-visit'
                ? 'Site visit'
                : websiteRequest.kind === 'request-brochure'
                  ? 'Send brochure'
                  : String(lead.websiteRequest?.label || lead.enquiryType || websiteRequest.label).replace(/^WhatsApp:\s*/i, '');
            const accent = websiteRequestCompleted ? theme.palette.success.main : theme.palette[websiteRequest.color].main;
            const now = new Date();
            const overdueMinutes = dueAt ? Math.round((now.getTime() - dueAt.getTime()) / 60000) : 0;
            const isOverdue = !websiteRequestCompleted && !!dueAt && overdueMinutes > 0;
            return (
              <Box
                component="section"
                aria-label={`${sourceLabel.toLowerCase()}${websiteRequestCompleted ? ' completed' : ''}`}
                sx={{
                  mt: 1.25,
                  p: 1.5,
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: alpha(accent, 0.3),
                  borderLeft: `4px solid ${accent}`,
                  bgcolor: alpha(accent, 0.055),
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                  <Box sx={{ width: 42, height: 42, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: alpha(accent, 0.12), color: accent, flexShrink: 0 }}>
                    {websiteRequest.kind === 'schedule-site-visit'
                      ? <PlaceOutlinedIcon sx={{ fontSize: 23 }} />
                      : websiteRequestCompleted
                        ? <CheckOutlinedIcon sx={{ fontSize: 23 }} />
                        : <CallOutlinedIcon sx={{ fontSize: 23 }} />}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} sx={{ mb: 0.45 }}>
                      <Typography variant="caption" sx={{ fontWeight: 900, letterSpacing: 0.55, color: websiteRequestCompleted ? 'success.dark' : `${websiteRequest.color}.dark` }}>
                        {sourceLabel}
                      </Typography>
                      <Chip
                        label={websiteRequestCompleted ? 'Completed' : websiteRequest.priority ? 'High priority' : 'Pending'}
                        size="small"
                        sx={{ height: 20, bgcolor: alpha(accent, 0.12), color: websiteRequestCompleted ? 'success.dark' : `${websiteRequest.color}.dark`, fontWeight: 800, flexShrink: 0, '& .MuiChip-label': { px: 0.85, fontSize: 10 } }}
                      />
                    </Stack>
                    <Typography sx={{ fontSize: 16, lineHeight: 1.3, fontWeight: 800, color: 'text.primary' }}>
                      {requestTitle}
                    </Typography>
                    {dueAt && (
                      <Typography sx={{ mt: 0.45, fontSize: 13.5, fontWeight: 800, color: isOverdue ? 'error.main' : websiteRequestCompleted ? 'success.dark' : `${websiteRequest.color}.dark` }}>
                        {isOverdue ? `Overdue by ${overdueMinutes < 60 ? `${overdueMinutes} min` : overdueMinutes < 1440 ? `${Math.round(overdueMinutes / 60)} hr` : `${Math.round(overdueMinutes / 1440)} days`} · ` : ''}
                        {relativeDateTimeLabel(dueAt)}
                      </Typography>
                    )}
                    {websiteRequestReceivedAt && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.45 }}>
                        Received {relativeDateTimeLabel(websiteRequestReceivedAt).toLowerCase()}
                      </Typography>
                    )}
                    {typeof lead.notes === 'string' && lead.notes.trim() && (
                      <Typography variant="body2" sx={{ mt: 0.75, color: 'text.secondary', whiteSpace: 'pre-wrap' }}>
                        {lead.notes.trim()}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {!websiteRequestCompleted && (
                  <Stack direction="row" gap={0.75} sx={{ mt: 1.35, pt: 1.2, borderTop: `1px solid ${alpha(accent, 0.18)}` }}>
                    {websiteRequest.kind === 'schedule-site-visit' ? (
                      <Button
                        fullWidth
                        size="small"
                        variant="contained"
                        color="warning"
                        disabled={!canManageAppointments}
                        onClick={() => {
                          setApptType('site-visit');
                          setActivityType('appointment');
                          setApptCompleteOpen(false);
                          setApptFormOpen(true);
                          setLogExpanded(true);
                        }}
                      >
                        Schedule Visit
                      </Button>
                    ) : websiteRequest.kind === 'request-brochure' ? (
                      <Button
                        fullWidth
                        size="small"
                        variant="contained"
                        color="success"
                        disabled={!phoneDigits}
                        onClick={(event) => {
                          setBrochureLastInboundAt(null);
                          setBrochureAnchorEl(event.currentTarget);
                        }}
                        startIcon={<WhatsAppIcon />}
                      >
                        Send Brochure
                      </Button>
                    ) : (
                      <Button
                        fullWidth
                        size="small"
                        variant="contained"
                        color={websiteRequest.color}
                        component="a"
                        href={telHref || undefined}
                        disabled={!telHref}
                        startIcon={<CallOutlinedIcon />}
                      >
                        Call Lead
                      </Button>
                    )}
                    {websiteRequest.kind !== 'schedule-site-visit' && (
                      <Button
                        fullWidth
                        size="small"
                        variant="outlined"
                        color={websiteRequest.color}
                        disabled={completingWebsiteRequest}
                        onClick={handleCompleteWebsiteRequest}
                        startIcon={<CheckOutlinedIcon />}
                      >
                        Mark Completed
                      </Button>
                    )}
                  </Stack>
                )}
              </Box>
            );
          })()}
          <Box
            sx={{
              mt: 1.5,
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: 1,
            }}
          >
            {lead.phone && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 0.75,
                  borderRadius: 1.5,
                  bgcolor: 'grey.100',
                  gap: 1,
                }}
              >
                <PhoneIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                <Typography
                  component={telHref ? 'a' : 'span'}
                  href={telHref || undefined}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: 13,
                    color: 'text.primary',
                    textDecoration: 'none',
                    cursor: telHref ? 'pointer' : 'default',
                  }}
                  noWrap
                >
                  {formatPhoneDisplay(lead.phone)}
                </Typography>
                <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
                  <Tooltip title="Call">
                    <span>
                      <IconButton
                        size="small"
                        component="a"
                        href={telHref}
                        disabled={!telHref}
                        aria-label="Call"
                        sx={{ p: 0.25 }}
                      >
                        <CallOutlinedIcon fontSize="inherit" sx={{ fontSize: 18 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="WhatsApp">
                    <span>
                      <IconButton
                        size="small"
                        onClick={handleWhatsApp}
                        disabled={!phoneDigits}
                        aria-label="WhatsApp"
                        sx={{ p: 0.25 }}
                      >
                        <WhatsAppIcon fontSize="inherit" sx={{ fontSize: 18 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Send unit details on WhatsApp">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => setPriceDialogOpen(true)}
                        disabled={!phoneDigits || !lead?.id}
                        aria-label="Send unit details on WhatsApp"
                        sx={{ p: 0.25 }}
                      >
                        <RequestQuoteOutlinedIcon fontSize="inherit" sx={{ fontSize: 18 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {lead?.id && (
                    <SendPriceDialog
                      open={priceDialogOpen}
                      onClose={() => setPriceDialogOpen(false)}
                      leadId={lead.id}
                      leadName={String(lead.name || '')}
                      projectName={String((lead as any).projectName || '')}
                    />
                  )}
                  <Tooltip title="Copy phone">
                    <span>
                      <IconButton
                        size="small"
                        onClick={async () => {
                          try {
                            await copyToClipboard(lead.phone || '');
                          } catch {}
                        }}
                        aria-label="Copy phone"
                        sx={{ p: 0.25 }}
                      >
                        <ContentCopyOutlinedIcon fontSize="inherit" sx={{ fontSize: 18 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Box>
            )}
            {alternatePhoneRows.map((phone) => {
              const altDigits = normalizePhoneDigits(phone);
              const altTelHref = altDigits ? `tel:+${altDigits}` : '';
              return (
                <Box
                  key={phoneIdentity(phone) || phone}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 0.75,
                    borderRadius: 1.5,
                    bgcolor: alpha(theme.palette.info.main, 0.08),
                    border: `1px dashed ${alpha(theme.palette.info.main, 0.3)}`,
                    gap: 1,
                  }}
                >
                  <PhoneIcon
                    fontSize="small"
                    sx={{ color: 'text.secondary' }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        color: 'text.secondary',
                        fontWeight: 800,
                        lineHeight: 1.1,
                      }}
                    >
                      Alt
                    </Typography>
                    <Typography
                      component={altTelHref ? 'a' : 'span'}
                      href={altTelHref || undefined}
                      sx={{
                        display: 'block',
                        fontSize: 13,
                        color: 'text.primary',
                        textDecoration: 'none',
                        cursor: altTelHref ? 'pointer' : 'default',
                      }}
                      noWrap
                    >
                      {formatPhoneDisplay(phone)}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
                    <Tooltip title="Call alternate">
                      <span>
                        <IconButton
                          size="small"
                          component="a"
                          href={altTelHref}
                          disabled={!altTelHref}
                          aria-label="Call alternate phone"
                          sx={{ p: 0.25 }}
                        >
                          <CallOutlinedIcon
                            fontSize="inherit"
                            sx={{ fontSize: 18 }}
                          />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="WhatsApp alternate">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleAlternateWhatsApp(phone)}
                          disabled={!altDigits}
                          aria-label="WhatsApp alternate phone"
                          sx={{ p: 0.25 }}
                        >
                          <WhatsAppIcon
                            fontSize="inherit"
                            sx={{ fontSize: 18 }}
                          />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Copy alternate">
                      <span>
                        <IconButton
                          size="small"
                          onClick={async () => {
                            try {
                              await copyToClipboard(phone);
                            } catch {}
                          }}
                          aria-label="Copy alternate phone"
                          sx={{ p: 0.25 }}
                        >
                          <ContentCopyOutlinedIcon
                            fontSize="inherit"
                            sx={{ fontSize: 18 }}
                          />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Box>
              );
            })}
            {lead.email && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 0.75,
                  borderRadius: 1.5,
                  bgcolor: 'grey.100',
                  gap: 1,
                }}
              >
                <EmailIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: 13, color: 'text.primary' }}>
                  {lead.email}
                </Typography>
                <Tooltip title="Copy email">
                  <span>
                    <IconButton
                      size="small"
                      onClick={async () => {
                        try {
                          await copyToClipboard(lead.email || '');
                        } catch {}
                      }}
                      aria-label="Copy email"
                      sx={{ p: 0.25 }}
                    >
                      <ContentCopyOutlinedIcon fontSize="inherit" sx={{ fontSize: 18 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            )}
            {lead.source && (
              <Box sx={{ display: 'flex', alignItems: 'center', p: 0.75, borderRadius: 1.5, bgcolor: 'grey.100' }}>
                <SourceOutlinedIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography sx={{ fontSize: 13, color: 'text.primary' }}>Source: {lead.source}</Typography>
              </Box>
            )}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 0.75,
                borderRadius: 1.5,
                bgcolor: 'grey.100',
                cursor: canEditRefBy ? 'pointer' : 'default',
                '&:hover': canEditRefBy ? { bgcolor: 'grey.200' } : undefined,
                // Show edit affordance only on hover (desktop), keep click-to-edit behavior.
                '& .refby-edit': {
                  opacity: 0,
                  transition: 'opacity 120ms ease',
                },
                '&:hover .refby-edit': {
                  opacity: 1,
                },
              }}
              onClick={(e) => {
                if (!canEditRefBy) return;
                openRefByEditor(e.currentTarget as HTMLElement);
              }}
            >
              <HandshakeOutlinedIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
                <Typography variant="body2" noWrap sx={{ minWidth: 0, flex: 1 }}>
                  Ref by:{' '}
                  {lead.referredAgencyName || lead.referredBrokerName || (lead as any).brokerName || lead.referredBrokerId
                    ? (
                        lead.referredAgencyName
                          ? `${lead.referredAgencyName}${lead.referredBrokerName ? ` - ${lead.referredBrokerName}` : ''}`
                          : (lead.referredBrokerName || (lead as any).brokerName || lead.referredBrokerId)
                      )
                    : (String((lead as any).refByName || '').trim() || '—')}
                </Typography>
                <Stack direction="row" alignItems="center" gap={0.75} sx={{ flexShrink: 0 }}>
                  {!canSeeBrokerName && refByTelHref && refByPhoneDisplay && (
                    <Tooltip title="Call broker">
                      <Typography
                        component="a"
                        href={refByTelHref}
                        onClick={(e) => e.stopPropagation()}
                        variant="caption"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.4,
                          color: 'primary.main',
                          textDecoration: 'none',
                          fontWeight: 700,
                        }}
                      >
                        <PhoneIcon sx={{ fontSize: 14 }} />
                        {refByPhoneDisplay}
                      </Typography>
                    </Tooltip>
                  )}
                  {canSeeBrokerName && (() => {
                    // Prefer explicit referredBrokerAt; fall back to created/imported date only when ref-by exists.
                    const hasRef = !!(
                      (lead as any).refByAt ||
                      lead.referredBrokerAt ||
                      lead.referredBrokerId ||
                      lead.referredBrokerName ||
                      lead.referredAgencyName ||
                      (lead as any).brokerName ||
                      String((lead as any).refByName || '').trim()
                    );
                    const refAt =
                      toDateMaybe((lead as any).refByAt) ||
                      toDateMaybe(lead.referredBrokerAt) ||
                      (hasRef ? (toDateMaybe(lead.importedAt) || toDateMaybe(lead.createdAt)) : null);
                    if (!refAt) return null;
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const then = new Date(refAt);
                    then.setHours(0, 0, 0, 0);
                    const days = Math.max(
                      0,
                      Math.floor((today.getTime() - then.getTime()) / (24 * 60 * 60 * 1000))
                    );
                    return (
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {days}d
                      </Typography>
                    );
                  })()}
                  {canEditRefBy && (
                    <EditOutlinedIcon
                      fontSize="small"
                      className="refby-edit"
                      sx={{ color: 'text.secondary', pointerEvents: 'none' }}
                    />
                  )}
                </Stack>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 1,
                borderRadius: 1.5,
                bgcolor: 'grey.100',
                cursor: canEditAssignment ? 'pointer' : 'default',
                '&:hover': canEditAssignment ? { bgcolor: 'grey.200' } : undefined,
              }}
              onClick={(e) => {
                if (!canEditAssignment) return;
                setAssignAnchorEl(e.currentTarget as HTMLElement);
              }}
            >
              <AssignmentIndIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
                <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                  Assigned: {currentAssigneeLabel}
                </Typography>
                {canEditAssignment && (
                  <EditOutlinedIcon
                    className="assign-edit"
                    fontSize="small"
                    sx={{ ml: 1, color: 'text.secondary', pointerEvents: 'none' }}
                  />
                )}
              </Box>
            </Box>
            {lead.nextFollowUpDate && (
              <Box sx={{ display: 'flex', alignItems: 'center', p: 1, borderRadius: 1.5, bgcolor: 'grey.100' }}>
                <EventIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2">Next: {formatDateValue(lead.nextFollowUpDate)}</Typography>
              </Box>
            )}
            {createdAtDate && (
              <Box sx={{ display: 'flex', alignItems: 'center', p: 1, borderRadius: 1.5, bgcolor: 'grey.100' }}>
                <CalendarMonthOutlinedIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2">Received: {createdAtDate.toLocaleDateString()}</Typography>
              </Box>
            )}

            <Tooltip
              placement="bottom-start"
              disableHoverListener={visitItems.length === 0}
              title={
                <Box sx={{ py: 0.5 }}>
                  {visitItems.map((v, idx) => (
                    <Box
                      key={idx}
                      sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, minWidth: 280 }}
                    >
                      <Typography variant="body2">
                        {`Visit ${idx + 1}${v.withLabel ? ` ${v.withLabel.toLowerCase()}` : ''}`}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right' }}>
                        {v.at.toLocaleString()}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              }
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 1,
                  borderRadius: 1.5,
                  bgcolor: 'grey.100',
                  cursor: isMobile && visitItems.length > 0 ? 'pointer' : 'default',
                }}
                onClick={(e) => {
                  if (!isMobile) return;
                  if (visitItems.length === 0) return;
                  setVisitDatesAnchorEl(e.currentTarget as HTMLElement);
                }}
              >
                <PlaceOutlinedIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2">
                  Visits: {loadingActivities ? '…' : visitCount}
                </Typography>
              </Box>
            </Tooltip>

            <Tooltip
              placement="bottom-start"
              disableHoverListener={callBreakdownRows.length === 0}
              title={
                <Box sx={{ py: 0.5 }}>
                  {callBreakdownRows.map((r) => (
                    <Box
                      key={r.key}
                      sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, minWidth: 220 }}
                    >
                      <Typography variant="body2">{r.label}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {r.count}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              }
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 1,
                  borderRadius: 1.5,
                  bgcolor: 'grey.100',
                  cursor: isMobile && callBreakdownRows.length > 0 ? 'pointer' : 'default',
                }}
                onClick={(e) => {
                  if (!isMobile) return;
                  if (callBreakdownRows.length === 0) return;
                  setCallBreakdownAnchorEl(e.currentTarget as HTMLElement);
                }}
              >
                <CallOutlinedIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2">
                  Calls: {loadingActivities ? '…' : connectedCallCount}
                </Typography>
              </Box>
            </Tooltip>
          </Box>

          {lead.promptAnswers && Object.keys(lead.promptAnswers).length > 0 && (
            <Box sx={{ mt: 1.25 }}>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                {Object.entries(lead.promptAnswers).map(([pid, ans]) => {
                  const val = displayAnswer(ans);
                  if (!val) return null;
                  const label = (ans as any)?.label || findPromptDef(pid)?.label || pid;
                  return (
                    <Chip
                      key={pid}
                      label={`${label}: ${val}`}
                      size="small"
                      variant="outlined"
                      onClick={canEditPromptAnswers ? () => openEditPrompt(pid) : undefined}
                      onDelete={canEditPromptAnswers ? () => openEditPrompt(pid) : undefined}
                      deleteIcon={canEditPromptAnswers ? <EditOutlinedIcon /> : undefined}
                      sx={{
                        maxWidth: '100%',
                        '& .MuiChip-label': { maxWidth: 340, overflow: 'hidden', textOverflow: 'ellipsis' },
                        '& .MuiChip-deleteIcon': { display: 'none' },
                        '&:hover .MuiChip-deleteIcon': { display: 'inline-flex' },
                      }}
                    />
                  );
                })}
              </Stack>
            </Box>
          )}

          {editingPromptId && (
            <Box sx={{ mt: 1.5, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={700}>
                  Edit Answer
                </Typography>
                <IconButton size="small" onClick={() => setEditingPromptId(null)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
              <TextField
                fullWidth
                label={
                  findPromptDef(editingPromptId)?.label ||
                  (lead.promptAnswers?.[editingPromptId] as any)?.label ||
                  editingPromptId
                }
                type={
                  editingPromptType === 'number'
                    ? 'number'
                    : editingPromptType === 'date'
                      ? 'date'
                      : 'text'
                }
                InputLabelProps={editingPromptType === 'date' ? { shrink: true } : undefined}
                value={editingPromptValue}
                onChange={(e) => setEditingPromptValue(e.target.value)}
                select={editingPromptType === 'select'}
              >
                {editingPromptType === 'select' &&
                  editingPromptOptions.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
              </TextField>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }} justifyContent="flex-end">
                <Button onClick={() => setEditingPromptId(null)}>Cancel</Button>
                <Button
                  variant="contained"
                  onClick={handleSavePromptEdit}
                  disabled={saving || !canEditPromptAnswers}
                  sx={accentContainedBtnSx}
                >
                  Save
                </Button>
              </Stack>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          <Popover
            open={callBreakdownOpen}
            anchorEl={callBreakdownAnchorEl}
            onClose={() => setCallBreakdownAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{ sx: { p: 1.25, borderRadius: 2 } }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
              Call Outcomes
            </Typography>
            {callBreakdownRows.map((r) => (
              <Box
                key={r.key}
                sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, minWidth: 220 }}
              >
                <Typography variant="body2">{r.label}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {r.count}
                </Typography>
              </Box>
            ))}
          </Popover>

          <Popover
            open={visitDatesOpen}
            anchorEl={visitDatesAnchorEl}
            onClose={() => setVisitDatesAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{ sx: { p: 1.25, borderRadius: 2 } }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
              Visit Dates
            </Typography>
            {visitItems.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No visits yet.
              </Typography>
            ) : (
              visitItems.map((v, idx) => (
                <Box
                  key={idx}
                  sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, minWidth: 280 }}
                >
                  <Typography variant="body2">
                    {`Visit ${idx + 1}${v.withLabel ? ` ${v.withLabel.toLowerCase()}` : ''}`}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right' }}>
                    {v.at.toLocaleString()}
                  </Typography>
                </Box>
              ))
            )}
          </Popover>

          <Popover
            open={Boolean(assignAnchorEl)}
            anchorEl={assignAnchorEl}
            onClose={() => setAssignAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{ sx: { p: 1.25, borderRadius: 2, maxWidth: 340 } }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
              Assign Lead
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: 1,
              }}
            >
              {users
                .filter((u) => !!u.email)
                .map((u) => (
                  <Chip
                    key={u.id}
                    label={u.name ? u.name : (u.email?.split('@')[0] || u.email)}
                    variant={lead.assignedTo === u.email ? 'filled' : 'outlined'}
                    clickable
                    onClick={() => handleAssign(u.email!)}
                    sx={lead.assignedTo === u.email ? accentFilledChipSx : accentOutlinedChipSx}
                  />
                ))}
            </Box>
          </Popover>

          <Popover
            open={Boolean(refByAnchorEl)}
            anchorEl={refByAnchorEl}
            onClose={() => {
              if (!savingRefBy) setRefByAnchorEl(null);
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{ sx: { p: 1.25, borderRadius: 2, maxWidth: 420 } }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
              Ref By
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              {[
                { key: 'none', label: 'None' },
                { key: 'broker', label: 'Broker' },
                { key: 'reference', label: 'Reference' },
              ].map((m) => (
                <Chip
                  key={m.key}
                  label={m.label}
                  variant="filled"
                  clickable
                  onClick={() => setRefByMode(m.key as any)}
                  sx={refByMode === m.key ? accentFilledChipSx : accentOutlinedChipSx}
                />
              ))}
            </Box>
            {refByMode === 'broker' && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Autocomplete
                  options={refByOptions}
                  value={refByDraft}
                  onChange={(_, v) => setRefByDraft(v)}
                  getOptionLabel={(o) => (typeof o === 'string' ? o : o.label)}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search broker…"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 999,
                          backgroundColor: 'rgba(255, 255, 255, 0.95)',
                          border: '1px solid rgba(15, 23, 42, 0.14)',
                          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                          '& fieldset': { border: 'none' },
                        },
                        '& input': { py: 1.25 },
                      }}
                    />
                  )}
                />
              </Box>
              {canInlineAddBroker && (
                <Button
                  variant="outlined"
                  onClick={() => setBrokerDrawerOpen(true)}
                  sx={accentOutlinedBtnSx}
                >
                  +
                </Button>
              )}
            </Stack>
            )}
            {refByMode === 'reference' && (
              <TextField
                fullWidth
                value={refByNameDraft}
                onChange={(e) => setRefByNameDraft(e.target.value)}
                placeholder="Enter reference name…"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 999,
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid rgba(15, 23, 42, 0.14)',
                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                    '& fieldset': { border: 'none' },
                  },
                  '& input': { py: 1.25 },
                }}
              />
            )}
            <Stack direction="row" justifyContent="flex-end" gap={1} sx={{ mt: 1.25 }}>
              <Button
                size="small"
                onClick={() => setRefByAnchorEl(null)}
                disabled={savingRefBy}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleSaveRefBy}
                disabled={
                  savingRefBy ||
                  (refByMode === 'broker' ? !refByDraft : refByMode === 'reference' ? !refByNameDraft.trim() : false)
                }
              >
                {savingRefBy ? 'Saving...' : 'Save'}
              </Button>
            </Stack>
          </Popover>

          {/* Prompt chips and appointment summary stay above the activity form. */}
        </Box>

        <Accordion
          disableGutters
          elevation={0}
          expanded={showLog ? logExpanded : false}
          onChange={(_, expanded) => setLogExpanded(expanded)}
          sx={{
            p: 0,
            borderRadius: 2.5,
            border: '1px solid',
            borderColor: alpha(accent, 0.18),
            backgroundColor: 'background.paper',
            position: 'relative',
            overflow: 'hidden',
            // Remove MUI's default divider pseudo-element but keep our own ribbon.
            '&:before': { display: 'none' },
            '&::after': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${alpha(accent, 0.95)} 0%, ${alpha(
                accent,
                0.15
              )} 70%, ${alpha(accent, 0)} 100%)`,
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="log-activity-content"
            id="log-activity-header"
            sx={{
              px: 2,
              py: 1.25,
              minHeight: 0,
              '& .MuiAccordionSummary-content': { my: 0 },
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              Activity & Appointments
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
            {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
            {(() => {
              const hasType = !!activityType && activityType !== 'appointment';

              const BrokerRefByBlock =
                activityType === 'visit' ? (
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Autocomplete
                          options={brokers}
                          value={visitReferredBroker}
                          onChange={(_, v) => setVisitReferredBroker(v)}
                          getOptionLabel={(o) => (typeof o === 'string' ? o : o.label)}
                          isOptionEqualToValue={(a, b) => a.id === b.id}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Search broker…"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 999,
                                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                  border: '1px solid rgba(15, 23, 42, 0.14)',
                                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                                  '& fieldset': { border: 'none' },
                                },
                                '& input': { py: 1.25 },
                              }}
                            />
                          )}
                        />
                      </Box>
                      {canInlineAddBroker && (
                        <Button
                          variant="outlined"
                          onClick={() => setBrokerDrawerOpen(true)}
                          sx={accentOutlinedBtnSx}
                        >
                          +
                        </Button>
                      )}
                    </Stack>
                  </Box>
                ) : null;

              const FirstVisitRefByBlock =
                requireFirstVisitRefByChoice ? (
                  <Box>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                        gap: 1,
                      }}
                    >
                      {[
                        { key: 'direct', label: 'Direct' },
                        { key: 'broker', label: 'Broker' },
                        { key: 'reference', label: 'Reference' },
                        { key: 'c2c', label: 'C2C' },
                      ].map((option) => (
                        <Chip
                          key={option.key}
                          label={option.label}
                          variant="filled"
                          clickable
                          onClick={() =>
                            setVisitFirstRefByMode(
                              option.key as
                                | ''
                                | 'direct'
                                | 'broker'
                                | 'reference'
                                | 'c2c'
                            )
                          }
                          sx={
                            visitFirstRefByMode === option.key
                              ? cardsUi.pillSelectedSx
                              : cardsUi.pillUnselectedSx
                          }
                        />
                      ))}
                    </Box>

                    {visitFirstRefByMode === 'broker' && (
                      <Box sx={{ mt: 1.25 }}>{BrokerRefByBlock}</Box>
                    )}

                    {(visitFirstRefByMode === 'reference' ||
                      visitFirstRefByMode === 'c2c') && (
                      <TextField
                        fullWidth
                        value={visitFirstRefByName}
                        onChange={(e) => setVisitFirstRefByName(e.target.value)}
                        placeholder={
                          visitFirstRefByMode === 'c2c'
                            ? 'Enter C2C name…'
                            : 'Enter reference name…'
                        }
                        sx={{
                          mt: 1.25,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 999,
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            border: '1px solid rgba(15, 23, 42, 0.14)',
                            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                            '& fieldset': { border: 'none' },
                          },
                          '& input': { py: 1.25 },
                        }}
                      />
                    )}
                  </Box>
                ) : null;

              const StatusBlock = (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                    gap: 1,
                  }}
                >
                  <Chip
                    label={currentStatusLabel}
                    variant="filled"
                    clickable
                    onClick={() => setNewStatus('')}
                    sx={{
                      ...(newStatus === '' ? cardsUi.pillSelectedSx : cardsUi.pillUnselectedSx),
                    }}
                  />
                  {(statuses.length ? statuses : [{ id: lead.status, label: lead.status }])
                    .filter((s) => allowedStatus.includes(s.id))
                    .filter((s) => s.id !== currentStatusDocResolved?.id)
                    .map((s) => (
                      <Chip
                        key={s.id}
                        label={s.label}
                        variant="filled"
                        clickable
                        onClick={() => setNewStatus(s.id)}
                        sx={{
                          ...(newStatus === s.id ? cardsUi.pillSelectedSx : cardsUi.pillUnselectedSx),
                        }}
                      />
                    ))}
                </Box>
              );

              const QuestionsBlock =
                selectedPrompts.length > 0 ? (
                  <Stack spacing={0} sx={{ mt: '10px !important', gap: '15px' }}>
                    {selectedPrompts
                      .filter((p) => {
                        const lbl = String(p.label || '').trim().toLowerCase();
                        // These are handled as Visit-only fields (chips) instead of status prompts.
                        if (lbl === 'occupation' || lbl === 'current residence') return false;
                        const existing = lead.promptAnswers?.[p.id];
                        return !(existing && displayAnswer(existing).length > 0);
                      })
                      .map((p) => {
                        const existing = lead.promptAnswers?.[p.id];
                        const hasExisting = !!existing && displayAnswer(existing).length > 0;
                        return (
                          <Box key={p.id}>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                              sx={{ mb: 0.75, fontWeight: 800 }}
                            >
                              {p.label}
                            </Typography>
                            <TextField
                              fullWidth
                              disabled={hasExisting}
                              type={p.type === 'number' ? 'number' : p.type === 'date' ? 'date' : 'text'}
                              value={hasExisting ? displayAnswer(existing) : (promptDraft[p.id] || '')}
                              onChange={(e) => setPromptDraft((prev) => ({ ...prev, [p.id]: e.target.value }))}
                              select={p.type === 'select'}
                              helperText={hasExisting ? 'Already answered (cannot change).' : undefined}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 999,
                                  backgroundColor: hasExisting
                                    ? 'rgba(15, 23, 42, 0.04)'
                                    : 'rgba(255, 255, 255, 0.9)',
                                  border: '1px solid rgba(15, 23, 42, 0.14)',
                                  '&:hover': {
                                    backgroundColor: hasExisting
                                      ? 'rgba(15, 23, 42, 0.04)'
                                      : 'rgba(255, 255, 255, 1)',
                                  },
                                  '& fieldset': { border: 'none' },
                                },
                                '& input': { py: 1.25 },
                                '& .MuiSelect-select': { py: 1.25 },
                              }}
                            >
                              {p.type === 'select' &&
                                (
                                  (p.options && p.options.length
                                    ? p.options
                                    : []
                                  )
                                ).map((opt) => (
                                  <MenuItem key={opt} value={opt}>
                                    {opt}
                                  </MenuItem>
                                ))}
                            </TextField>
                          </Box>
                        );
                      })}
                  </Stack>
                ) : null;

              const FollowUpBlock = statusWillBeClosed ? null : (
                <Box sx={{ mt: '15px' }}>
                  {(() => {
                    const picked = parseLocalDateInputValue(followUpDate);
                    const today = parseLocalDateInputValue(todayLocalDateInput);
                    const isPast = !!picked && !!today && picked.getTime() < today.getTime();
                    return (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 800 }}>
                          Next Follow-up{followUpRequired ? ' *' : ''}
                        </Typography>
                        <TextField
                          type="date"
                          fullWidth
                          required={followUpRequired}
                          value={followUpDate}
                          onChange={(e) => setFollowUpDate(e.target.value)}
                          inputProps={{ min: todayLocalDateInput }}
                          error={isPast}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 999,
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              border: '1px solid',
                              borderColor: isPast ? 'rgba(220, 38, 38, 0.55)' : 'rgba(15, 23, 42, 0.14)',
                              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                              '& fieldset': { border: 'none' },
                            },
                            '& input': { py: 1.25 },
                          }}
                        />
                        <FormControlLabel
                          sx={{
                            mt: 1,
                            ml: 0,
                            mr: 0,
                            '& .MuiFormControlLabel-label': { fontWeight: 600 },
                          }}
                          control={(
                            <Switch
                              size="small"
                              checked={followUpTimeEnabled}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setFollowUpTimeEnabled(checked);
                                if (checked) {
                                  const now = new Date();
                                  const hh = String(now.getHours()).padStart(2, '0');
                                  const mm = String(now.getMinutes()).padStart(2, '0');
                                  setFollowUpTime((prev) => prev || `${hh}:${mm}`);
                                } else {
                                  setFollowUpTime('');
                                }
                              }}
                            />
                          )}
                          label="Set reminder time"
                        />
                        {followUpTimeEnabled && (
                          <TextField
                            type="time"
                            fullWidth
                            value={followUpTime}
                            onChange={(e) => setFollowUpTime(e.target.value)}
                            inputProps={{ step: 300 }}
                            sx={{
                              mt: 1.25,
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 999,
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                border: '1px solid rgba(15, 23, 42, 0.14)',
                                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                                '& fieldset': { border: 'none' },
                              },
                              '& input': { py: 1.25 },
                            }}
                          />
                        )}
                        <Box sx={{ mt: 1 }}>
                          {(() => {
                            const base = parseLocalDateInputValue(todayLocalDateInput) || new Date();
                            const addDays = (days: number) => {
                              const d = new Date(base);
                              d.setDate(d.getDate() + days);
                              return d;
                            };
                            const nextDow = (target: number) => {
                              const d = new Date(base);
                              const dow = d.getDay(); // 0=Sun ... 6=Sat
                              let delta = (target - dow + 7) % 7;
                              if (delta === 0) delta = 7; // "next" means strictly in the future
                              d.setDate(d.getDate() + delta);
                              return d;
                            };

                            const baseChipSx = {
                              ...cardsUi.pillUnselectedSx,
                              py: 0.5,
                              px: 0.75,
                              fontSize: 11,
                              fontWeight: 800,
                              minHeight: 30,
                              width: '100%',
                              justifyContent: 'center',
                              '& .MuiChip-label': { px: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' },
                            } as const;

                            const selectedChipSx = {
                              ...baseChipSx,
                              ...cardsUi.pillSelectedSx,
                              borderColor: 'transparent',
                            } as const;

                            const shortcuts = [
                              { label: 'Today', date: addDays(0) },
                              { label: 'Tomorrow', date: addDays(1) },
                              { label: '+3 days', date: addDays(3) },
                              { label: '+7 days', date: addDays(7) },
                              { label: 'Next Saturday', date: nextDow(6) },
                              { label: 'Next Sunday', date: nextDow(0) },
                            ];

                            return (
                              <Box
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))' },
                                  gap: 0.75,
                                }}
                              >
                                {shortcuts.map((s) => {
                                  const iso = localDateInputValue(s.date);
                                  const selected = followUpDate === iso;
                                  return (
                                    <Chip
                                      key={s.label}
                                      label={s.label}
                                      variant="filled"
                                      clickable
                                      onClick={() => setFollowUpDate(iso)}
                                      sx={selected ? selectedChipSx : baseChipSx}
                                    />
                                  );
                                })}
                              </Box>
                            );
                          })()}
                        </Box>
                      </Box>
                    );
                  })()}
                </Box>
              );

              const NotesBlock = !hasType ? null : (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 800 }}>
                    Add Note
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      fullWidth
                      value={noteInput}
                      onChange={(e) => setNoteInput(e.target.value)}
                      placeholder="Type a note and press Enter"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const t = noteInput.trim();
                          if (!t) return;
                          setNoteList((prev) => [...prev, t]);
                          setNoteInput('');
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 999,
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          border: '1px solid rgba(15, 23, 42, 0.14)',
                          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                          '& fieldset': { border: 'none' },
                        },
                      }}
                    />
                  </Stack>

                  {noteList.length > 0 && (
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {noteList.map((n, idx) => (
                        <Chip
                          key={`${idx}-${n}`}
                          label={n}
                          variant="filled"
                          onDelete={() => setNoteList((prev) => prev.filter((_, i) => i !== idx))}
                          deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                          sx={{
                            borderRadius: 999,
                            px: 1.25,
                            py: 1.75,
                            fontWeight: 700,
                            bgcolor: 'rgba(15, 23, 42, 0.06)',
                            border: '1px solid rgba(15, 23, 42, 0.10)',
                            '& .MuiChip-label': { px: 0.75 },
                            '& .MuiChip-deleteIcon': {
                              color: 'rgba(15, 23, 42, 0.55)',
                              mr: 0.25,
                              ml: 0.25,
                              fontSize: 14,
                              '&:hover': { color: 'rgba(15, 23, 42, 0.75)' },
                            },
                          }}
                        />
                      ))}
                    </Box>
                  )}

                  {activityType === 'visit' && (
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25 }}>Quick add:</Typography>
                      {VISIT_QUICK_POINTS
                        .filter((q) => !noteList.some((n) => n.toLowerCase() === q.toLowerCase()))
                        .map((q) => (
                          <Chip
                            key={q}
                            label={`+ ${q}`}
                            size="small"
                            variant="outlined"
                            clickable
                            onClick={() => setNoteList((prev) => [...prev, q])}
                            sx={{ borderStyle: 'dashed', borderRadius: 999 }}
                          />
                        ))}
                    </Box>
                  )}
                </Box>
              );

              const SubmitBtn = !hasType ? null : (
                <Button
                  type="button"
                  variant="contained"
                  onClick={requestLog}
                  disabled={saving}
                  startIcon={<AddIcon />}
                  fullWidth
                  sx={{
                    ...submitBtnSx,
                    borderRadius: 3,
                    py: 1.6,
                    fontWeight: 900,
                    letterSpacing: 0.4,
                    textTransform: 'none',
                    boxShadow: '0 10px 24px rgba(15, 23, 42, 0.12)',
                    '&:hover': {
                      ...submitBtnSx['&:hover'],
                      boxShadow: '0 12px 28px rgba(15, 23, 42, 0.16)',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: alpha(accent, 0.35),
                      color: 'rgba(255,255,255,0.9)',
                    },
                    ...(logSubmitState === 'success'
                      ? {
                          backgroundColor: 'success.main',
                          '&:hover': { backgroundColor: 'success.dark' },
                        }
                      : {}),
                  }}
                >
                  {logSubmitState === 'saving'
                    ? 'Processing...'
                    : logSubmitState === 'success'
                      ? 'Success'
                      : 'Log Activity'}
                </Button>
              );

              // minimal
	              const CardsLayout = (
	                <Stack spacing={1.25} sx={{ mb: 0 }}>
	                  <Box sx={cardsUi.cardSx}>
	                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 800 }}>
	                      Type *
	                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                      {[
                        { key: 'call', label: 'Call' },
                        ...(canManageAppointments ? [{ key: 'appointment', label: 'Appointment' }] : []),
                        { key: 'internal', label: 'Internal Update' },
                      ].map((t) => {
                        const selected = activityType === (t.key as any);
                        return (
                          <Chip
                            key={t.key}
                            label={t.label}
                            variant="filled"
                            clickable
                            onClick={() => {
                              setActivityType(t.key as any);
                              setApptFormOpen(t.key === 'appointment');
                              setApptCompleteOpen(false);
                            }}
                            sx={selected ? cardsUi.pillSelectedSx : cardsUi.pillUnselectedSx}
                          />
                        );
                      })}
	                    </Stack>
	                  </Box>

            {apptFormOpen && (
              <Box sx={{ mt: 1.25, display: 'grid', gap: 1.25 }}>
                {(() => {
                  const pillFieldSx = {
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 999,
                      backgroundColor: 'rgba(255, 255, 255, 0.92)',
                      border: '1px solid rgba(15, 23, 42, 0.14)',
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                      '& fieldset': { border: 'none' },
                    },
                    // TextField inputs
                    '& input': { py: 0.9, fontSize: 13 },
                    // Select (TextField select uses a div)
                    '& .MuiSelect-select': { py: 0.9, fontSize: 13 },
                  } as const;

                  const apptPillUnselectedSx = {
                    ...cardsUi.pillUnselectedSx,
                    px: 1,
                    py: 1.25,
                    fontSize: 13,
                    minHeight: 34,
                    '& .MuiChip-label': { px: 0.75 },
                  } as const;
                  const apptPillSelectedSx = {
                    ...cardsUi.pillSelectedSx,
                    px: 1,
                    py: 1.25,
                    fontSize: 13,
                    minHeight: 34,
                    '& .MuiChip-label': { px: 0.75 },
                  } as const;

                  const addMeetingPoint = () => {
                    const t = apptMeetingPointInput.trim();
                    if (!t) return;
                    setApptMeetingPoints((prev) => [...prev, t]);
                    setApptMeetingPointInput('');
                  };

                  return (
                    <>
                      <Box sx={cardsUi.cardSx}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 800 }}>
                          Type *
                        </Typography>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                            gap: 1,
                          }}
                        >
                          {[
                            { key: 'site-visit', label: 'Site Visit' },
                            { key: 'office-meeting', label: 'Office Meeting' },
                          ].map((t) => {
                            const selected = apptType === (t.key as any);
                            return (
                              <Chip
                                key={t.key}
                                label={t.label}
                                variant="filled"
                                clickable
                                onClick={() => setApptType(t.key as any)}
                                sx={selected ? apptPillSelectedSx : apptPillUnselectedSx}
                              />
                            );
                          })}
                        </Box>
                      </Box>

                      <Box sx={cardsUi.cardSx}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 800 }}>
                          Date & Time *
                        </Typography>
                        <TextField
                          type="datetime-local"
                          fullWidth
                          required
                          InputLabelProps={{ shrink: true }}
                          value={apptScheduledAt}
                          onChange={(e) => setApptScheduledAt(e.target.value)}
                          sx={pillFieldSx}
                        />
                      </Box>

                      <Box sx={cardsUi.cardSx}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 800 }}>
                          Project *
                        </Typography>
                        <TextField
                          select
                          fullWidth
                          value={apptProjectId}
                          onChange={(e) => {
                            setApptProjectId(e.target.value as string);
                            setApptUnitType('');
                          }}
                          sx={pillFieldSx}
                        >
                          {visibleProjects.map((p) => (
                            <MenuItem key={p.id} value={p.id}>
                              {p.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Box>

                      <Box sx={cardsUi.cardSx}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 800 }}>
                          Unit Type
                        </Typography>
                        {apptUnitOptions.length > 0 ? (
                          <TextField
                            select
                            fullWidth
                            value={apptUnitType}
                            onChange={(e) => setApptUnitType(e.target.value as string)}
                            sx={pillFieldSx}
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {apptUnitOptions.map((u) => (
                              <MenuItem key={u} value={u}>
                                {u}
                              </MenuItem>
                            ))}
                          </TextField>
                        ) : (
                          <TextField
                            fullWidth
                            value={apptUnitType}
                            onChange={(e) => setApptUnitType(e.target.value)}
                            placeholder="Enter unit type"
                            sx={pillFieldSx}
                          />
                        )}
                      </Box>

                      <Box sx={cardsUi.cardSx}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 800 }}>
                          Visit With
                        </Typography>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                            gap: 1,
                          }}
                        >
                          {[
                            { key: '', label: 'Skip' },
                            { key: 'alone', label: 'Alone' },
                            { key: 'family', label: 'With Family' },
                            { key: 'friend', label: 'With Friend/Relative' },
                            { key: 'other', label: 'Other' },
                          ].map((o) => {
                            const selected = apptVisitWith === (o.key as any);
                            return (
                              <Chip
                                key={o.key || 'skip'}
                                label={o.label}
                                variant="filled"
                                clickable
                                onClick={() => setApptVisitWith(o.key as any)}
                                sx={selected ? apptPillSelectedSx : apptPillUnselectedSx}
                              />
                            );
                          })}
                        </Box>
                        {apptVisitWith === 'other' && (
                          <TextField
                            fullWidth
                            value={apptVisitWithOther}
                            onChange={(e) => setApptVisitWithOther(e.target.value)}
                            placeholder="Other (who visited with?)"
                            sx={{ ...pillFieldSx, mt: 1 }}
                          />
                        )}
                      </Box>

                      <Box sx={cardsUi.cardSx}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 800 }}>
                          Meeting Points
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <TextField
                            fullWidth
                            value={apptMeetingPointInput}
                            onChange={(e) => setApptMeetingPointInput(e.target.value)}
                            placeholder="Add meeting point"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                addMeetingPoint();
                              }
                            }}
                            sx={pillFieldSx}
                          />
                          <Button
                            variant="outlined"
                            onClick={addMeetingPoint}
                            sx={{ whiteSpace: 'nowrap', ...accentOutlinedBtnSx }}
                          >
                            Add
                          </Button>
                        </Stack>
                        {apptMeetingPoints.length > 0 && (
                          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            {apptMeetingPoints.map((mp, idx) => (
                              <Chip
                                key={`${mp}-${idx}`}
                                label={mp}
                                variant="filled"
                                onDelete={() =>
                                  setApptMeetingPoints((prev) => prev.filter((_, i) => i !== idx))
                                }
                                sx={{
                                  ...apptPillSelectedSx,
                                  fontWeight: 800,
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      </Box>

                      <Button
                        variant="contained"
                        onClick={handleScheduleAppointment}
                        disabled={saving || !canManageAppointments}
                        sx={accentContainedBtnSx}
                      >
                        Save Appointment
                      </Button>
                    </>
                  );
                })()}
              </Box>
            )}

            {apptCompleteOpen && lead.appointment?.status === 'scheduled' && (
              <Box sx={{ mt: 1.25, display: 'grid', gap: 1.25 }}>
                {(() => {
                  const pillFieldSx = {
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 999,
                      backgroundColor: 'rgba(255, 255, 255, 0.92)',
                      border: '1px solid rgba(15, 23, 42, 0.14)',
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                      '& fieldset': { border: 'none' },
                    },
                    '& input': { py: 0.9, fontSize: 13 },
                    '& .MuiSelect-select': { py: 0.9, fontSize: 13 },
                  } as const;

                  const apptPillUnselectedSx = {
                    ...cardsUi.pillUnselectedSx,
                    px: 1,
                    py: 1.25,
                    fontSize: 13,
                    minHeight: 34,
                    '& .MuiChip-label': { px: 0.75 },
                  } as const;
                  const apptPillSelectedSx = {
                    ...cardsUi.pillSelectedSx,
                    px: 1,
                    py: 1.25,
                    fontSize: 13,
                    minHeight: 34,
                    '& .MuiChip-label': { px: 0.75 },
                  } as const;

                  const addNote = () => {
                    const t = apptCompleteNoteInput.trim();
                    if (!t) return;
                    setApptCompleteNotes((prev) => [...prev, t]);
                    setApptCompleteNoteInput('');
                  };

                  return (
                    <>
                      <Box sx={cardsUi.cardSx}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 800 }}>
                          Outcome *
                        </Typography>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                            gap: 1,
                          }}
                        >
                          {[
                            { key: 'completed', label: 'Completed' },
                            { key: 'no-show', label: 'No-show' },
                            { key: 'rescheduled', label: 'Rescheduled' },
                            { key: 'cancelled', label: 'Cancelled' },
                          ].map((o) => {
                            const selected = apptOutcome === (o.key as any);
                            return (
                              <Chip
                                key={o.key}
                                label={o.label}
                                variant="filled"
                                clickable
                                onClick={() => setApptOutcome(o.key as any)}
                                sx={selected ? apptPillSelectedSx : apptPillUnselectedSx}
                              />
                            );
                          })}
                        </Box>
                      </Box>

                      {apptOutcome === 'rescheduled' && (
                        <Box sx={cardsUi.cardSx}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 800 }}>
                            New Date & Time *
                          </Typography>
                          <TextField
                            type="datetime-local"
                            fullWidth
                            required
                            InputLabelProps={{ shrink: true }}
                            value={apptRescheduleAt}
                            onChange={(e) => setApptRescheduleAt(e.target.value)}
                            sx={pillFieldSx}
                          />
                        </Box>
                      )}

                      {apptOutcome !== 'cancelled' && !isClosedStatus(apptStatusChange || lead.status) && (
                        <Box sx={cardsUi.cardSx}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 800 }}>
                            Next Follow-up *
                          </Typography>
                          <TextField
                            type="date"
                            fullWidth
                            required
                            InputLabelProps={{ shrink: true }}
                            value={apptFollowUpDate}
                            onChange={(e) => setApptFollowUpDate(e.target.value)}
                            inputProps={{ min: todayLocalDateInput }}
                            sx={pillFieldSx}
                          />
                        </Box>
                      )}

                      {canEditRating && (
                        <Box sx={cardsUi.cardSx}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 800 }}>
                            Update Rating
                          </Typography>
                          <Rating
                            value={apptRating}
                            max={5}
                            size="medium"
                            IconContainerComponent={RatingIconContainer}
                            onChange={(_, v) => setApptRating(typeof v === 'number' ? v : 0)}
                          />
                        </Box>
                      )}

                      <Box sx={cardsUi.cardSx}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 800 }}>
                          Status Change
                        </Typography>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                            gap: 1,
                          }}
                        >
                          <Chip
                            label="No change"
                            variant="filled"
                            clickable
                            onClick={() => setApptStatusChange('')}
                            sx={apptStatusChange === '' ? apptPillSelectedSx : apptPillUnselectedSx}
                          />
                          {(statuses.length ? statuses : [{ id: lead.status, label: lead.status }])
                            .filter((s) => allowedStatus.includes(s.id))
                            .filter((s) => s.id !== currentStatusDocResolved?.id)
                            .map((s) => {
                              const selected = apptStatusChange === s.id;
                              return (
                                <Chip
                                  key={s.id}
                                  label={s.label}
                                  variant="filled"
                                  clickable
                                  onClick={() => setApptStatusChange(s.id)}
                                  sx={selected ? apptPillSelectedSx : apptPillUnselectedSx}
                                />
                              );
                            })}
                        </Box>
                      </Box>

                      <Box sx={cardsUi.cardSx}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 800 }}>
                          Notes
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <TextField
                            fullWidth
                            value={apptCompleteNoteInput}
                            onChange={(e) => setApptCompleteNoteInput(e.target.value)}
                            placeholder="Type note and press Enter"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                addNote();
                              }
                            }}
                            sx={pillFieldSx}
                          />
                          <Button
                            variant="outlined"
                            onClick={addNote}
                            sx={{ whiteSpace: 'nowrap', ...accentOutlinedBtnSx }}
                          >
                            Add
                          </Button>
                        </Stack>
                        {apptCompleteNotes.length > 0 && (
                          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            {apptCompleteNotes.map((n, idx) => (
                              <Chip
                                key={`${idx}-${n}`}
                                label={n}
                                variant="filled"
                                onDelete={() =>
                                  setApptCompleteNotes((prev) => prev.filter((_, i) => i !== idx))
                                }
                                sx={{
                                  ...apptPillSelectedSx,
                                  fontWeight: 800,
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      </Box>

                      <Button
                        variant="contained"
                        onClick={handleCompleteAppointment}
                        disabled={saving || !canManageAppointments}
                        sx={accentContainedBtnSx}
                      >
                        Save Update
                      </Button>
                    </>
                  );
                })()}
              </Box>
            )}


		                  {hasType && activityType === 'visit' && (() => {
		                    const needProject = !String((lead as any)?.projectId || '').trim();
		                    const needUnitType = !String((lead as any)?.unitType || '').trim();
		                    if (!needProject && !needUnitType) return null;

	                    const leadPid = String((lead as any)?.projectId || '').trim();
	                    const projectLabel =
	                      leadPid ? (projectForId.get(leadPid)?.name || (lead as any)?.projectName || leadPid) : '—';
	                    const inputSx = {
	                      '& .MuiOutlinedInput-root': {
	                        borderRadius: 999,
	                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
	                        border: '1px solid rgba(15, 23, 42, 0.14)',
	                        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
	                        '& fieldset': { border: 'none' },
	                      },
	                      '& input': { py: 1.25 },
	                    } as const;

	                    return (
	                      <Box sx={cardsUi.cardSx}>
	                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 800 }}>
	                          {needProject && needUnitType
	                            ? 'Project & Unit Type *'
	                            : needProject
	                              ? 'Project *'
	                              : 'Unit Type *'}
	                        </Typography>

	                        {needProject ? (
	                          visibleProjects.length === 0 ? (
	                            <Alert severity="warning">No projects available</Alert>
	                          ) : (
	                            <TextField
	                              select
	                              fullWidth
	                              size="small"
	                              value={logProjectIdDraft}
	                              onChange={(e) => setLogProjectIdDraft(e.target.value)}
	                              sx={inputSx}
	                            >
	                              <MenuItem value="">Select project…</MenuItem>
	                              {visibleProjects
	                                .slice()
	                                .sort((a: any, b: any) =>
	                                  String(a?.name || '').localeCompare(String(b?.name || ''))
	                                )
	                                .map((p: any) => (
	                                  <MenuItem key={p.id} value={p.id}>
	                                    {p.name}
	                                  </MenuItem>
	                                ))}
	                            </TextField>
	                          )
	                        ) : (
	                          <TextField
	                            fullWidth
	                            size="small"
	                            value={projectLabel}
	                            disabled
	                            sx={inputSx}
	                          />
	                        )}

	                        {needUnitType ? (
	                          <Box sx={{ mt: 1 }}>
	                            <Autocomplete
	                              freeSolo
	                              options={logUnitTypeOptions}
	                              value={logUnitTypeDraft}
	                              onInputChange={(_, v) => setLogUnitTypeDraft(v)}
	                              renderInput={(params) => (
	                                <TextField
	                                  {...params}
	                                  size="small"
	                                  placeholder="Enter unit type (e.g., 2BHK)"
	                                  sx={inputSx}
	                                />
	                              )}
	                            />
	                          </Box>
	                        ) : (
	                          <Box sx={{ mt: 1 }}>
	                            <TextField
	                              fullWidth
	                              size="small"
	                              value={String((lead as any)?.unitType || '—')}
	                              disabled
	                              sx={inputSx}
	                            />
	                          </Box>
	                        )}
	                      </Box>
	                    );
	                  })()}

	                  {activityType === 'call' && (
	                    <Box sx={cardsUi.cardSx}>
	                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 800 }}>
	                        Call Outcome *
                      </Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                          gap: 1,
                        }}
                      >
                        {[
                          { key: 'connected', label: 'Connected' },
                          { key: 'cnr', label: 'CNR' },
                          { key: 'busy', label: 'Busy' },
                          { key: 'call-back', label: 'Call Back Later' },
                          { key: 'wrong-number', label: 'Wrong Number' },
                        ].map((o) => {
                          const selected = callOutcome === o.key;
                          return (
                            <Chip
                              key={o.key}
                              label={o.label}
                              variant="filled"
                              clickable
                              onClick={() => setCallOutcome(o.key)}
                              sx={selected ? cardsUi.pillSelectedSx : cardsUi.pillUnselectedSx}
                            />
                          );
                        })}
                      </Box>
                    </Box>
                  )}

                  {activityType === 'visit' && (
                    <Box sx={cardsUi.cardSx}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 800 }}>
                        Visit With *
                      </Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                          gap: 1,
                        }}
                      >
                        {[
                          { key: 'alone', label: 'Alone' },
                          { key: 'family', label: 'With Family' },
                          { key: 'friend', label: 'With Relative' },
                          { key: 'other', label: 'Other' },
                        ].map((o) => {
                          const selected = visitWith === (o.key as any);
                          return (
                            <Chip
                              key={o.key}
                              label={o.label}
                              variant="filled"
                              clickable
                              onClick={() => setVisitWith(o.key as any)}
                              sx={selected ? cardsUi.pillSelectedSx : cardsUi.pillUnselectedSx}
                            />
                          );
                        })}
                      </Box>
                      {visitWith === 'other' && (
                        <TextField
                          label="Other"
                          fullWidth
                          value={visitWithOther}
                          onChange={(e) => setVisitWithOther(e.target.value)}
                          sx={{ mt: 1 }}
                        />
                      )}

                      <Box sx={{ mt: 1.5 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 800 }}>
                          This Visit Came Via <Typography component="span" variant="caption">(optional)</Typography>
                        </Typography>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                            gap: 1,
                          }}
                        >
                          {[
                            { key: 'walk-in', label: 'Walk-in' },
                            { key: 'broker', label: 'Broker' },
                            { key: 'c2c', label: 'C2C' },
                          ].map((option) => (
                            <Chip
                              key={option.key}
                              label={option.label}
                              variant="filled"
                              clickable
                              onClick={() => {
                                const next = option.key as 'walk-in' | 'broker' | 'c2c';
                                setVisitArrival((current) => current === next ? '' : next);
                              }}
                              sx={visitArrival === option.key ? cardsUi.pillSelectedSx : cardsUi.pillUnselectedSx}
                            />
                          ))}
                        </Box>

                        {visitArrival === 'broker' && (
                          <Autocomplete
                            sx={{ mt: 1 }}
                            options={brokers}
                            value={visitArrivalBroker}
                            onChange={(_, value) => setVisitArrivalBroker(value)}
                            getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Search broker for this visit..."
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 999,
                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                    border: '1px solid rgba(15, 23, 42, 0.14)',
                                    '& fieldset': { border: 'none' },
                                  },
                                  '& input': { py: 1.25 },
                                }}
                              />
                            )}
                          />
                        )}

                        {visitArrival === 'c2c' && (
                          <TextField
                            fullWidth
                            label="C2C reference name"
                            value={visitArrivalC2cName}
                            onChange={(event) => setVisitArrivalC2cName(event.target.value)}
                            sx={{ mt: 1 }}
                          />
                        )}
                      </Box>

                      {!hasLegacyBudget && (
                        <Box sx={{ mt: 1.25 }}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 800 }}>
                            Budget Fit
                          </Typography>
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                              gap: 1,
                            }}
                          >
                            {['In Budget', 'Under Budget'].map((opt) => {
                              const selected = visitBudgetFit === opt;
                              return (
                                <Chip
                                  key={opt}
                                  label={opt}
                                  variant="filled"
                                  clickable
                                  onClick={() => setVisitBudgetFit(opt)}
                                  sx={selected ? cardsUi.pillSelectedSx : cardsUi.pillUnselectedSx}
                                />
                              );
                            })}
                          </Box>
                        </Box>
                      )}

                      {!hasLegacyFloorChoice && (
                        <Box sx={{ mt: 1.25 }}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 800 }}>
                            Floor Choice
                          </Typography>
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                              gap: 1,
                            }}
                          >
                            {['Lower', 'Middle', 'Higher', 'Any'].map((opt) => {
                              const selected = visitFloorChoice === opt;
                              return (
                                <Chip
                                  key={opt}
                                  label={opt}
                                  variant="filled"
                                  clickable
                                  onClick={() => setVisitFloorChoice(opt)}
                                  sx={selected ? cardsUi.pillSelectedSx : cardsUi.pillUnselectedSx}
                                />
                              );
                            })}
                          </Box>
                        </Box>
                      )}

                      {!hasLegacyUnitChoice && (
                        <Box sx={{ mt: 1.25 }}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 800 }}>
                            Unit Choice
                          </Typography>
                          <Autocomplete
                            multiple
                            options={unitChoiceOptions}
                            value={visitUnitChoices}
                            inputValue={visitUnitChoiceInput}
                            onInputChange={(_, val) => setVisitUnitChoiceInput(val)}
                            onChange={(_, val) =>
                              setVisitUnitChoices(
                                (val || [])
                                  .filter((x) => typeof x === 'string' && x.trim())
                                  .map((x) => x.trim())
                              )
                            }
                            renderTags={(value, getTagProps) =>
                              (value || []).map((option, index) => (
                                <Chip
                                  // eslint-disable-next-line react/jsx-key
                                  {...getTagProps({ index })}
                                  label={option}
                                  variant="filled"
                                  sx={{
                                    ...cardsUi.pillSelectedSx,
                                    py: 1.25,
                                    px: 1,
                                    fontWeight: 800,
                                    '& .MuiChip-label': { px: 0.75 },
                                  }}
                                />
                              ))
                            }
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 999,
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                border: '1px solid rgba(15, 23, 42, 0.14)',
                                paddingTop: 0.75,
                                paddingBottom: 0.75,
                                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                                '& fieldset': { border: 'none' },
                              },
                              '& .MuiInputLabel-root': { fontWeight: 800 },
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder={
                                  unitChoiceOptions.length
                                    ? 'Select unit number(s)'
                                    : 'No units uploaded for this project'
                                }
                              />
                            )}
                          />
                        </Box>
                      )}

                      <Box sx={{ mt: 1.25, display: 'grid', gap: 1.25 }}>
                        {(() => {
                          return (
                            <>
                              {hasLegacyResidence ? null : (
                                <Box>
                                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 800 }}>
                                    Current Residence
                                  </Typography>
                                  <TextField
                                    fullWidth
                                    value={visitResidenceInput}
                                    onChange={(e) => setVisitResidenceInput(e.target.value)}
                                    placeholder="Enter current residence"
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        borderRadius: 999,
                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                        border: '1px solid rgba(15, 23, 42, 0.14)',
                                        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                                        '& fieldset': { border: 'none' },
                                      },
                                    }}
                                  />
                                </Box>
                              )}

                              {hasLegacyOccupation ? null : (
                                <Box>
                                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 800 }}>
                                    Occupation
                                  </Typography>
                                  <TextField
                                    fullWidth
                                    value={visitOccupationInput}
                                    onChange={(e) => setVisitOccupationInput(e.target.value)}
                                    placeholder="Enter occupation"
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        borderRadius: 999,
                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                        border: '1px solid rgba(15, 23, 42, 0.14)',
                                        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                                        '& fieldset': { border: 'none' },
                                      },
                                    }}
                                  />
                                </Box>
                              )}
                            </>
                          );
                        })()}
                      </Box>
                    </Box>
                  )}

                  {FirstVisitRefByBlock && (
                    <Box sx={cardsUi.cardSx}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 800 }}>
                        First Visit Ref By *
                      </Typography>
                      {FirstVisitRefByBlock}
                    </Box>
                  )}


                  {hasType && NotesBlock && <Box sx={cardsUi.cardSx}>{NotesBlock}</Box>}

	                  {hasType && (
	                    <Box sx={cardsUi.cardSx}>
	                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 800 }}>
	                        Status Change
	                      </Typography>
	                      {StatusBlock}
	                    </Box>
	                  )}

                  {hasType && isClosingNowPreview && (
                    <Box sx={cardsUi.cardSx}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 800 }}>
                        Close Analysis *
                      </Typography>
                      <Stack spacing={1.25}>
                        {(() => {
                          const hideCloseDetails = closePrimaryReason === 'Completed';
                          const closeSelectSx = {
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 999,
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              border: '1px solid rgba(15, 23, 42, 0.14)',
                              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                              '& fieldset': { border: 'none' },
                            },
                            '& .MuiSelect-select': {
                              py: '9px !important',
                            },
                            '& .MuiOutlinedInput-input': {
                              py: 1.1,
                            },
                          } as const;
                          return (
                            <>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 800 }}>
                            Primary Reason *
                          </Typography>
                          <TextField
                            select
                            fullWidth
                            value={closePrimaryReason}
                            onChange={(e) => {
                              const next = String(e.target.value || '');
                              setClosePrimaryReason(next);
                              if (next !== 'Other') setClosePrimaryOtherReason('');
                            }}
                            placeholder="Select primary reason"
                            sx={closeSelectSx}
                          >
                            <MenuItem value="">Select primary reason</MenuItem>
                            {CLOSE_REASON_OPTIONS.map((opt) => (
                              <MenuItem key={opt} value={opt}>
                                {opt}
                              </MenuItem>
                            ))}
                          </TextField>
                          {closePrimaryReason === 'Other' && (
                            <Box sx={{ mt: 1 }}>
                              <Typography
                                variant="subtitle2"
                                color="text.secondary"
                                sx={{ mb: 0.5, fontWeight: 800 }}
                              >
                                Other Reason *
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Autocomplete
                                  freeSolo
                                  options={closeOtherReasonOptions}
                                  value={closePrimaryOtherReason}
                                  inputValue={closePrimaryOtherReason}
                                  onInputChange={(_, value) =>
                                    setClosePrimaryOtherReason(toProperCaseWords(String(value || '')))
                                  }
                                  onChange={(_, value) => {
                                    const next = toProperCaseWords(String(value || ''));
                                    setClosePrimaryOtherReason(
                                      findOptionIgnoreCase(closeOtherReasonOptions, next)
                                    );
                                  }}
                                  sx={{ flex: 1 }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      placeholder="Search previous reason"
                                      onBlur={() => {
                                        const matched = findOptionIgnoreCase(
                                          closeOtherReasonOptions,
                                          closePrimaryOtherReason
                                        );
                                        setClosePrimaryOtherReason(matched);
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') e.preventDefault();
                                      }}
                                      sx={{
                                        '& .MuiOutlinedInput-root': {
                                          borderRadius: 999,
                                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                          border: '1px solid rgba(15, 23, 42, 0.14)',
                                          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                                          '& fieldset': { border: 'none' },
                                        },
                                      }}
                                    />
                                  )}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setShowAddOtherReason(true);
                                    setNewOtherReasonInput(
                                      toProperCaseWords(closePrimaryOtherReason)
                                    );
                                  }}
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    border: '1px solid rgba(15, 23, 42, 0.18)',
                                    borderRadius: '50%',
                                  }}
                                >
                                  <AddIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Box>
                              {showAddOtherReason && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75 }}>
                                  <TextField
                                    fullWidth
                                    autoFocus
                                    value={newOtherReasonInput}
                                    onChange={(e) =>
                                      setNewOtherReasonInput(
                                        toProperCaseWords(String(e.target.value || ''))
                                      )
                                    }
                                    placeholder="Enter new reason"
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        borderRadius: 999,
                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                        border: '1px solid rgba(15, 23, 42, 0.14)',
                                        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                                        '& fieldset': { border: 'none' },
                                      },
                                    }}
                                  />
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      const next = toProperCaseWords(newOtherReasonInput);
                                      if (!next) return;
                                      setCloseOtherReasonOptions((prev) =>
                                        findOptionIgnoreCase(prev, next)
                                          ? prev
                                          : [...prev, next].sort((a, b) => a.localeCompare(b))
                                      );
                                      setClosePrimaryOtherReason(next);
                                      setShowAddOtherReason(false);
                                      setNewOtherReasonInput('');
                                    }}
                                  >
                                    <CheckOutlinedIcon sx={{ fontSize: 17 }} />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setShowAddOtherReason(false);
                                      setNewOtherReasonInput('');
                                    }}
                                  >
                                    <CloseIcon sx={{ fontSize: 17 }} />
                                  </IconButton>
                                </Box>
                              )}
                            </Box>
                          )}
                        </Box>

                        {!hideCloseDetails && closePrimaryReason === 'Interested in Another Location' && (
                          <Box>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 800 }}>
                              Interested Location *
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <Autocomplete
                                freeSolo
                                options={closeInterestedLocationOptions}
                                value={closeInterestedLocation}
                                inputValue={closeInterestedLocation}
                                onInputChange={(_, value) =>
                                  setCloseInterestedLocation(toProperCaseWords(String(value || '')))
                                }
                                onChange={(_, value) => {
                                  const next = toProperCaseWords(String(value || ''));
                                  setCloseInterestedLocation(
                                    findOptionIgnoreCase(closeInterestedLocationOptions, next)
                                  );
                                }}
                                sx={{ flex: 1 }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    placeholder="Search location"
                                    onBlur={() => {
                                      const matched = findOptionIgnoreCase(
                                        closeInterestedLocationOptions,
                                        closeInterestedLocation
                                      );
                                      setCloseInterestedLocation(matched);
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') e.preventDefault();
                                    }}
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        borderRadius: 999,
                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                        border: '1px solid rgba(15, 23, 42, 0.14)',
                                        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                                        '& fieldset': { border: 'none' },
                                      },
                                    }}
                                  />
                                )}
                              />
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setShowAddInterestedLocation(true);
                                  setNewInterestedLocationInput(
                                    toProperCaseWords(closeInterestedLocation)
                                  );
                                }}
                                sx={{
                                  width: 32,
                                  height: 32,
                                  border: '1px solid rgba(15, 23, 42, 0.18)',
                                  borderRadius: '50%',
                                }}
                              >
                                <AddIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Box>
                            {showAddInterestedLocation && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75 }}>
                                <TextField
                                  fullWidth
                                  autoFocus
                                  value={newInterestedLocationInput}
                                  onChange={(e) =>
                                    setNewInterestedLocationInput(
                                      toProperCaseWords(String(e.target.value || ''))
                                    )
                                  }
                                  placeholder="Enter new location"
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 999,
                                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                      border: '1px solid rgba(15, 23, 42, 0.14)',
                                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                                      '& fieldset': { border: 'none' },
                                    },
                                  }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    const next = toProperCaseWords(newInterestedLocationInput);
                                    if (!next) return;
                                    setCloseInterestedLocationOptions((prev) =>
                                      findOptionIgnoreCase(prev, next)
                                        ? prev
                                        : [...prev, next].sort((a, b) => a.localeCompare(b))
                                    );
                                    setCloseInterestedLocation(next);
                                    setShowAddInterestedLocation(false);
                                    setNewInterestedLocationInput('');
                                  }}
                                >
                                  <CheckOutlinedIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setShowAddInterestedLocation(false);
                                    setNewInterestedLocationInput('');
                                  }}
                                >
                                  <CloseIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                              </Box>
                            )}
                          </Box>
                        )}

                        {!hideCloseDetails && closePrimaryReason === 'Purchased in Another Location' && (
                          <Box>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 800 }}>
                              Purchased Location *
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <Autocomplete
                                freeSolo
                                options={closeBoughtAtLocationOptions}
                                value={closeBoughtAtLocation}
                                inputValue={closeBoughtAtLocation}
                                onInputChange={(_, value) =>
                                  setCloseBoughtAtLocation(toProperCaseWords(String(value || '')))
                                }
                                onChange={(_, value) => {
                                  const next = toProperCaseWords(String(value || ''));
                                  setCloseBoughtAtLocation(
                                    findOptionIgnoreCase(closeBoughtAtLocationOptions, next)
                                  );
                                }}
                                sx={{ flex: 1 }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    placeholder="Search location"
                                    onBlur={() => {
                                      const matched = findOptionIgnoreCase(
                                        closeBoughtAtLocationOptions,
                                        closeBoughtAtLocation
                                      );
                                      setCloseBoughtAtLocation(matched);
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') e.preventDefault();
                                    }}
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        borderRadius: 999,
                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                        border: '1px solid rgba(15, 23, 42, 0.14)',
                                        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                                        '& fieldset': { border: 'none' },
                                      },
                                    }}
                                  />
                                )}
                              />
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setShowAddBoughtAtLocation(true);
                                  setNewBoughtAtLocationInput(
                                    toProperCaseWords(closeBoughtAtLocation)
                                  );
                                }}
                                sx={{
                                  width: 32,
                                  height: 32,
                                  border: '1px solid rgba(15, 23, 42, 0.18)',
                                  borderRadius: '50%',
                                }}
                              >
                                <AddIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Box>
                            {showAddBoughtAtLocation && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75 }}>
                                <TextField
                                  fullWidth
                                  autoFocus
                                  value={newBoughtAtLocationInput}
                                  onChange={(e) =>
                                    setNewBoughtAtLocationInput(
                                      toProperCaseWords(String(e.target.value || ''))
                                    )
                                  }
                                  placeholder="Enter purchased location"
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 999,
                                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                      border: '1px solid rgba(15, 23, 42, 0.14)',
                                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                                      '& fieldset': { border: 'none' },
                                    },
                                  }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    const next = toProperCaseWords(newBoughtAtLocationInput);
                                    if (!next) return;
                                    setCloseBoughtAtLocationOptions((prev) =>
                                      findOptionIgnoreCase(prev, next)
                                        ? prev
                                        : [...prev, next].sort((a, b) => a.localeCompare(b))
                                    );
                                    setCloseBoughtAtLocation(next);
                                    setShowAddBoughtAtLocation(false);
                                    setNewBoughtAtLocationInput('');
                                  }}
                                >
                                  <CheckOutlinedIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setShowAddBoughtAtLocation(false);
                                    setNewBoughtAtLocationInput('');
                                  }}
                                >
                                  <CloseIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                              </Box>
                            )}
                          </Box>
                        )}

                        {!hideCloseDetails && closePrimaryReason === 'Need Other Type' && (
                          <Box sx={{ display: 'grid', gap: 1 }}>
                            <Box>
                              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 800 }}>
                                Required Type *
                              </Typography>
                              <TextField
                                select
                                fullWidth
                                value={closeRequiredType}
                                onChange={(e) => setCloseRequiredType(String(e.target.value || ''))}
                                sx={closeSelectSx}
                              >
                                <MenuItem value="">Select required type</MenuItem>
                                {CLOSE_REQUIRED_TYPE_OPTIONS.map((opt) => (
                                  <MenuItem key={opt} value={opt}>
                                    {opt}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Box>

                            <Box>
                              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 800 }}>
                                Location *
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Autocomplete
                                  freeSolo
                                  options={closeRequiredTypeLocationOptions}
                                  value={closeRequiredTypeLocation}
                                  inputValue={closeRequiredTypeLocation}
                                  onInputChange={(_, value) =>
                                    setCloseRequiredTypeLocation(toProperCaseWords(String(value || '')))
                                  }
                                  onChange={(_, value) => {
                                    const next = toProperCaseWords(String(value || ''));
                                    setCloseRequiredTypeLocation(
                                      findOptionIgnoreCase(closeRequiredTypeLocationOptions, next)
                                    );
                                  }}
                                  sx={{ flex: 1 }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      placeholder="Search location"
                                      onBlur={() => {
                                        const matched = findOptionIgnoreCase(
                                          closeRequiredTypeLocationOptions,
                                          closeRequiredTypeLocation
                                        );
                                        setCloseRequiredTypeLocation(matched);
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') e.preventDefault();
                                      }}
                                      sx={{
                                        '& .MuiOutlinedInput-root': {
                                          borderRadius: 999,
                                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                          border: '1px solid rgba(15, 23, 42, 0.14)',
                                          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                                          '& fieldset': { border: 'none' },
                                        },
                                      }}
                                    />
                                  )}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setShowAddRequiredTypeLocation(true);
                                    setNewRequiredTypeLocationInput(
                                      toProperCaseWords(closeRequiredTypeLocation)
                                    );
                                  }}
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    border: '1px solid rgba(15, 23, 42, 0.18)',
                                    borderRadius: '50%',
                                  }}
                                >
                                  <AddIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Box>
                              {showAddRequiredTypeLocation && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75 }}>
                                  <TextField
                                    fullWidth
                                    autoFocus
                                    value={newRequiredTypeLocationInput}
                                    onChange={(e) =>
                                      setNewRequiredTypeLocationInput(
                                        toProperCaseWords(String(e.target.value || ''))
                                      )
                                    }
                                    placeholder="Enter location"
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        borderRadius: 999,
                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                        border: '1px solid rgba(15, 23, 42, 0.14)',
                                        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                                        '& fieldset': { border: 'none' },
                                      },
                                    }}
                                  />
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      const next = toProperCaseWords(newRequiredTypeLocationInput);
                                      if (!next) return;
                                      setCloseRequiredTypeLocationOptions((prev) =>
                                        findOptionIgnoreCase(prev, next)
                                          ? prev
                                          : [...prev, next].sort((a, b) => a.localeCompare(b))
                                      );
                                      setCloseRequiredTypeLocation(next);
                                      setShowAddRequiredTypeLocation(false);
                                      setNewRequiredTypeLocationInput('');
                                    }}
                                  >
                                    <CheckOutlinedIcon sx={{ fontSize: 17 }} />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setShowAddRequiredTypeLocation(false);
                                      setNewRequiredTypeLocationInput('');
                                    }}
                                  >
                                    <CloseIcon sx={{ fontSize: 17 }} />
                                  </IconButton>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        )}

                        {!hideCloseDetails && closePrimaryReason === 'Budget / Financial Issue' && (
                          <Box>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 800 }}>
                              Budget Detail *
                            </Typography>
                            <TextField
                              select
                              fullWidth
                              value={closeBudgetDetail}
                              onChange={(e) => setCloseBudgetDetail(String(e.target.value || ''))}
                              sx={closeSelectSx}
                            >
                              <MenuItem value="">Select budget detail</MenuItem>
                              {CLOSE_BUDGET_DETAIL_OPTIONS.map((opt) => (
                                <MenuItem key={opt} value={opt}>
                                  {opt}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Box>
                        )}

                        {!hideCloseDetails && closePrimaryReason === 'Distance Issue' && (
                          <Box>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 800 }}>
                              Preferred Area *
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <Autocomplete
                                freeSolo
                                options={closeDistanceAreaOptions}
                                value={closeDistancePreferredArea}
                                inputValue={closeDistancePreferredArea}
                                onInputChange={(_, value) =>
                                  setCloseDistancePreferredArea(
                                    toProperCaseWords(String(value || ''))
                                  )
                                }
                                onChange={(_, value) => {
                                  const next = toProperCaseWords(String(value || ''));
                                  setCloseDistancePreferredArea(
                                    findOptionIgnoreCase(closeDistanceAreaOptions, next)
                                  );
                                }}
                                sx={{ flex: 1 }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    placeholder="Search area"
                                    onBlur={() => {
                                      const matched = findOptionIgnoreCase(
                                        closeDistanceAreaOptions,
                                        closeDistancePreferredArea
                                      );
                                      setCloseDistancePreferredArea(matched);
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') e.preventDefault();
                                    }}
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        borderRadius: 999,
                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                        border: '1px solid rgba(15, 23, 42, 0.14)',
                                        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                                        '& fieldset': { border: 'none' },
                                      },
                                    }}
                                  />
                                )}
                              />
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setShowAddDistanceArea(true);
                                  setNewDistanceAreaInput(
                                    toProperCaseWords(closeDistancePreferredArea)
                                  );
                                }}
                                sx={{
                                  width: 32,
                                  height: 32,
                                  border: '1px solid rgba(15, 23, 42, 0.18)',
                                  borderRadius: '50%',
                                }}
                              >
                                <AddIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Box>
                            {showAddDistanceArea && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75 }}>
                                <TextField
                                  fullWidth
                                  autoFocus
                                  value={newDistanceAreaInput}
                                  onChange={(e) =>
                                    setNewDistanceAreaInput(
                                      toProperCaseWords(String(e.target.value || ''))
                                    )
                                  }
                                  placeholder="Enter new area"
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 999,
                                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                      border: '1px solid rgba(15, 23, 42, 0.14)',
                                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                                      '& fieldset': { border: 'none' },
                                    },
                                  }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    const next = toProperCaseWords(newDistanceAreaInput);
                                    if (!next) return;
                                    setCloseDistanceAreaOptions((prev) =>
                                      findOptionIgnoreCase(prev, next)
                                        ? prev
                                        : [...prev, next].sort((a, b) => a.localeCompare(b))
                                    );
                                    setCloseDistancePreferredArea(next);
                                    setShowAddDistanceArea(false);
                                    setNewDistanceAreaInput('');
                                  }}
                                >
                                  <CheckOutlinedIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setShowAddDistanceArea(false);
                                    setNewDistanceAreaInput('');
                                  }}
                                >
                                  <CloseIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                              </Box>
                            )}
                          </Box>
                        )}

                        {!hideCloseDetails && (closePrimaryReason === 'Loss due to Competitor' ||
                          closeSecondaryReasons.includes('Loss due to Competitor')) && (
                          <Box sx={{ display: 'grid', gap: 1 }}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 800 }}>
                              Competitor Details *
                            </Typography>
                            <TextField
                              fullWidth
                              placeholder="Competitor project name"
                              value={closeCompetitorProject}
                              onChange={(e) => setCloseCompetitorProject(e.target.value)}
                              onBlur={() =>
                                setCloseCompetitorProject((prev) => toProperCaseWords(prev))
                              }
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 999,
                                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                  border: '1px solid rgba(15, 23, 42, 0.14)',
                                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                                  '& fieldset': { border: 'none' },
                                },
                              }}
                            />
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                              sx={{ mt: 0.25, mb: 0.25, fontWeight: 800 }}
                            >
                              Price Comparison *
                            </Typography>
                            <TextField
                              select
                              fullWidth
                              value={closeCompetitorPriceBand}
                              onChange={(e) => setCloseCompetitorPriceBand(String(e.target.value || ''))}
                              sx={closeSelectSx}
                            >
                              <MenuItem value="">Select competitor price band</MenuItem>
                              {CLOSE_COMP_PRICE_BAND_OPTIONS.map((opt) => (
                                <MenuItem key={opt} value={opt}>
                                  {opt}
                                </MenuItem>
                              ))}
                            </TextField>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                              sx={{ mt: 0.25, mb: 0.25, fontWeight: 800 }}
                            >
                              Main Competitor Advantage *
                            </Typography>
                            <TextField
                              select
                              fullWidth
                              value={closeCompetitorAdvantage}
                              onChange={(e) => setCloseCompetitorAdvantage(String(e.target.value || ''))}
                              sx={closeSelectSx}
                            >
                              <MenuItem value="">Select main competitor advantage</MenuItem>
                              {CLOSE_COMP_ADVANTAGE_OPTIONS.map((opt) => (
                                <MenuItem key={opt} value={opt}>
                                  {opt}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Box>
                        )}

                        {!hideCloseDetails && (
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 800 }}>
                            Secondary Reasons (Optional)
                          </Typography>
                          <Autocomplete
                            multiple
                            options={CLOSE_SECONDARY_OPTIONS}
                            value={closeSecondaryReasons}
                            onChange={(_, value) => setCloseSecondaryReasons(value)}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Select secondary reasons"
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 3,
                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                    border: '1px solid rgba(15, 23, 42, 0.14)',
                                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                                    '& fieldset': { border: 'none' },
                                  },
                                }}
                              />
                            )}
                          />
                        </Box>
                        )}

                        {!hideCloseDetails && (
                          <Box>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 800 }}>
                              Decision Stage *
                            </Typography>
                            {!closeDecisionStageEditing ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  borderRadius: 999,
                                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                  border: '1px solid rgba(15, 23, 42, 0.14)',
                                  px: 1.5,
                                  py: 0.6,
                                }}
                              >
                                <Typography sx={{ color: 'rgba(15, 23, 42, 0.85)' }}>
                                  {closeDecisionStage || closeDecisionStageAuto || 'New Lead'}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => setCloseDecisionStageEditing(true)}
                                  sx={{ ml: 1 }}
                                >
                                  <EditIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Box>
                            ) : (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TextField
                                  select
                                  fullWidth
                                  value={closeDecisionStage || closeDecisionStageAuto || ''}
                                  onChange={(e) => setCloseDecisionStage(String(e.target.value || ''))}
                                  sx={closeSelectSx}
                                >
                                  {CLOSE_STAGE_OPTIONS.map((opt) => (
                                    <MenuItem key={opt} value={opt}>
                                      {opt}
                                    </MenuItem>
                                  ))}
                                </TextField>
                                <IconButton
                                  size="small"
                                  onClick={() => setCloseDecisionStageEditing(false)}
                                >
                                  <CheckOutlinedIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                              </Box>
                            )}
                          </Box>
                        )}

                            </>
                          );
                        })()}
                      </Stack>
                    </Box>
                  )}

                  {hasType && QuestionsBlock && <Box sx={cardsUi.cardSx}>{QuestionsBlock}</Box>}
                  {hasType && FollowUpBlock && <Box sx={cardsUi.cardSx}>{FollowUpBlock}</Box>}

                  {logSubmitError && (
                    <Alert severity="error" sx={{ borderRadius: 2 }}>
                      {logSubmitError}
                    </Alert>
                  )}

                  {SubmitBtn && (
                    <Box sx={{ pt: 0.25 }}>
                      {SubmitBtn}
                    </Box>
                  )}
                </Stack>
              );

              return CardsLayout;
            })()}
          </AccordionDetails>
        </Accordion>

        {lead?.phone && (() => {
          const waDigits = normalizePhoneDigits(lead.phone);
          if (!waDigits) return null;
          const waContactId = normalizeContactId('whatsapp', waDigits);
          return (
            <Accordion disableGutters elevation={0} sx={{ mt: 2, ...sectionSx(), '&::before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <WhatsAppIcon sx={{ fontSize: 18, color: '#25d366' }} />
                  <Typography sx={{ fontWeight: 500, fontSize: 14 }}>WhatsApp Messages</Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <ChannelChat
                  channel="whatsapp"
                  contactId={waContactId}
                  contactName={lead.name || null}
                  leadId={lead.id}
                  projectId={lead.projectId || undefined}
                  height={400}
                  showHeader={false}
                  onSendBrochure={(anchorEl, lastInboundAt) => {
                    setBrochureLastInboundAt(lastInboundAt);
                    setBrochureAnchorEl(anchorEl);
                  }}
                />
              </AccordionDetails>
            </Accordion>
          );
        })()}
        {lead?.id && <LeadBrochureMenu
          anchorEl={brochureAnchorEl}
          onClose={() => setBrochureAnchorEl(null)}
          leadId={lead.id}
          leadName={String(lead.name || '')}
          phone={String(lead.phone || '')}
          projectId={lead.projectId || null}
          projectName={String((lead as any).projectName || '')}
          lastInboundAt={brochureLastInboundAt}
        />}

        <Box
          sx={{
            display: showTimeline ? 'block' : 'none',
            mt: 2,
            ...sectionSx(),
          }}
        >
          

          <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
            <HistoryIcon fontSize="small" />
            <Typography variant="subtitle1">Timeline</Typography>
            <Box sx={{ flex: 1 }} />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={onlyCustomerContact}
                  onChange={(e) => setOnlyCustomerContact(e.target.checked)}
                />
              }
              label="Only customer contact"
              sx={{
                m: 0,
                '& .MuiFormControlLabel-label': { fontSize: 12, color: 'text.secondary' },
              }}
            />
          </Stack>
          {loadingActivities ? (
            <Typography variant="body2" color="text.secondary">Loading timeline...</Typography>
          ) : timelineError ? (
            <Alert severity="error" sx={{ borderRadius: 2 }}>{timelineError}</Alert>
          ) : visibleActivities.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No activity yet.</Typography>
          ) : (
            <Stack spacing={1.25}>
              {visibleActivities.map((a, idx) =>
                renderActivity(a, idx === visibleActivities.length - 1, idx)
              )}
            </Stack>
          )}
        </Box>

        <Dialog
          open={!!editPA}
          onClose={() => {
            if (!editPASaving) setEditPA(null);
          }}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Edit {editPA?.label || 'Value'}</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              value={editPAInput}
              onChange={(e) => setEditPAInput(e.target.value)}
              placeholder={`Enter ${editPA?.label || 'value'}`}
              sx={{
                mt: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 999,
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid rgba(15, 23, 42, 0.14)',
                  '& fieldset': { border: 'none' },
                },
                '& input': { py: 1.25 },
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditPA(null)} disabled={editPASaving}>
              Cancel
            </Button>
            <Button variant="contained" onClick={saveEditPA} disabled={editPASaving}>
              {editPASaving ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={reopenDialogOpen}
          onClose={() => {
            if (!saving) setReopenDialogOpen(false);
          }}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Reopen Lead</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                select
                fullWidth
                label="Reason"
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
                required
              >
                <MenuItem value="">Select reason</MenuItem>
                <MenuItem value="Customer called">Customer called</MenuItem>
                <MenuItem value="Customer replied on WhatsApp">Customer replied on WhatsApp</MenuItem>
                <MenuItem value="Social Media Inquiry">Social Media Inquiry</MenuItem>
                <MenuItem value="Customer visited site">Customer visited site</MenuItem>
                <MenuItem value="Customer wants details again">Customer wants details again</MenuItem>
                <MenuItem value="Follow-up restarted manually">Follow-up restarted manually</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </TextField>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Note"
                value={reopenNote}
                onChange={(e) => setReopenNote(e.target.value)}
                placeholder="Optional internal note"
              />
              {logSubmitError ? (
                <Alert severity="error">{logSubmitError}</Alert>
              ) : null}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setReopenDialogOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              variant="outlined"
              disabled={saving || !reopenReason}
              onClick={() => handleLog({
                reason: reopenReason,
                note: reopenNote,
                sendWhatsapp: false,
              })}
            >
              Log Activity
            </Button>
            <Button
              variant="contained"
              disabled={saving || !reopenReason}
              startIcon={<WhatsAppIcon />}
              onClick={() => handleLog({
                reason: reopenReason,
                note: reopenNote,
                sendWhatsapp: true,
              })}
            >
              Log Activity + WhatsApp
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={ownerAskOpen} onClose={() => setOwnerAskOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 800 }}>Assign {lead?.name || 'this lead'} to you?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              You presented this visit. The lead is assigned to {(lead as any)?.assignedToName || (lead as any)?.assignedTo}.
              Either way the visit is saved with you as presenter.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1, px: 3, pb: 2.5 }}>
            <Button variant="contained" disabled={saving} onClick={() => handleLog(undefined, true)}>
              Yes, assign to me
            </Button>
            <Button variant="outlined" disabled={saving} onClick={() => handleLog(undefined, false)} sx={{ ml: '0 !important' }}>
              Keep with {(lead as any)?.assignedToName || (lead as any)?.assignedTo}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Drawer>
    <Snackbar
      open={postSubmitToastOpen}
      autoHideDuration={1000}
      onClose={() => setPostSubmitToastOpen(false)}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert
        onClose={() => setPostSubmitToastOpen(false)}
        severity="success"
        variant="filled"
        sx={{ width: '100%' }}
      >
        Successfully submitted this details.
      </Alert>
    </Snackbar>
    <BrokerDrawer
      open={brokerDrawerOpen}
      onClose={() => setBrokerDrawerOpen(false)}
      onSaved={() => setBrokerDrawerOpen(false)}
      editing={null}
    />
    </>
  );
}

export default function LeadDetailDrawer(props: Props) {
  return (
    <LeadDetailDrawerErrorBoundary
      leadId={props.lead?.id}
      open={props.open}
      onClose={props.onClose}
    >
      <LeadDetailDrawerInner {...props} />
    </LeadDetailDrawerErrorBoundary>
  );
}
